import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { EXPENSE_CATEGORIES, fetchVendorOptions, fetchCategoryOptions, DEFAULT_VENDOR_OPTIONS } from "@/lib/expenseOptions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuRadioGroup, DropdownMenuRadioItem } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { AiBreakdownPanel } from "@/components/finance/AiBreakdownPanel";
import { Plus, X, Pencil, Trash2, TrendingUp, TrendingDown, Wallet, Users, Receipt, Calendar, Download, Upload, CreditCard, CopyMinus, History, Undo2, UserCog, Building2, Wrench, Bell, KeyRound, FileText, Send, Printer, Loader2, MoreVertical, Bug as BugIcon, Coins, PiggyBank, FilterX, ShoppingCart, Search, ChevronRight, Mail, Sparkles } from "lucide-react";
import { ShoppingListView } from "@/components/ShoppingList";
import FeedbackAdmin from "./FeedbackAdmin";
import { useAuth } from "@/contexts/AuthContext";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { toast } from "sonner";
import { toCsv, downloadCsv, parseCsv } from "@/lib/csv";
import { ReceiptUploader } from "@/components/ReceiptUploader";
import PlaidCards from "@/components/PlaidCards";
import { Checkbox } from "@/components/ui/checkbox";
import { SortableHeader, useSort, makeComparator } from "@/components/SortableHeader";
import ColonyPortalUsersDialog from "./ColonyPortalUsersDialog";
import ColonyTicketsAdmin from "./ColonyTicketsAdmin";
import { ColonyDotName, colonyColor } from "@/lib/colonyColors";
import ColonyWatchersDialog from "./ColonyWatchersDialog";
import WorkerUserDialog from "./WorkerUserDialog";
import { PayrollBillingSection } from "@/components/payroll/PayrollBillingSection";
import { PayrollTemplatesSection } from "@/components/payroll/PayrollTemplatesSection";
import { PayrollWorkerDashboard } from "@/components/payroll/PayrollWorkerDashboard";
import { PayrollWorkerDetailsDialog } from "@/components/payroll/PayrollWorkerDetailsDialog";
import { ReceivedPaymentsPanel } from "@/components/finance/ReceivedPaymentsPanel";
import { InvoicePaymentsDialog } from "@/components/finance/InvoicePaymentsDialog";
import { InvoiceEditDialog } from "@/components/finance/InvoiceEditDialog";
import { InvoiceEditHistory } from "@/components/finance/InvoiceEditHistory";
import { InvoiceEditReasonDialog } from "@/components/finance/InvoiceEditReasonDialog";
import { InvoiceResendDialog } from "@/components/finance/InvoiceResendDialog";
import { shouldOfferInvoiceResend } from "@/lib/invoiceResendPolicy";
import { FinanceSummaryCards } from "@/components/finance/FinanceSummaryCards";
import { DisplayNameField } from "@/components/finance/DisplayNameField";
import { AddWorkerForm } from "@/components/finance/AddWorkerForm";
import { TutorialButton } from "@/components/tutorials/TutorialButton";
import { InvoiceAttachments } from "@/components/finance/InvoiceAttachments";
import { ColonyStatementTotals, ChargeBillingBadge } from "@/components/finance/ColonyStatementTotals";
import { useExpenseList } from "@/components/finance/useExpenseList";
import { ExpenseReceiptFields } from "@/components/finance/ExpenseReceiptFields";
import { ReceiptFollowupBadge, AmountMismatchBadge } from "@/components/finance/ReceiptFollowup";
import { InvoiceAttachmentEditor } from "@/components/finance/InvoiceAttachmentEditor";
import { useInvoiceAttachmentDraft } from "@/components/finance/useInvoiceAttachmentDraft";
import "@/styles/management.css";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";
import { useAdminDateRange, valueInDateRange } from "@/lib/adminDateRange";
import { readTicketParam, readExpenseParam } from "@/lib/managementDeepLink";

const todayISO = () => new Date().toISOString().slice(0, 10);
const statementCache = new Map();
const STATEMENT_CACHE_TTL_MS = 60_000;
const expenseCurrency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const startOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
const toLocalISO = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const previousPayrollPeriod = () => {
  const end = new Date();
  end.setHours(12, 0, 0, 0);
  const daysSinceFriday = ((end.getDay() - 5 + 7) % 7) || 7;
  end.setDate(end.getDate() - daysSinceFriday);
  const start = new Date(end);
  start.setDate(end.getDate() - 5);
  return { period_start: toLocalISO(start), period_end: toLocalISO(end) };
};

// Every backend endpoint that reads/writes an expense or payroll row is
// ledger-scoped. Central helper so we can't forget to pass it.
const withLedger = (path, ledger) => {
  if (!ledger || ledger === "pool") return path;  // default; backend also defaults
  return path + (path.includes("?") ? "&" : "?") + `ledger=${encodeURIComponent(ledger)}`;
};

function ExpandingFinanceSearch({ value, onChange, placeholder, testId, ariaLabel, alwaysOpen = false }) {
  const [open, setOpen] = useState(() => alwaysOpen || Boolean(value));
  const inputRef = React.useRef(null);
  const expandAndFocus = () => {
    setOpen(true);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <div
      className={`relative flex max-w-lg items-center transition-[width] duration-300 ${alwaysOpen ? "management-search" : ""} ${open ? "w-full" : "w-11"}`}
      data-testid={`${testId}-wrap`}
    >
      <button
        type="button"
        onClick={expandAndFocus}
        className={`absolute left-0 z-10 flex h-11 w-11 items-center justify-center rounded-sm text-muted-foreground transition-[color,background-color,border-color] hover:text-foreground ${open ? "border border-transparent" : "border border-input bg-background shadow-sm hover:bg-secondary/50"}`}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={`${testId}-input`}
        title={ariaLabel}
        data-testid={`${testId}-toggle`}
      >
        <Search className="h-4 w-4" />
      </button>
      <Input
        ref={inputRef}
        id={`${testId}-input`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => { if (!alwaysOpen && !value.trim()) setOpen(false); }}
        placeholder={open ? placeholder : ""}
        className={`h-11 rounded-sm pl-11 transition-[width,opacity] duration-300 ${open ? "w-full pr-10 opacity-100" : "pointer-events-none w-11 opacity-0"}`}
        aria-label={ariaLabel}
        tabIndex={open ? 0 : -1}
        data-testid={testId}
      />
      {open && value && (
        <button
          type="button"
          onPointerDown={(event) => event.preventDefault()}
          onClick={() => { onChange(""); inputRef.current?.focus(); }}
          className="absolute right-1 z-10 flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
          aria-label={`Clear ${ariaLabel.toLowerCase()}`}
          title="Clear"
          data-testid={`${testId}-clear`}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export default function Finance({ ledger = "pool", pageTitle = "Finance", pageSubtitle = "Expenses, payroll, and net profit. Visible to admin accounts only.", showCards = true } = {}) {
  const { user: authUser } = useAuth();
  useEffect(() => {
    if (ledger !== "mgmt_usa_nj") return;
    document.body.classList.add("mgmt-finance-surface");
    return () => document.body.classList.remove("mgmt-finance-surface");
  }, [ledger]);
  const linkedTicketIdRef = useRef(ledger === "mgmt_usa_nj" ? readTicketParam(window.location.search) : "");
  const linkedTicketId = linkedTicketIdRef.current;
  const [linkedExpenseId] = useState(() => (ledger === "mgmt_usa_nj" ? readExpenseParam(window.location.search) : ""));
  useEffect(() => {
    if (!linkedExpenseId) return;
    try { localStorage.setItem(`finance_tab_${ledger}`, "expenses"); localStorage.setItem(`finance_expense_subtab_${ledger}`, "expenses"); } catch { /* private mode */ }
    const params = new URLSearchParams(window.location.search);
    params.delete("expense");
    const search = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${search ? `?${search}` : ""}`);
  }, [linkedExpenseId, ledger]);
  const isRealAdmin = authUser?.role === "admin";
  // Owner-only surface: the Issues/Feedback tracker is where the primary
  // admin collects bug reports for the developer. Extra admins the owner
  // hands out can't act on those, so we hide the tab from them entirely.
  const isOwner = authUser?.is_owner === true;
  // Public demo sandbox — hide Desktop / Users / Issues / Connected cards
  // so the visitor stays inside the safe demo surface. Keep Overview,
  // Tickets, Expenses, Payroll (the whole reason to demo).
  const isDemo = authUser?.is_demo === true;
  // Remember the active tab per ledger so a page refresh brings you right
  // back to where you were (Desktop, Tickets, Payroll, …).
  const validTabs = ["overview", "expenses", "payroll",
    ...(ledger === "mgmt_usa_nj" ? ["tickets", "shopping", ...(isDemo ? [] : [...(isOwner ? ["issues"] : [])])] : []),
    ...(showCards && !isDemo ? ["cards"] : [])];
  const [tab, setTabRaw] = useState(() => {
    if (linkedTicketId) return "tickets";
    if (linkedExpenseId) return "expenses";
    if (typeof window !== "undefined" && window.location.search.includes("oauth_state_id")) return "cards";
    try {
      const saved = localStorage.getItem(`finance_tab_${ledger}`);
      return validTabs.includes(saved) ? saved : "overview";
    } catch { return "overview"; }
  });
  const setTab = (k) => {
    setTabRaw(k);
    try { localStorage.setItem(`finance_tab_${ledger}`, k); } catch { /* private mode */ }
  };
  const [navigationRequest, setNavigationRequest] = useState(null);
  const selectMainTab = (target) => {
    setNavigationRequest(null);
    setTab(target);
  };
  const navigateFinance = (target, section) => {
    setNavigationRequest({ target, section, id: Date.now() });
    setTab(target);
  };
  const { dateFrom, dateTo, setDateFrom, setDateTo, showAll: resetDateRange } = useAdminDateRange();

  // Cross-tab jump: clicking "Ticket" on a shopping-list item switches to
  // the Tickets tab and focuses that ticket. Bumping the counter forces
  // ColonyTicketsAdmin to remount + re-focus even if it's the same id.
  const [focusTicket, setFocusTicket] = useState(() => (
    linkedTicketId ? { id: linkedTicketId, n: Date.now() } : null
  ));
  const consumedLinkedTicketRef = useRef(false);
  useEffect(() => {
    if (!linkedTicketId || consumedLinkedTicketRef.current) return;
    consumedLinkedTicketRef.current = true;
    resetDateRange();
    const params = new URLSearchParams(window.location.search);
    params.delete("ticket");
    const search = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${search ? `?${search}` : ""}`);
  }, [linkedTicketId, resetDateRange]);
  const openTicketFromShopping = (ticketId) => {
    resetDateRange();
    setFocusTicket(previous => ({ id: ticketId, n: (previous?.n || 0) + 1 }));
    setTab("tickets");
  };

  // Full colony list for the standalone "Add item" dialog's colony picker.
  const [shoppingColonies, setShoppingColonies] = useState([]);
  useEffect(() => {
    if (ledger !== "mgmt_usa_nj") return;
    api.get("/mgmt/colonies").then((r) => setShoppingColonies(r.data || [])).catch(() => {});
  }, [ledger]);

  return (
    <div className={`${tab === "overview" ? "space-y-4 sm:space-y-5 lg:space-y-6" : "space-y-5 sm:space-y-6"} ${ledger === "mgmt_usa_nj" ? "management-skin lg:w-[calc(100vw-4rem)] lg:max-w-[1440px] lg:relative lg:left-1/2 lg:-translate-x-1/2" : ""} min-w-0`} data-testid={`finance-workspace-${ledger}`}>
      {ledger === "mgmt_usa_nj" ? (
        <div className="finance-management-toolbar -mx-3 -mt-4 px-3 pt-1.5 pb-1 sm:-mx-4 sm:-mt-6 sm:px-4 sm:pt-2 flex flex-col gap-2 lg:flex-row lg:items-end" data-testid="finance-management-toolbar">
          <FinanceTabs tab={tab} setTab={selectMainTab} ledger={ledger} isOwner={isOwner} isDemo={isDemo} showCards={showCards} className="lg:flex-1" />
          <FinanceDateControls dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} resetDateRange={resetDateRange} className="lg:ml-auto" />
        </div>
      ) : (
        <>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <div className="min-w-0">
            <p className="overline">Owner only</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight break-words" data-testid="finance-page-title">{pageTitle}</h1>
            <p className="text-sm text-muted-foreground mt-1">{pageSubtitle}</p>
          </div>
            <FinanceDateControls dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} resetDateRange={resetDateRange} className="lg:ml-auto" />
          </div>
          <FinanceTabs tab={tab} setTab={selectMainTab} ledger={ledger} isOwner={isOwner} isDemo={isDemo} showCards={showCards} />
        </>
      )}

      <div
        className={ledger === "mgmt_usa_nj" && !["overview", "expenses"].includes(tab) ? "w-full max-w-[1248px] mx-auto" : "w-full"}
        data-testid={`finance-tab-content-${tab}`}
      >
        {tab === "overview" && <Overview dateFrom={dateFrom} dateTo={dateTo} ledger={ledger} onNavigate={navigateFinance} />}
        {tab === "tickets" && ledger === "mgmt_usa_nj" && (
          <ColonyTicketsAdmin
            key={focusTicket?.n || "tickets"}
            focusTicketId={focusTicket?.id}
            onGoToShoppingList={() => setTab("shopping")}
            dateFrom={dateFrom}
            dateTo={dateTo}
            setDateFrom={setDateFrom}
            setDateTo={setDateTo}
          />
        )}
        {tab === "shopping" && ledger === "mgmt_usa_nj" && (
          <ShoppingListView client={api} basePath="/mgmt" onOpenTicket={openTicketFromShopping} allColonies={shoppingColonies} />
        )}
        {tab === "expenses" && (ledger === "mgmt_usa_nj"
          ? <ExpensesInvoicesSplit dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} ledger={ledger} hideCards={isDemo} navigationRequest={navigationRequest} focusExpenseId={linkedExpenseId} />
          : <Expenses dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} ledger={ledger} />)}
        {tab === "payroll" && <Payroll dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} ledger={ledger} navigationRequest={navigationRequest} />}
        {tab === "issues" && ledger === "mgmt_usa_nj" && isOwner && !isDemo && <FeedbackAdmin />}
        {tab === "cards" && showCards && !isDemo && <PlaidCards />}
      </div>
    </div>
  );
}

function FinanceDateControls({ dateFrom, dateTo, setDateFrom, setDateTo, resetDateRange, className = "" }) {
  return (
    <div className={`grid grid-cols-2 sm:flex items-end gap-2 w-full lg:w-auto ${className}`} data-testid="finance-date-controls">
      <div className="min-w-0 sm:w-40"><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">From</Label><Input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className="finance-date-input h-9 rounded-sm border border-primary bg-transparent focus-visible:ring-primary" data-testid="finance-from" /></div>
      <div className="min-w-0 sm:w-40"><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">To</Label><Input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} className="finance-date-input h-9 rounded-sm border border-primary bg-transparent focus-visible:ring-primary" data-testid="finance-to" /></div>
      <Button variant="ghost" onClick={resetDateRange} className="h-11 sm:h-9 rounded-sm text-xs px-2 col-span-2 sm:col-span-1" title="Reset the date filter to show everything" data-testid="finance-show-all">Show all</Button>
    </div>
  );
}

function FinanceTabs({ tab, setTab, ledger, isOwner, isDemo, showCards, className = "" }) {
  const tabs = [
    { k: "overview", label: ledger === "mgmt_usa_nj" ? "Dashboard" : "Overview / P&L", icon: TrendingUp },
    ...(ledger === "mgmt_usa_nj" ? [{ k: "tickets", label: "Tickets", icon: Wrench }, { k: "shopping", label: "Shopping list", icon: ShoppingCart }] : []),
    { k: "expenses", label: ledger === "mgmt_usa_nj" ? "Inv & Exp" : "Expenses", icon: Receipt },
    { k: "payroll", label: "Payroll", icon: Users },
    ...(ledger === "mgmt_usa_nj" && isOwner && !isDemo ? [{ k: "issues", label: "Issues", icon: BugIcon }] : []),
    ...(showCards && !isDemo ? [{ k: "cards", label: "Connected cards", icon: CreditCard }] : []),
  ];
  return (
    <div className={`management-main-tabs flex flex-wrap gap-1 sm:gap-2 border-b border-border ${className}`} data-testid="finance-tabs">
      {tabs.map(({ k, label, icon: Icon }) => <button key={k} onClick={() => setTab(k)} aria-current={tab === k ? "page" : undefined} className={`px-3 sm:px-4 min-h-11 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors flex items-center gap-2 ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`finance-tab-${k}`}><Icon className="w-4 h-4 shrink-0" /> {label}</button>)}
    </div>
  );
}

// ---------------------------------------------------------------------
function Overview({ dateFrom, dateTo, ledger, onNavigate }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(withLedger(`/finance/summary?date_from=${dateFrom}&date_to=${dateTo}`, ledger))
      .then((r) => setS(r.data))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, ledger]);
  useEffect(() => { load(); }, [load]);

  if (loading || !s) return <div className="py-14 flex items-center justify-center text-sm text-muted-foreground" data-testid="finance-dashboard-loading"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading dashboard…</div>;

  const isProfit = s.net_profit >= 0;
  return (
    <div className="w-full max-w-[1440px] mx-auto space-y-8 sm:space-y-10 lg:space-y-14" data-testid="finance-overview">
      {ledger === "mgmt_usa_nj" && <div className="flex flex-wrap items-center justify-between gap-3"><h1 className="management-title font-display" data-testid="management-dashboard-heading">Dashboard</h1><TutorialButton section="dashboard" /></div>}
      <section className="space-y-3 sm:space-y-4" aria-labelledby="dashboard-snapshot-heading">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-5">
          <div><h2 id="dashboard-snapshot-heading" className="font-display text-sm sm:text-base font-semibold" data-testid="dashboard-snapshot-heading">Financial snapshot</h2><p className="text-[10px] sm:text-[11px] text-muted-foreground mt-0.5" data-testid="dashboard-snapshot-description">Money received, operating costs, payroll, and the resulting profit.</p></div>
          <p className="self-start sm:self-auto text-[11px] sm:text-xs text-muted-foreground mono-num border border-border/70 bg-secondary/35 px-3 py-1.5 rounded-sm" data-testid="dashboard-period">{dateFrom || dateTo ? `${dateFrom || "Start"} → ${dateTo || "Today"}` : "All history"}</p>
        </div>
        <div className="management-overview-kpis grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8" data-testid="dashboard-kpi-grid">
        <Stat label="Revenue paid" value={fmtMoney(s.revenue)} sub={`${s.invoices_count} invoices`} accent="text-amber-800" testId="stat-revenue" icon={Coins} tone="amber" />
        <Stat label="Expenses" value={fmtMoney(s.expenses_total)} sub={`${s.expenses_count} entries`} accent="text-rose-800" testId="stat-expenses" icon={Wallet} tone="rose" onActivate={() => onNavigate?.("expenses", "expenses")} actionLabel="Open expenses" />
        <Stat label="Payroll" value={fmtMoney(s.payroll_total)} sub={`${s.payroll_count} entries`} accent="text-teal-800" testId="stat-payroll" icon={Users} tone="teal" onActivate={() => onNavigate?.("payroll", "records")} actionLabel="Open payroll" />
        <Stat
          label="Net profit"
          value={fmtMoney(s.net_profit)}
          sub={isProfit ? "Income after costs" : "Costs exceed income"}
          accent={isProfit ? "text-emerald-800" : "text-destructive"}
          testId="stat-net-profit"
          icon={isProfit ? PiggyBank : TrendingDown}
          tone={isProfit ? "emerald" : "red"}
        />
        </div>
      </section>

      <div className="grid xl:grid-cols-12 gap-6 sm:gap-8 lg:gap-10 xl:gap-12 items-start" data-testid="dashboard-main-grid">
        {ledger === "mgmt_usa_nj" && <div className="xl:col-span-8"><BungalowColoniesSection onNavigate={onNavigate} /></div>}
        <div className={ledger === "mgmt_usa_nj" ? "xl:col-span-4 space-y-6 sm:space-y-8 lg:space-y-10" : "xl:col-span-12 grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10"} data-testid="dashboard-breakdowns">
          <DashboardBreakdown title="Expenses by category" description={`${s.expenses_count} expense entries`} icon={Receipt} items={s.expenses_by_category.map((category) => ({ key: category.category, label: category.category, value: category.amount }))} empty="No expenses in this period." color="bg-rose-700" testPrefix="overview-cat" />
          <DashboardBreakdown title="Payroll by worker" description={`${s.payroll_count} payroll entries`} icon={Users} items={s.payroll_by_worker.map((worker) => ({ key: worker.worker_user_id, label: worker.worker_name, value: worker.total, meta: `${worker.entries} entries` }))} empty="No payroll in this period." color="bg-teal-700" testPrefix="overview-worker" />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent, testId, icon: Icon, tone = "slate", onActivate, actionLabel }) {
  const [armed, setArmed] = useState(false);
  const lastPressRef = React.useRef(0);
  const timerRef = React.useRef(null);
  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const activate = (event) => {
    if (!onActivate) return;
    if (event.detail === 0) { onActivate(); return; }
    const now = Date.now();
    if (now - lastPressRef.current < 650) {
      window.clearTimeout(timerRef.current);
      lastPressRef.current = 0;
      setArmed(false);
      onActivate();
      return;
    }
    lastPressRef.current = now;
    setArmed(true);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setArmed(false), 700);
  };

  const TONES = {
    amber: { line: "bg-amber-500", chip: "bg-amber-50", icon: "text-amber-800" },
    rose: { line: "bg-rose-600", chip: "bg-rose-50", icon: "text-rose-800" },
    teal: { line: "bg-teal-600", chip: "bg-teal-50", icon: "text-teal-800" },
    emerald: { line: "bg-emerald-600", chip: "bg-emerald-50", icon: "text-emerald-800" },
    red: { line: "bg-red-700", chip: "bg-red-50", icon: "text-red-800" },
    slate: { line: "bg-slate-500", chip: "bg-secondary", icon: "text-muted-foreground" },
  };
  const selected = TONES[tone] || TONES.slate;
  return (
    <div onClick={onActivate ? activate : undefined} onKeyDown={onActivate ? (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onActivate(); } } : undefined} role={onActivate ? "button" : undefined} tabIndex={onActivate ? 0 : undefined} aria-label={onActivate ? actionLabel : undefined} className={`relative overflow-hidden border rounded-sm bg-background px-3 sm:px-5 lg:px-6 py-3 sm:py-3.5 transition-[border-color,box-shadow,transform,background-color] duration-200 hover:border-foreground/25 hover:shadow-[0_10px_30px_rgba(15,23,42,0.07)] hover:-translate-y-0.5 ${onActivate ? "cursor-pointer touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" : ""} ${armed ? "border-primary bg-primary/5" : "border-border"}`} data-testid={testId}>
      <div className={`absolute inset-x-0 top-0 h-0.5 ${selected.line}`} />
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] sm:text-xs font-semibold uppercase text-muted-foreground">{label}</p>
        {Icon && (
          <div className={`${selected.chip} rounded-sm p-1.5 flex-shrink-0`}>
            <Icon className={`w-4 h-4 ${selected.icon}`} />
          </div>
        )}
      </div>
      <p className={`mono-num text-lg sm:text-xl lg:text-2xl font-bold mt-2 break-words ${accent}`} data-testid={`${testId}-value`}>{value}</p>
      {sub && <p className="text-[11px] sm:text-xs text-muted-foreground mt-1" data-testid={`${testId}-sub`}>{sub}</p>}
    </div>
  );
}

function DashboardBreakdown({ title, description, icon: Icon, items, empty, color, testPrefix }) {
  const max = Math.max(...items.map((item) => Number(item.value || 0)), 0);
  return (
    <section className="management-breakdown border border-border rounded-sm bg-background shadow-sm" data-testid={`${testPrefix}-panel`}>
      <div className="px-3 sm:px-5 lg:px-6 py-3 border-b border-border flex items-start gap-2.5 sm:gap-3.5 bg-secondary/15">
        <div className="h-8 w-8 rounded-sm bg-secondary flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>
        <div><h2 className="font-semibold text-sm sm:text-base" data-testid={`${testPrefix}-heading`}>{title}</h2><p className="text-xs text-muted-foreground mt-0.5" data-testid={`${testPrefix}-description`}>{description}</p></div>
      </div>
      {items.length === 0 ? <p className="px-4 py-8 text-sm text-muted-foreground text-center" data-testid={`${testPrefix}-empty`}>{empty}</p> : (
        <ul className="divide-y divide-border">
          {items.map((item) => {
            const width = max > 0 ? Math.max(4, Number(item.value || 0) / max * 100) : 0;
            return (
              <li key={item.key} className="px-3 sm:px-5 lg:px-6 py-2 transition-colors hover:bg-secondary/20" data-testid={`${testPrefix}-${item.key}`}>
                <div className="flex items-start justify-between gap-3 text-sm"><span className="min-w-0 truncate">{item.label}{item.meta && <span className="block text-[11px] text-muted-foreground">{item.meta}</span>}</span><span className="mono-num font-semibold shrink-0">{fmtMoney(item.value)}</span></div>
                <div className="h-1 bg-secondary mt-2 overflow-hidden rounded-sm" aria-hidden="true"><div className={`h-full ${color}`} style={{ width: `${width}%` }} /></div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------
function Expenses({ dateFrom, dateTo, setDateFrom, setDateTo, ledger, focusExpenseId = "" }) {
  const { list, loading, hasData, error: listError, load } = useExpenseList({ ledger, dateFrom, dateTo });
  const [rowLimit, setRowLimit] = useState(50);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importReport, setImportReport] = useState(null); // { inserted, errors }
  const [dupePreview, setDupePreview] = useState(null);   // { groups, total_groups, total_to_delete } | null
  const [dupeBusy, setDupeBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [restoreSince, setRestoreSince] = useState("");   // datetime-local value
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [rollbackBusy, setRollbackBusy] = useState(false);
  const [rollbackPreview, setRollbackPreview] = useState(null); // { snap, diff }
  const [selected, setSelected] = useState(() => new Set()); // multi-select ids
  const { sortKey, sortDir, toggleSort } = useSort("date", "desc");
  const fileInputRef = React.useRef(null);
  // Management-only: filter/sort expenses by which bungalow colony they're
  // billed to. `colonyMap` turns colony_id → name for the inline badge.
  const isMgmt = ledger === "mgmt_usa_nj";
  const [colonyList, setColonyList] = useState([]);
  const [colonyFilter, setColonyFilter] = useState("all");
  // Filter by who submitted the expense — built from whatever's in the
  // currently-loaded date range (no extra API call needed).
  const [submitterFilter, setSubmitterFilter] = useState("all");
  const [expenseSearch, setExpenseSearch] = useState("");
  // "all" | "uninvoiced" | "invoiced" — mgmt only, persisted per ledger.
  const [invoicedFilter, setInvoicedFilter] = useState(() => {
    try { return localStorage.getItem(`finance_invoiced_filter_${ledger}`) || "all"; } catch { return "all"; }
  });
  const changeInvoicedFilter = (v) => {
    setInvoicedFilter(v);
    try { localStorage.setItem(`finance_invoiced_filter_${ledger}`, v); } catch { /* private mode */ }
  };
  useEffect(() => {
    if (!isMgmt) return;
    api.get("/mgmt/colonies").then((r) => setColonyList(r.data || [])).catch(() => {});
  }, [isMgmt]);
  const colonyMap = useMemo(() => {
    const m = {}; colonyList.forEach((c) => { m[c.id] = c.name; }); return m;
  }, [colonyList]);

  // Defensive: when every dialog in this view is closed, force-release any
  // body scroll-lock that Radix Dialog may have left behind. We've seen
  // production reports of "page won't scroll" after a tap that interrupted a
  // dialog open/close transition; this guarantees the lock is gone.
  useEffect(() => {
    const anyDialogOpen = open || !!dupePreview || historyOpen || rollbackOpen;
    if (!anyDialogOpen && typeof document !== "undefined") {
      // Radix sets pointer-events:none + overflow:hidden via inline style and
      // `data-scroll-locked` attribute. Both have to go.
      document.body.style.pointerEvents = "";
      document.body.style.overflow = "";
      document.body.removeAttribute("data-scroll-locked");
    }
  }, [open, dupePreview, historyOpen, rollbackOpen]);

  // Receipts are stripped from the list payload for speed. Pull them
  // on-demand when a user actually opens/edits/views a row.
  const fetchFullExpense = useCallback(async (row) => {
    if (!row?.id) return row;
    if (Array.isArray(row.receipts) || row.receipt_data_url) return row; // already loaded
    try {
      const { data } = await api.get(`/expenses/${row.id}`);
      return { ...row, ...data };
    } catch (_) {
      toast.error("Could not load this expense. Please try again.");
      return null;
    }
  }, []);
  const openDetails = useCallback(async (row) => {
    setDetails({ ...row, _loadingReceipts: true });
    const full = await fetchFullExpense(row);
    setDetails(current => current?.id === row.id ? (full || { ...row, _loadingReceipts: false }) : current);
  }, [fetchFullExpense]);
  const openedLinkedRef = useRef(false);
  useEffect(() => {
    if (!focusExpenseId || openedLinkedRef.current) return;
    openedLinkedRef.current = true;
    openDetails({ id: focusExpenseId });
  }, [focusExpenseId, openDetails]);
  const openReceipts = useCallback(async (row) => {
    const full = await fetchFullExpense(row);
    if (!full) return;
    const imgs = [...(full.receipts || []), ...(full.receipt_data_url ? [full.receipt_data_url] : [])].filter(Boolean);
    if (imgs.length) setReceiptView(imgs);
  }, [fetchFullExpense]);
  const openEdit = useCallback(async (row) => {
    const full = await fetchFullExpense(row);
    if (!full) return;
    setDetails(null);
    setEditing(full);
    setOpen(true);
  }, [fetchFullExpense]);

  const total = useMemo(() => list.reduce((s, e) => s + Number(e.amount || 0), 0), [list]);

  // Sorted view of the list — the multi-select checkboxes still operate on
  // ids, so the sort order is purely presentational.
  const sortedList = useMemo(() => {
    const getValue = (e) => {
      switch (sortKey) {
        case "date":      return e.date || "";
        case "category":  return (e.category || "").toLowerCase();
        case "vendor":    return (e.vendor || "").toLowerCase();
        case "submitter": return (e.submitter_name || e.created_by || "").toLowerCase();
        case "notes":     return (e.notes || "").toLowerCase();
        case "amount":    return Number(e.amount || 0);
        case "colony":    return (colonyMap[e.colony_id] || "~").toLowerCase();
        default:          return "";
      }
    };
    return [...list].sort(makeComparator(getValue, sortDir));
  }, [list, sortKey, sortDir, colonyMap]);

  // Distinct submitters seen in the currently-loaded list, for the
  // "Submitted by" filter dropdown.
  const submitterOptions = useMemo(() => {
    const seen = new Map();
    list.forEach((e) => {
      const id = e.created_by_user_id || e.created_by || "";
      if (!id || seen.has(id)) return;
      seen.set(id, e.submitter_name || e.created_by || "Unknown");
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [list]);

  // Apply the colony filter (mgmt only) + submitter filter on top of the sort.
  const visibleList = useMemo(() => {
    let out = sortedList;
    if (isMgmt && colonyFilter !== "all") {
      out = colonyFilter === "__none__" ? out.filter((e) => !e.colony_id) : out.filter((e) => e.colony_id === colonyFilter);
    }
    if (isMgmt && invoicedFilter === "uninvoiced") out = out.filter((e) => !e.invoiced);
    if (isMgmt && invoicedFilter === "invoiced") out = out.filter((e) => e.invoiced);
    if (submitterFilter !== "all") {
      out = out.filter((e) => (e.created_by_user_id || e.created_by || "") === submitterFilter);
    }
    const term = expenseSearch.trim().toLowerCase();
    if (term) {
      out = out.filter((expense) => {
        const amount = Number(expense.amount || 0);
        const haystack = [
          expense.name,
          expense.description,
          expense.date,
          expense.category,
          expense.vendor,
          expense.notes,
          expense.no_receipt_reason,
          expense.payment_method,
          expense.payment_reference,
          expense.submitter_name,
          expense.created_by,
          colonyMap[expense.colony_id],
          String(amount),
          amount.toFixed(2),
          expenseCurrency.format(amount),
        ].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(term);
      });
    }
    return out;
  }, [sortedList, isMgmt, colonyFilter, invoicedFilter, submitterFilter, expenseSearch, colonyMap]);
  useEffect(() => { setRowLimit(50); }, [dateFrom, dateTo, ledger, sortKey, sortDir, colonyFilter, invoicedFilter, submitterFilter, expenseSearch]);
  const renderedList = isMgmt ? visibleList.slice(0, rowLimit) : visibleList;
  const selectableList = useMemo(
    () => visibleList.filter((expense) => expense.source_type !== "payroll_colony_charge"),
    [visibleList],
  );

  // Auto-clear stale selection when the visible list changes (e.g. after a
  // delete or a filter change) so the toolbar count stays accurate.
  useEffect(() => {
    const visibleIds = new Set(selectableList.map((e) => e.id));
    setSelected((prev) => {
      const next = new Set();
      prev.forEach((id) => { if (visibleIds.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [selectableList]);

  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = selectableList.length > 0 && selected.size === selectableList.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableList.map((e) => e.id)));
  const [receiptView, setReceiptView] = useState(null); // string[] of image data urls
  const [details, setDetails] = useState(null); // expense row → info dialog
  const [bulkColony, setBulkColony] = useState(""); // bulk "assign to colony" pick

  const bulkAssignColony = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !bulkColony) return;
    try {
      const cid = bulkColony === "__none__" ? "" : bulkColony;
      const r = await api.post(withLedger("/expenses/bulk-assign-colony", ledger), { ids, colony_id: cid });
      const name = cid ? (colonyMap[cid] || "colony") : "unassigned overhead";
      toast.success(`${r.data.updated} expense${r.data.updated === 1 ? "" : "s"} assigned to ${name}.${r.data.skipped_invoiced ? ` ${r.data.skipped_invoiced} skipped (already on an invoice).` : ""}`);
      setSelected(new Set());
      setBulkColony("");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected expense${ids.length === 1 ? "" : "s"}? You can roll this back from Restore history.`)) return;
    try {
      const r = await api.post(withLedger("/expenses/bulk-delete", ledger), { ids });
      toast.success(`Deleted ${r.data.deleted} expense${r.data.deleted === 1 ? "" : "s"}.`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete this expense (${fmtMoney(e.amount)} · ${e.category})?`)) return;
    try { await api.delete(`/expenses/${e.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };

  // Look across the WHOLE ledger (not just the visible date range), so the
  // admin can spot ancient duplicates without first widening the date filter.
  const openDupePreview = async () => {
    setDupeBusy(true);
    try {
      const r = await api.get(withLedger("/expenses/duplicates", ledger));
      if (r.data.total_to_delete === 0) {
        toast.success("No duplicates found — your books are clean.");
        return;
      }
      setDupePreview(r.data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setDupeBusy(false); }
  };

  const confirmDedupe = async () => {
    setDupeBusy(true);
    try {
      const r = await api.post(withLedger("/expenses/dedupe", ledger));
      toast.success(`Removed ${r.data.deleted} duplicate${r.data.deleted === 1 ? "" : "s"} across ${r.data.groups} group${r.data.groups === 1 ? "" : "s"}.`);
      setDupePreview(null);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setDupeBusy(false); }
  };

  // ---- Restore history ----
  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const r = await api.get(withLedger("/expenses/deletion-history?limit=100", ledger));
      setHistory(r.data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  const restoreBatch = async (batch) => {
    if (!window.confirm(`Restore ${batch.count} expense${batch.count === 1 ? "" : "s"} deleted on ${new Date(batch.deleted_at).toLocaleString()}?`)) return;
    try {
      const r = await api.post(`/expenses/restore-batch/${batch.id}`);
      toast.success(`Restored ${r.data.restored} expense${r.data.restored === 1 ? "" : "s"}.${r.data.skipped ? ` ${r.data.skipped} already in books.` : ""}`);
      openHistory();
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  const restoreSinceDate = async () => {
    if (!restoreSince) return toast.error("Pick a date and time first.");
    // datetime-local gives "YYYY-MM-DDTHH:MM" in local time; convert to ISO with Z.
    const iso = new Date(restoreSince).toISOString();
    if (!window.confirm(`Restore every expense deleted on or after ${new Date(iso).toLocaleString()}? This will roll back EVERY deletion batch from that moment forward.`)) return;
    try {
      const r = await api.post(withLedger("/expenses/restore-since", ledger), { since: iso });
      toast.success(`Restored ${r.data.restored} expense${r.data.restored === 1 ? "" : "s"} from ${r.data.batches} deletion batch${r.data.batches === 1 ? "" : "es"}.`);
      openHistory();
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  // ---- Roll back to a past day (full-ledger snapshot restore) ----
  const openRollback = async () => {
    setRollbackOpen(true);
    setRollbackPreview(null);
    try {
      const r = await api.get(withLedger("/expenses/snapshots", ledger));
      setSnapshots(r.data.snapshots || []);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  // Step 1: fetch the diff so the admin sees exactly what will change.
  const previewRollback = async (snap) => {
    setRollbackBusy(true);
    try {
      const r = await api.get(`/expenses/restore-snapshot/${snap.id}/preview`);
      setRollbackPreview({ snap, diff: r.data });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setRollbackBusy(false); }
  };

  // Step 2: actually execute the rollback after the diff was reviewed.
  const doRollback = async (snap) => {
    setRollbackBusy(true);
    try {
      const r = await api.post(`/expenses/restore-snapshot/${snap.id}`);
      toast.success(`Rolled back to ${r.data.to_date} — ${r.data.restored} expense${r.data.restored === 1 ? "" : "s"} restored.`);
      setRollbackOpen(false);
      setRollbackPreview(null);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setRollbackBusy(false); }
  };

  const exportCsv = () => {
    // Exports whatever's currently visible (respects colony/invoiced/submitter
    // filters) so the CSV matches what the admin is looking at on screen.
    if (visibleList.length === 0) return toast.error("Nothing to export with the current filters.");
    const csv = toCsv(visibleList, [
      { key: "date", label: "Date" },
      { key: "category", label: "Category" },
      { key: "amount", label: "Amount", format: (v) => Number(v || 0).toFixed(2) },
      { key: "vendor", label: "Vendor" },
      { key: "payment_method", label: "Payment method" },
      { key: "payment_reference", label: "Payment reference" },
      { key: "submitter_name", label: "Submitted by" },
      { key: "notes", label: "Notes" },
    ]);
    downloadCsv(`expenses_${dateFrom}_to_${dateTo}.csv`, csv);
    toast.success(`Exported ${visibleList.length} expenses.`);
  };

  const onImportFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const { headers, rows } = parseCsv(text);
      if (rows.length === 0) {
        toast.error("CSV is empty or could not be parsed.");
        return;
      }

      // Auto-map common column-name variants used by Zoho Books, QuickBooks,
      // Wave, Excel etc. so users can drop their export in without renaming
      // headers. Keys are normalized to lowercase by parseCsv.
      const COLUMN_ALIASES = {
        date: ["date", "expense date", "transaction date", "txn date", "posted date"],
        category: ["category", "expense account", "account", "account name", "expense category", "expense type"],
        amount: ["amount", "total", "expense amount", "amount (usd)", "amount (incl. tax)", "amount excl. tax"],
        vendor: ["vendor", "vendor name", "paid to", "payee", "supplier", "merchant"],
        notes: ["notes", "description", "expense description", "memo", "reference", "reference number"],
      };
      const findCol = (key) => COLUMN_ALIASES[key].find((alias) => headers.includes(alias));
      const cols = {
        date: findCol("date"),
        category: findCol("category"),
        amount: findCol("amount"),
        vendor: findCol("vendor"),
        notes: findCol("notes"),
      };
      const missingRequired = ["date", "category", "amount"].filter((k) => !cols[k]);
      if (missingRequired.length) {
        toast.error(`CSV is missing column(s) for: ${missingRequired.join(", ")}. We accept names like Date / Expense Date, Category / Expense Account, Amount / Total.`);
        return;
      }

      // Try multiple date formats — Zoho international exports often use
      // DD/MM/YYYY; US exports use MM/DD/YYYY; native Date.parse fields work too.
      const toIsoDate = (raw) => {
        if (!raw) return "";
        const s = String(raw).trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
        // Match MM/DD/YYYY, DD/MM/YYYY, MM-DD-YYYY (we'll guess US first since the user is in NY)
        const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
        if (m) {
          let [, a, b, y] = m;
          if (y.length === 2) y = (parseInt(y, 10) < 50 ? "20" : "19") + y;
          // Heuristic: if a>12 it must be a day, so DD/MM. Otherwise assume MM/DD (US).
          const month = parseInt(a, 10) > 12 ? b : a;
          const day = parseInt(a, 10) > 12 ? a : b;
          return `${y}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        }
        // Last resort: native parse → ISO YYYY-MM-DD.
        const d = new Date(s);
        if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
        return "";
      };

      // Map raw CSV rows → ExpenseIn payloads.
      const expenses = rows.map((r) => ({
        date: toIsoDate(r[cols.date]),
        category: (r[cols.category] || "Other").trim(),
        // Strip currency symbols, commas, spaces. Negative kept (refunds become negative entries).
        amount: parseFloat((r[cols.amount] || "0").replace(/[^0-9.\-]/g, "")) || 0,
        vendor: cols.vendor ? (r[cols.vendor] || "").trim() : "",
        notes: cols.notes ? (r[cols.notes] || "").trim() : "",
      }));

      const localBad = expenses
        .map((e, i) => ({ i, e, bad: !e.date || !e.category || !e.amount }))
        .filter((x) => x.bad);
      if (localBad.length === expenses.length) {
        toast.error("No valid rows in this CSV — every row is missing date/category/amount.");
        setImportReport({ inserted: 0, errors: [], localBad });
        return;
      }
      const goodExpenses = expenses.filter((_, i) => !localBad.find((b) => b.i === i));
      const { data } = await api.post(withLedger("/expenses/bulk", ledger), { expenses: goodExpenses });
      const totalErrors = (data.errors || []).length + localBad.length;
      setImportReport({ inserted: data.inserted, errors: data.errors, localBad });

      // Widen the date filter so the user actually SEES the rows they just
      // imported. If their CSV spans 2023-2024 but the filter was a 2-week
      // window, the rows would silently fall outside the view.
      if (goodExpenses.length > 0 && setDateFrom && setDateTo) {
        const dates = goodExpenses.map((e) => e.date).filter(Boolean).sort();
        const minDate = dates[0];
        const maxDate = dates[dates.length - 1];
        if (minDate && minDate < dateFrom) setDateFrom(minDate);
        if (maxDate && maxDate > dateTo) setDateTo(maxDate);
      }

      toast.success(`Imported ${data.inserted} expenses${totalErrors > 0 ? ` (${totalErrors} skipped — see report below)` : ""}.`);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message || "Import failed.");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className={`${isMgmt ? "management-section" : "space-y-4"}`} data-testid="finance-expenses">
      <div className={`${isMgmt ? "management-section-header" : "flex items-center justify-between gap-3 flex-wrap mobile-stack"}`}>
        <div>
          {isMgmt && <div className="flex flex-wrap items-center gap-3"><h1 className="management-title font-display" data-testid="management-expenses-heading">Expenses</h1><TutorialButton section="expenses" compact /></div>}
          <p className={isMgmt ? "hidden" : "text-sm text-muted-foreground"} data-testid="expense-search-summary">{visibleList.length} entries · total <span className="font-semibold text-foreground mono-num">{fmtMoney(visibleList.reduce((s, e) => s + Number(e.amount || 0), 0))}</span></p>
        </div>
        <div className="flex gap-2 flex-wrap mobile-full">
          {/* Invoice status filter kept visible in the toolbar (not tucked
              into the kebab menu) so admins can flip it without opening a menu. */}
          {isMgmt && (
            <Select value={invoicedFilter} onValueChange={changeInvoicedFilter}>
              <SelectTrigger className="h-11 sm:h-9 w-full sm:w-44 rounded-sm" data-testid="expense-invoiced-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="expense-invoiced-filter-all">All expenses</SelectItem>
                <SelectItem value="uninvoiced" data-testid="expense-invoiced-filter-uninvoiced">Not invoiced yet</SelectItem>
                <SelectItem value="invoiced" data-testid="expense-invoiced-filter-invoiced">Already invoiced</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-sm flex-1 sm:flex-none" data-testid="expense-add">
            <Plus className="w-4 h-4 mr-2" /> Add expense
          </Button>
          {/* Tools tucked into a kebab menu so the toolbar stays clean. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="rounded-sm" title="More tools" data-testid="expense-tools-menu">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger data-testid="expense-filter-menu">
                  <Receipt className="w-4 h-4" /> Filter
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-64" data-testid="expense-filter-panel">
                  <DropdownMenuLabel>Filter expenses</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => { setColonyFilter("all"); setSubmitterFilter("all"); }}
                    disabled={colonyFilter === "all" && submitterFilter === "all"}
                    data-testid="expense-filter-clear"
                  >
                    <FilterX className="w-4 h-4 mr-2" /> Clear filters
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {isMgmt && colonyList.length > 0 && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger data-testid="expense-colony-filter">
                        Colony: {colonyFilter === "all" ? "All" : colonyFilter === "__none__" ? "General" : colonyMap[colonyFilter] || "Selected"}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-60" data-testid="expense-colony-filter-options">
                        <DropdownMenuLabel>Colony</DropdownMenuLabel>
                        <DropdownMenuRadioGroup value={colonyFilter} onValueChange={setColonyFilter}>
                          <DropdownMenuRadioItem value="all" data-testid="expense-colony-filter-all">All colonies</DropdownMenuRadioItem>
                          <DropdownMenuRadioItem value="__none__" data-testid="expense-colony-filter-general">General / no colony</DropdownMenuRadioItem>
                          {colonyList.map((c) => (
                            <DropdownMenuRadioItem key={c.id} value={c.id} data-testid={`expense-colony-filter-${c.id}`}>
                              <ColonyDotName id={c.id} name={c.name} />
                            </DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                  {submitterOptions.length > 0 && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger data-testid="expense-submitter-filter">
                        Submitted by: {submitterFilter === "all" ? "All" : submitterOptions.find((s) => s.id === submitterFilter)?.name || "Selected"}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-60" data-testid="expense-submitter-filter-options">
                        <DropdownMenuLabel>Submitted by</DropdownMenuLabel>
                        <DropdownMenuRadioGroup value={submitterFilter} onValueChange={setSubmitterFilter}>
                          <DropdownMenuRadioItem value="all" data-testid="expense-submitter-filter-all">All people</DropdownMenuRadioItem>
                          {submitterOptions.map((s) => (
                            <DropdownMenuRadioItem key={s.id} value={s.id} data-testid={`expense-submitter-filter-${s.id}`}>{s.name}</DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={exportCsv} data-testid="expense-export">
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => fileInputRef.current?.click()} data-testid="expense-import">
                <Upload className="w-4 h-4 mr-2" /> Import CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openHistory} data-testid="expense-restore-history">
                <History className="w-4 h-4 mr-2" /> Restore history
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openRollback} data-testid="expense-rollback">
                <Undo2 className="w-4 h-4 mr-2" /> Roll back to date
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openDupePreview} disabled={dupeBusy} data-testid="expense-dedupe">
                <CopyMinus className="w-4 h-4 mr-2" /> {dupeBusy ? "Checking…" : "Remove duplicates"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => onImportFile(e.target.files?.[0])}
            data-testid="expense-import-input"
          />
        </div>
      </div>

      {isMgmt && hasData && <FinanceSummaryCards testId="expense-summary-cards" items={[
        { key: "count", label: "Expense entries", value: visibleList.length, icon: Receipt },
        { key: "total", label: "Total expenses", value: fmtMoney(visibleList.reduce((sum, expense) => sum + Number(expense.amount || 0), 0)), icon: Wallet, tone: "rose" },
      ]} />}
      {listError && <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive" data-testid="expense-list-error"><span>{listError}</span><Button variant="outline" size="sm" onClick={() => load()} data-testid="expense-list-retry">Retry</Button></div>}
      {loading && <p role="status" className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="expense-list-loading"><Loader2 className="h-3.5 w-3.5 animate-spin" />{hasData ? "Refreshing expenses…" : "Loading expenses…"}</p>}
      <ExpandingFinanceSearch
        alwaysOpen={isMgmt}
        value={expenseSearch}
        onChange={setExpenseSearch}
        placeholder="Search expenses by vendor, category, notes, colony, or amount"
        testId="expense-search"
        ariaLabel="Search expenses"
      />

      {/* Brief help line so the user knows what columns to put in their CSV. */}
      {!isMgmt && <p className="text-xs text-muted-foreground">
        Drop in any expense CSV — including raw exports from <strong>Zoho Books</strong>, <strong>QuickBooks</strong>, <strong>Wave</strong>, or Excel. We auto-detect columns like <span className="font-mono">Date / Expense Date</span>, <span className="font-mono">Category / Expense Account</span>, <span className="font-mono">Amount / Total</span>, plus Vendor &amp; Notes.
      </p>}

      {/* Bulk selection toolbar — only shown when 1+ rows are checked. Sticks
          above the table for a moment while the admin reviews their choice. */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 border border-primary/50 bg-primary/5 rounded-sm px-4 py-2 mobile-stack" data-testid="expense-bulk-toolbar">
          <div className="text-sm font-semibold">
            {selected.size} selected · <span className="font-mono">{fmtMoney(sortedList.filter((e) => selected.has(e.id)).reduce((s, e) => s + Number(e.amount || 0), 0))}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap mobile-full">
            {isMgmt && (
              <>
                <Select value={bulkColony || "__pick__"} onValueChange={(v) => setBulkColony(v === "__pick__" ? "" : v)}>
                  <SelectTrigger className="h-11 sm:h-8 w-full sm:w-48 rounded-sm text-xs" data-testid="expense-bulk-colony-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__pick__">Assign to colony…</SelectItem>
                    <SelectItem value="__none__">— unassigned overhead —</SelectItem>
                    {colonyList.map((c) => <SelectItem key={c.id} value={c.id}><ColonyDotName id={c.id} name={c.name} /></SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={bulkAssignColony} disabled={!bulkColony} className="rounded-sm" data-testid="expense-bulk-assign">
                  Assign
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} className="rounded-sm" data-testid="expense-bulk-clear">Clear</Button>
            <Button variant="outline" size="sm" onClick={bulkDelete} className="rounded-sm text-destructive hover:text-destructive" data-testid="expense-bulk-delete">
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete selected
            </Button>
          </div>
        </div>
      )}

      <div className="management-list management-expense-list border border-border rounded-sm overflow-hidden admin-table-scroll" data-testid="expense-list-surface">
        <div className="admin-responsive-header grid grid-cols-12 px-4 py-2 text-xs font-mono uppercase tracking-wider text-muted-foreground border-b border-border bg-secondary/40 items-center">
          <div className="col-span-1 flex items-center">
            <Checkbox
              checked={allSelected}
              onCheckedChange={toggleAll}
              aria-label="Select all expenses"
              data-testid="expense-select-all"
            />
          </div>
          <div className="col-span-2">
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="date" onToggle={toggleSort} testId="sort-date">Date</SortableHeader>
          </div>
          <div className="col-span-2">
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="category" onToggle={toggleSort} testId="sort-category">Category</SortableHeader>
          </div>
          {/* Mgmt ledger: the Colony an expense is billed to matters more than
              the vendor — vendor moved into the click-to-open details view. */}
          {isMgmt ? (
            <div className="col-span-2">
              <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="colony" onToggle={toggleSort} testId="sort-colony">Colony</SortableHeader>
            </div>
          ) : (
            <div className="col-span-2">
              <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="vendor" onToggle={toggleSort} testId="sort-vendor">Vendor</SortableHeader>
            </div>
          )}
          <div className="col-span-2">
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="submitter" onToggle={toggleSort} testId="sort-submitter">Submitted by</SortableHeader>
          </div>
          <div className="col-span-1">
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="notes" onToggle={toggleSort} testId="sort-notes">Notes</SortableHeader>
          </div>
          <div className="col-span-1 flex justify-end">
            <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="amount" onToggle={toggleSort} testId="sort-amount">Amount</SortableHeader>
          </div>
          <div className="col-span-1 text-right">·</div>
        </div>
        {!loading && !listError && visibleList.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground break-words" data-testid="expense-empty-state">
            {expenseSearch.trim() ? `No expenses match “${expenseSearch.trim()}”.` : "No expenses in this period."}
          </div>
        )}
        {renderedList.map((e) => {
          // Backend sends `receipt_count` on the list payload so we don't
          // have to ship base64 receipt bytes just to draw the 📎 badge.
          // Fall back to counting local arrays for any freshly-inserted
          // row that hasn't been reloaded yet.
          const receiptCount = typeof e.receipt_count === "number"
            ? e.receipt_count
            : (e.receipts?.length || 0) + (e.receipt_data_url ? 1 : 0);
          const isSelected = selected.has(e.id);
          const isPayrollCharge = e.source_type === "payroll_colony_charge";
          return (
            <div
              key={e.id}
              className={`admin-responsive-row grid grid-cols-12 px-4 py-3 items-center border-b border-border last:border-b-0 ${e.amount_mismatch ? "border-l-4 bg-red-50/60" : ""} ${isSelected ? "bg-primary/5" : "hover:bg-secondary/30"}`}
              style={e.amount_mismatch ? { borderLeftColor: "#dc2626" } : undefined}
              data-testid={`expense-row-${e.id}`}
            >
              <div className="col-span-1 flex items-center" data-mobile-label="Select">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleOne(e.id)}
                  disabled={isPayrollCharge}
                  aria-label={`Select expense ${e.id}`}
                  data-testid={`expense-select-${e.id}`}
                />
              </div>
              <div className="col-span-2 text-sm cursor-pointer" data-mobile-label="Date" data-testid={`expense-date-${e.id}`} onClick={() => openDetails(e)}>{e.date}</div>
              <div className="col-span-2 text-sm cursor-pointer" data-mobile-label="Category" data-testid={`expense-category-${e.id}`} onClick={() => openDetails(e)}>
                {e.category}
                {isPayrollCharge && <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 font-semibold uppercase tracking-wider" data-testid={`expense-payroll-linked-${e.id}`}>Linked</span>}
                {(e.ai_correction_count ?? e.ai_audit?.corrections?.length ?? 0) > 0 && (
                  <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded-sm bg-violet-100 text-violet-800 font-semibold uppercase tracking-wider" title="AI corrected this expense from the receipt" data-testid={`expense-ai-fixed-${e.id}`}>AI fixed</span>
                )}
                <ReceiptFollowupBadge expense={e} testId={`expense-needs-details-${e.id}`} />
                <AmountMismatchBadge expense={e} testId={`expense-amount-flag-${e.id}`} />
              </div>
              {isMgmt ? (
                <div className="col-span-2 text-sm truncate cursor-pointer flex items-center gap-1.5" data-mobile-label="Colony" data-mobile-span="full" onClick={() => openDetails(e)} data-testid={`expense-colony-${e.id}`}>
                  {e.colony_id
                    ? <ColonyDotName id={e.colony_id} name={colonyMap[e.colony_id] || "Colony"} compact />
                    : <span className="text-muted-foreground">—</span>}
                  {e.invoiced && <span className="text-[9px] px-1 py-0.5 rounded-sm bg-sky-100 text-sky-800 font-semibold uppercase tracking-wider flex-shrink-0" data-testid={`expense-invoiced-${e.id}`}>Inv</span>}
                </div>
              ) : (
                <div className="col-span-2 text-sm text-muted-foreground truncate flex items-center gap-1.5 cursor-pointer" data-mobile-label="Vendor" onClick={() => openDetails(e)}>
                  <span className="truncate">{e.vendor || "—"}</span>
                  {e.payment_method && (
                    <span
                      title={e.payment_reference ? `${e.payment_method} · ${e.payment_method.toLowerCase().includes("check") ? "#" : "••••"}${e.payment_reference}` : e.payment_method}
                      className="inline-flex items-center text-[10px] text-foreground bg-secondary px-1.5 py-0.5 rounded-sm font-semibold uppercase tracking-wider flex-shrink-0"
                      data-testid={`expense-payment-${e.id}`}
                    >
                      {e.payment_method.toLowerCase().includes("credit") || e.payment_method.toLowerCase().includes("debit")
                        ? `••${e.payment_reference || ""}`
                        : e.payment_method.toLowerCase().includes("check")
                          ? `#${e.payment_reference || "—"}`
                          : e.payment_method}
                    </span>
                  )}
                </div>
              )}
              <div className="col-span-2 text-sm truncate flex items-center gap-1.5 cursor-pointer" data-mobile-label="Submitted" data-testid={`expense-submitted-${e.id}`} title={e.created_by || ""} onClick={() => openDetails(e)}>
                <span className="truncate">{e.submitter_name || e.created_by || "—"}</span>
                {/* Only tag as Driver if the submitter's CURRENT role is
                    driver AND we're on the pool ledger. Management has
                    no drivers, and stale flags from deleted/rehired
                    accounts must not linger. */}
                {!isMgmt && e.submitter_role === "driver" && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-sm font-semibold flex-shrink-0" data-testid={`expense-submitter-driver-${e.id}`}>Driver</span>
                )}
              </div>
              <div className="col-span-1 text-sm text-muted-foreground truncate flex items-center gap-2" data-mobile-label="Notes" data-mobile-span="full">
                <span className="truncate cursor-pointer" data-testid={`expense-notes-${e.id}`} onClick={() => openDetails(e)}>{e.notes || "—"}</span>
                {receiptCount > 0
                  ? (
                    <button
                      type="button"
                      onClick={() => openReceipts(e)}
                      title={`View ${receiptCount} receipt${receiptCount > 1 ? "s" : ""}`}
                      className="inline-flex items-center gap-0.5 text-[10px] text-primary font-semibold uppercase tracking-wider flex-shrink-0 hover:underline cursor-pointer"
                      data-testid={`expense-receipt-badge-${e.id}`}
                    >📎 {receiptCount}</button>
                  )
                  : e.no_receipt_reason
                    ? <span title={e.no_receipt_reason} className="inline-flex items-center text-[10px] text-amber-700 font-semibold uppercase tracking-wider flex-shrink-0" data-testid={`expense-noreceipt-${e.id}`}>⚠ no receipt</span>
                    : null}
              </div>
              <div className={`col-span-1 text-right mono-num font-semibold cursor-pointer ${Number(e.amount) < 0 ? "text-emerald-700" : ""}`} data-mobile-label="Amount" data-testid={`expense-amount-${e.id}`} onClick={() => openDetails(e)}>{fmtMoney(e.amount)}</div>
              <div className="col-span-1 text-right flex justify-end gap-1 mobile-card-actions" data-mobile-label="Actions">
                {isPayrollCharge ? (
                  <span className="text-[10px] text-muted-foreground" title="Edit this charge from its payroll record" data-testid={`expense-payroll-managed-${e.id}`}>Managed in Payroll</span>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(e)} className="rounded-sm" data-testid={`expense-edit-${e.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(e)} className="rounded-sm text-destructive" data-testid={`expense-delete-${e.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {isMgmt && hasData && <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground" data-testid="expense-pagination"><span data-testid="expense-visible-count">Showing {renderedList.length} of {visibleList.length} expenses</span>{renderedList.length < visibleList.length && <Button variant="outline" size="sm" onClick={() => setRowLimit(value => value + 50)} data-testid="expense-load-more">Show 50 more</Button>}</div>}

      <ExpenseDialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}
        editing={editing}
        ledger={ledger}
        onSaved={() => { setOpen(false); setEditing(null); load(); }}
      />

      {/* Click a 📎 receipt badge → fullscreen viewer with download. */}
      {receiptView && (
        <PhotoLightbox photos={receiptView} alt="receipt" onClose={() => setReceiptView(null)} />
      )}

      {/* Click anywhere on a row → full expense details. */}
      <ExpenseDetailsDialog
        expense={details}
        onOpenChange={(v) => { if (!v) setDetails(null); }}
        isMgmt={isMgmt}
        colonyMap={colonyMap}
        onViewReceipts={(imgs) => setReceiptView(imgs)}
        onEdit={(exp) => openEdit(exp)}
        onRefresh={async (exp) => {
          try {
            const { data } = await api.get(`/expenses/${exp.id}`);
            setDetails((d) => (d && d.id === exp.id ? { ...d, ...data } : d));
          } catch (_) {}
          load();
        }}
      />

      {/* Remove-duplicates confirmation. Shows the groups Plaid + manual
          entries collide on so the admin can see exactly what will be wiped
          before clicking the destructive button. */}
      <Dialog open={!!dupePreview} onOpenChange={(v) => { if (!v) setDupePreview(null); }}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dedupe-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Remove duplicate expenses</DialogTitle>
            <DialogDescription>
              {dupePreview && (
                <>Found <strong className="text-foreground">{dupePreview.total_groups}</strong> duplicate group{dupePreview.total_groups === 1 ? "" : "s"} — we&apos;ll keep the oldest entry in each and delete <strong className="text-destructive">{dupePreview.total_to_delete}</strong> extra row{dupePreview.total_to_delete === 1 ? "" : "s"}. Two expenses count as duplicates only when their <em>date, category, vendor, submitter, notes, and amount</em> all match exactly.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            {dupePreview?.groups?.slice(0, 50).map((g, i) => (
              <div key={i} className="border border-border rounded-sm p-3 flex items-start justify-between gap-3" data-testid={`dedupe-group-${i}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs text-muted-foreground">{g.date} · {g.category} · {g.submitted_by || "—"}</div>
                  <div className="font-semibold truncate">{g.vendor || "(no vendor)"}{g.notes ? ` — ${g.notes}` : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold mono-num">{fmtMoney(g.amount)}</div>
                  <div className="text-[10px] text-destructive font-mono uppercase tracking-wider">×{g.count} → remove {g.count - 1}</div>
                </div>
              </div>
            ))}
            {dupePreview?.groups?.length > 50 && (
              <p className="text-xs text-muted-foreground italic">…and {dupePreview.groups.length - 50} more groups not shown here.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupePreview(null)} disabled={dupeBusy} className="rounded-sm" data-testid="dedupe-cancel">Cancel</Button>
            <Button
              onClick={confirmDedupe}
              disabled={dupeBusy || !dupePreview?.total_to_delete}
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="dedupe-confirm"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {dupeBusy ? "Removing…" : `Remove ${dupePreview?.total_to_delete || 0} duplicate${dupePreview?.total_to_delete === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore history — rollback any past deletion batch (single delete,
          dedupe, bulk) OR every batch since a chosen date/time. */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="restore-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Restore deleted expenses</DialogTitle>
            <DialogDescription>
              Every time an expense is deleted (manually, by dedupe, or by bulk), we save a full snapshot here so you can undo it. Restored expenses get their original ID back. Once a batch is restored, the entry disappears from this list.
            </DialogDescription>
          </DialogHeader>

          <div className="border border-border rounded-sm p-3 space-y-2 bg-secondary/30" data-testid="restore-since-block">
            <div className="text-xs font-semibold uppercase tracking-wider">Bulk roll-back by date</div>
            <p className="text-xs text-muted-foreground">Restore every deletion that happened on or after the chosen moment — perfect for &quot;undo the last hour of edits.&quot;</p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="datetime-local"
                value={restoreSince}
                onChange={(e) => setRestoreSince(e.target.value)}
                className="rounded-sm border border-border bg-background px-3 h-9 text-sm"
                data-testid="restore-since-date"
              />
              <Button onClick={restoreSinceDate} disabled={!restoreSince} variant="outline" className="rounded-sm" data-testid="restore-since-go">
                <Undo2 className="w-4 h-4 mr-2" /> Restore everything since
              </Button>
            </div>
          </div>

          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-2">Individual deletion batches</div>
          <div className="space-y-2">
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No deletion history yet. Anything you delete from now on can be rolled back from here.</p>
            ) : (
              history.map((b) => (
                <div key={b.id} className="border border-border rounded-sm p-3 flex items-start justify-between gap-3" data-testid={`restore-batch-${b.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">
                      {b.kind === "dedupe" ? "Duplicate cleanup" : b.kind === "bulk" ? "Bulk deletion" : "Single deletion"}
                      <span className="text-muted-foreground font-normal"> · {b.count} expense{b.count === 1 ? "" : "s"}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground font-mono">
                      {new Date(b.deleted_at).toLocaleString()} · by {b.deleted_by || "—"}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => restoreBatch(b)} className="rounded-sm shrink-0" data-testid={`restore-batch-go-${b.id}`}>
                    <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Restore
                  </Button>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)} className="rounded-sm">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Roll back to date — restore the entire ledger to a saved daily
          snapshot, undoing additions, edits, and deletions since that day. */}
      <Dialog open={rollbackOpen} onOpenChange={(v) => { setRollbackOpen(v); if (!v) setRollbackPreview(null); }}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rollback-dialog">
          {!rollbackPreview ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Roll back expenses to a past day</DialogTitle>
                <DialogDescription>
                  We automatically save a snapshot of your expense books the first time they&apos;re opened each day. Pick a day below to preview exactly what would change — nothing happens until you confirm on the next screen.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                {snapshots.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No snapshots yet. From today onward a daily snapshot is captured automatically.</p>
                ) : (
                  snapshots.map((s) => (
                    <div key={s.id} className="border border-border rounded-sm p-3 flex items-start justify-between gap-3" data-testid={`rollback-snap-${s.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold">
                          {s.date}
                          <span className="text-muted-foreground font-normal"> · {s.count} expense{s.count === 1 ? "" : "s"}</span>
                          {s.kind === "pre_restore" && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700 font-semibold">auto-save before rollback</span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground font-mono">
                          Saved {new Date(s.created_at).toLocaleString()}{s.note ? ` · ${s.note}` : ""}
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => previewRollback(s)} disabled={rollbackBusy} className="rounded-sm shrink-0" data-testid={`rollback-go-${s.id}`}>
                        <Undo2 className="w-3.5 h-3.5 mr-1.5" /> {rollbackBusy ? "Loading…" : "Preview"}
                      </Button>
                    </div>
                  ))
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRollbackOpen(false)} className="rounded-sm" data-testid="rollback-close">Close</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Rolling back to {rollbackPreview.diff.to_date}</DialogTitle>
                <DialogDescription>
                  Here&apos;s exactly what will change. A safety snapshot of the current state is saved first, so this rollback can itself be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm" data-testid="rollback-preview">
                {rollbackPreview.diff.will_remove.length === 0 && rollbackPreview.diff.will_restore.length === 0 && rollbackPreview.diff.will_revert.length === 0 ? (
                  <p className="text-muted-foreground italic border border-border rounded-sm p-3" data-testid="rollback-no-changes">The books already match this snapshot exactly — rolling back would change nothing.</p>
                ) : (
                  <>
                    {rollbackPreview.diff.will_remove.length > 0 && (
                      <div className="border border-destructive/40 rounded-sm p-3 space-y-1.5" data-testid="rollback-will-remove">
                        <div className="text-xs font-semibold uppercase tracking-wider text-destructive">Will be removed — {rollbackPreview.diff.will_remove.length} added since the snapshot</div>
                        {rollbackPreview.diff.will_remove.slice(0, 30).map((e) => (
                          <div key={e.id} className="flex items-center justify-between gap-2 text-xs">
                            <span className="truncate font-mono text-muted-foreground">{e.date} · {e.category}{e.vendor ? ` · ${e.vendor}` : ""}{e.notes ? ` — ${e.notes}` : ""}</span>
                            <span className="mono-num font-semibold shrink-0">{fmtMoney(e.amount)}</span>
                          </div>
                        ))}
                        {rollbackPreview.diff.will_remove.length > 30 && <p className="text-[10px] text-muted-foreground italic">…and {rollbackPreview.diff.will_remove.length - 30} more</p>}
                      </div>
                    )}
                    {rollbackPreview.diff.will_restore.length > 0 && (
                      <div className="border border-emerald-600/40 rounded-sm p-3 space-y-1.5" data-testid="rollback-will-restore">
                        <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Will come back — {rollbackPreview.diff.will_restore.length} deleted since the snapshot</div>
                        {rollbackPreview.diff.will_restore.slice(0, 30).map((e) => (
                          <div key={e.id} className="flex items-center justify-between gap-2 text-xs">
                            <span className="truncate font-mono text-muted-foreground">{e.date} · {e.category}{e.vendor ? ` · ${e.vendor}` : ""}{e.notes ? ` — ${e.notes}` : ""}</span>
                            <span className="mono-num font-semibold shrink-0">{fmtMoney(e.amount)}</span>
                          </div>
                        ))}
                        {rollbackPreview.diff.will_restore.length > 30 && <p className="text-[10px] text-muted-foreground italic">…and {rollbackPreview.diff.will_restore.length - 30} more</p>}
                      </div>
                    )}
                    {rollbackPreview.diff.will_revert.length > 0 && (
                      <div className="border border-amber-600/40 rounded-sm p-3 space-y-2" data-testid="rollback-will-revert">
                        <div className="text-xs font-semibold uppercase tracking-wider text-amber-700">Edits undone — {rollbackPreview.diff.will_revert.length} changed since the snapshot</div>
                        {rollbackPreview.diff.will_revert.slice(0, 20).map((r) => (
                          <div key={r.now.id} className="text-xs space-y-0.5">
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate font-mono text-muted-foreground line-through">{r.now.date} · {r.now.category}{r.now.vendor ? ` · ${r.now.vendor}` : ""}</span>
                              <span className="mono-num line-through text-muted-foreground shrink-0">{fmtMoney(r.now.amount)}</span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate font-mono">→ {r.then.date} · {r.then.category}{r.then.vendor ? ` · ${r.then.vendor}` : ""}</span>
                              <span className="mono-num font-semibold shrink-0">{fmtMoney(r.then.amount)}</span>
                            </div>
                          </div>
                        ))}
                        {rollbackPreview.diff.will_revert.length > 20 && <p className="text-[10px] text-muted-foreground italic">…and {rollbackPreview.diff.will_revert.length - 20} more</p>}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">{rollbackPreview.diff.unchanged} expense{rollbackPreview.diff.unchanged === 1 ? "" : "s"} stay exactly as they are.</p>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRollbackPreview(null)} disabled={rollbackBusy} className="rounded-sm" data-testid="rollback-back">Back</Button>
                <Button
                  onClick={() => doRollback(rollbackPreview.snap)}
                  disabled={rollbackBusy || (rollbackPreview.diff.will_remove.length === 0 && rollbackPreview.diff.will_restore.length === 0 && rollbackPreview.diff.will_revert.length === 0)}
                  className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  data-testid="rollback-confirm"
                >
                  <Undo2 className="w-4 h-4 mr-2" /> {rollbackBusy ? "Rolling back…" : `Confirm rollback to ${rollbackPreview.diff.to_date}`}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Import report — collapses the per-row issues so the admin knows
          which CSV lines need fixing without flooding the screen. */}
      {importReport && (
        <div className="border border-border rounded-sm p-4 text-sm bg-secondary/30" data-testid="expense-import-report">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold">Last import: {importReport.inserted} added{(importReport.errors?.length || importReport.localBad?.length)
              ? `, ${(importReport.errors?.length || 0) + (importReport.localBad?.length || 0)} skipped` : ""}</p>
            <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setImportReport(null)} data-testid="dismiss-import-report">Dismiss</button>
          </div>
          {(importReport.localBad?.length > 0) && (
            <div className="mb-2">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Skipped on client (bad date / category / amount):</p>
              <ul className="text-xs space-y-0.5 max-h-32 overflow-auto">
                {importReport.localBad.slice(0, 10).map((b) => (
                  <li key={`local-${b.i}`} className="font-mono">Row {b.i + 2}: {JSON.stringify(b.e)}</li>
                ))}
                {importReport.localBad.length > 10 && <li className="text-muted-foreground">…and {importReport.localBad.length - 10} more</li>}
              </ul>
            </div>
          )}
          {(importReport.errors?.length > 0) && (
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Server validation errors:</p>
              <ul className="text-xs space-y-0.5 max-h-32 overflow-auto">
                {importReport.errors.slice(0, 10).map((e, i) => (
                  <li key={`srv-${i}`} className="font-mono">Row {e.row}: {e.error}</li>
                ))}
                {importReport.errors.length > 10 && <li className="text-muted-foreground">…and {importReport.errors.length - 10} more</li>}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExpenseDialog({ open, onOpenChange, editing, onSaved, ledger = "pool" }) {
  const receiptRequired = ledger !== "pool";
  const [form, setForm] = useState({ date: todayISO(), category: "Fuel", amount: 0, vendor: "", notes: "", receipts: [], no_receipt_reason: "", payment_method: "", payment_reference: "", colony_id: "" });
  const [noReceipt, setNoReceipt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [colonies, setColonies] = useState([]);
  const [vendorOptions, setVendorOptions] = useState(DEFAULT_VENDOR_OPTIONS);
  const [categoryOptions, setCategoryOptions] = useState(EXPENSE_CATEGORIES);
  // setState is asynchronous, so on a fast mobile tap React doesn't disable
  // the button before the next tap fires. Without this ref, a 3-finger
  // accidental tap on the Save button can POST the same expense 3 times.
  const savingRef = React.useRef(false);

  // Load bungalow colonies once per open on the mgmt page — they drive the
  // optional "Bill to" picker below. Pool ledger doesn't have colonies.
  useEffect(() => {
    if (open && ledger === "mgmt_usa_nj") {
      api.get("/mgmt/colonies").then((r) => setColonies(r.data || [])).catch(() => {});
    }
  }, [open, ledger]);

  // Load the shared vendor/category picklists (presets + anything an admin
  // added on THIS ledger — separate admin businesses never share these).
  useEffect(() => {
    if (open) {
      fetchVendorOptions(api, ledger).then(setVendorOptions);
      fetchCategoryOptions(api, ledger).then(setCategoryOptions);
    }
  }, [open, ledger]);

  useEffect(() => {
    if (open) {
      const base = editing
        ? {
            date: editing.date,
            category: editing.category,
            amount: editing.amount,
            vendor: editing.vendor || "",
            notes: editing.notes || "",
            // Merge legacy single field into the new multi-receipts list so old
            // records still display their attached file when re-opened.
            receipts: [
              ...(Array.isArray(editing.receipts) ? editing.receipts : []),
              ...(editing.receipt_data_url ? [editing.receipt_data_url] : []),
            ],
            item_photos: editing.item_photos || [],
            no_receipt_reason: editing.no_receipt_reason || "",
            payment_method: editing.payment_method || "",
            payment_reference: editing.payment_reference || "",
            colony_id: editing.colony_id || "",
          }
        : { date: todayISO(), category: receiptRequired ? "" : "Fuel", amount: 0, vendor: "", notes: "", receipts: [], no_receipt_reason: "", payment_method: "", payment_reference: "", colony_id: "" };
      setForm(base);
      setNoReceipt(receiptRequired && !!base.no_receipt_reason && base.receipts.length === 0);
      savingRef.current = false;
    }
  }, [open, editing, receiptRequired]);

  // Payment-method UX: methods that have an identifying number get an
  // extra input. Card numbers are clamped to 4 digits (we NEVER store
  // a full PAN), check numbers up to 10 digits.
  const refSpec = useMemo(() => {
    const m = (form.payment_method || "").toLowerCase();
    if (m.includes("credit") || m.includes("debit")) {
      return { show: true, label: "Last 4 of card", placeholder: "1234", maxLen: 4, digitsOnly: true, testId: "expense-payment-card-last4" };
    }
    if (m.includes("check")) {
      return { show: true, label: "Check #", placeholder: "1042", maxLen: 10, digitsOnly: true, testId: "expense-payment-check-number" };
    }
    if (m.includes("zelle")) {
      return { show: true, label: "Zelle transaction # (optional)", placeholder: "e.g. ABC123", maxLen: 32, testId: "expense-payment-zelle-ref" };
    }
    return { show: false };
  }, [form.payment_method]);

  const onPaymentMethodChange = (v) => {
    // Wipe the reference whenever the payment method category changes — a
    // check number doesn't belong on a credit-card row and vice versa.
    setForm((f) => ({ ...f, payment_method: v, payment_reference: "" }));
  };

  const onPaymentRefChange = (raw) => {
    let v = raw;
    if (refSpec.digitsOnly) v = v.replace(/\D/g, "");
    if (refSpec.maxLen) v = v.slice(0, refSpec.maxLen);
    setForm((f) => ({ ...f, payment_reference: v }));
  };

  const save = async () => {
    // Hard guard against double-fire from rapid taps before React re-renders.
    if (savingRef.current) return;
    if (!Number(form.amount)) return toast.error("Enter an amount (use a minus for returns, e.g. -25).");
    if (receiptRequired && noReceipt) {
      if (!form.no_receipt_reason.trim()) return toast.error("Please explain why no receipt is available.");
    } else if (receiptRequired && form.receipts.length === 0) {
      return toast.error("Attach at least one receipt — or tick 'No receipt available' below.");
    }
    const detailsFromReceipt = receiptRequired && !noReceipt;
    if (!detailsFromReceipt && !form.vendor.trim()) return toast.error("Vendor / store is required.");
    if (receiptRequired && !form.category.trim()) return toast.error("Category is required.");
    // Sanity-check the payment reference if a method was picked.
    if (refSpec.show && form.payment_reference) {
      if (refSpec.maxLen === 4 && form.payment_reference.length !== 4) {
        return toast.error("Card last-4 must be exactly 4 digits.");
      }
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        category: form.category,
        amount: Number(form.amount),
        vendor: form.vendor,
        notes: form.notes,
        receipts: receiptRequired && noReceipt ? [] : form.receipts,
        item_photos: form.item_photos || [],
        no_receipt_reason: receiptRequired
          ? (noReceipt ? form.no_receipt_reason.trim() : "")
          : (form.no_receipt_reason || "").trim(),
        payment_method: form.payment_method || "",
        // Only persist a reference for methods that take one.
        payment_reference: refSpec.show ? (form.payment_reference || "") : "",
        // Clear the legacy single field on update so it doesn't double-display.
        receipt_data_url: "",
        // Pin the row to whichever ledger opened the dialog.
        ledger,
        // Only mgmt ledger uses colony billing; keep null on pool.
        colony_id: ledger === "mgmt_usa_nj" ? (form.colony_id || null) : null,
      };
      if (editing) await api.put(`/expenses/${editing.id}`, payload);
      else await api.post(`/expenses`, payload);
      toast.success(detailsFromReceipt && !form.vendor.trim() ? "Saved — reading the receipt now" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); savingRef.current = false; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="expense-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit expense" : "Add expense"}</DialogTitle>
          <DialogDescription>{receiptRequired ? "Every expense needs proof — attach a receipt (image or PDF) or note why one isn't available." : "Receipt photos are optional for Catskills Pool Supplies admin expenses. Attach one whenever you have it."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {receiptRequired
              ? <div><Label>Category *</Label><Combobox value={form.category} onChange={category => setForm(current => ({ ...current, category }))} options={categoryOptions} placeholder="Pick or type a category…" testId="expense-category" /></div>
              : <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date" /></div>}
            <div><Label>Amount ($)</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Use minus for returns, e.g. -25" data-testid="expense-amount" /></div>
          </div>
          {receiptRequired && <>
            <div><Label>Details — what was this for?</Label><Textarea rows={2} value={form.notes} onChange={event => setForm(current => ({ ...current, notes: event.target.value }))} data-testid="expense-notes" /></div>
            <ExpenseReceiptFields key={editing?.id || "new"} form={form} setForm={setForm} receipts={form.receipts} onReceiptsChange={receipts => setForm(current => ({ ...current, receipts }))} noReceipt={noReceipt} onNoReceiptChange={setNoReceipt} vendorOptions={vendorOptions} showDetails={!!editing && (!editing.vendor || editing.receipt_followup?.status === "pending")} />
          </>}
          {!receiptRequired && <>
          <div>
            <Label>Category</Label>
            <Combobox
              value={form.category}
              onChange={(v) => setForm({ ...form, category: v })}
              options={categoryOptions}
              placeholder="Pick or type a category…"
              testId="expense-category"
            />
          </div>
          <div>
            <Label>Vendor *</Label>
            <Combobox
              value={form.vendor}
              onChange={(v) => setForm({ ...form, vendor: v })}
              options={vendorOptions}
              placeholder="Pick or type a vendor…"
              testId="expense-vendor"
            />
          </div>
          <div><Label>Notes (optional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="expense-notes" /></div>
          </>}

          {/* Payment method: how the bill was paid. Card / Check unlock an
              extra field for the last-4 or check number so the ledger
              matches the bank/credit-card statement at reconciliation time. */}
          <div className={refSpec.show ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : ""}>
            <div>
              <Label>Payment method (optional)</Label>
              <Select value={form.payment_method || "__none__"} onValueChange={(v) => onPaymentMethodChange(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="expense-payment-method"><SelectValue placeholder="Pick a method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— not specified —</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Check">Check</SelectItem>
                  <SelectItem value="Zelle">Zelle</SelectItem>
                  <SelectItem value="Credit card">Credit card</SelectItem>
                  <SelectItem value="Debit card">Debit card</SelectItem>
                  <SelectItem value="Bank transfer">Bank transfer / ACH</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {refSpec.show && (
              <div>
                <Label>{refSpec.label}</Label>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={refSpec.maxLen}
                  placeholder={refSpec.placeholder}
                  value={form.payment_reference}
                  onChange={(e) => onPaymentRefChange(e.target.value)}
                  data-testid={refSpec.testId}
                />
              </div>
            )}
          </div>

          {/* Bill to (Bungalow colony) — Management USA NJ ledger only. Optional. */}
          {ledger === "mgmt_usa_nj" && (
            <div>
              <Label>Bill to (bungalow colony)</Label>
              <Select value={form.colony_id || "__none__"} onValueChange={(v) => setForm({ ...form, colony_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="expense-colony"><SelectValue placeholder="— unassigned overhead —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— unassigned overhead —</SelectItem>
                  {colonies.map((c) => <SelectItem key={c.id} value={c.id}><ColonyDotName id={c.id} name={c.name} /></SelectItem>)}
                </SelectContent>
              </Select>
              {colonies.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">No colonies yet — add one on the Overview tab first.</p>
              )}
            </div>
          )}

          {/* Pool-admin receipts are optional. Management keeps the required
              receipt-or-reason flow for worker and manager accountability. */}
          {!receiptRequired && (!noReceipt ? (
            <div className="border border-border rounded-sm p-3 bg-secondary/20">
              <ReceiptUploader
                value={form.receipts}
                onChange={(v) => setForm({ ...form, receipts: v })}
                testId="expense-receipt"
                colonyId={form.colony_id || undefined}
                optional={!receiptRequired}
              />
            </div>
          ) : (
            <div>
              <Label>Reason no receipt is available <span className="text-destructive">*</span></Label>
              <Textarea
                rows={2}
                placeholder="e.g. paid in cash at gas pump, vendor didn't issue one, lost receipt during week of June 10…"
                value={form.no_receipt_reason}
                onChange={(e) => setForm({ ...form, no_receipt_reason: e.target.value })}
                data-testid="expense-no-receipt-reason"
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="expense-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="expense-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
function Payroll({ dateFrom, dateTo, setDateFrom, setDateTo, ledger, navigationRequest }) {
  const [list, setList] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payDialog, setPayDialog] = useState(null); // payroll entry currently receiving a partial payment
  const [workersOpen, setWorkersOpen] = useState(false); // Manage workers dialog (mgmt ledger only)
  const [details, setDetails] = useState(null);
  const [colonies, setColonies] = useState([]);
  const [payrollTemplates, setPayrollTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [payrollSection, setPayrollSection] = useState(() => ledger === "mgmt_usa_nj" ? "workers" : "records");
  const [payrollRefreshKey, setPayrollRefreshKey] = useState(0);
  const [workerDashboard, setWorkerDashboard] = useState(null);
  const [workerDashboardLoading, setWorkerDashboardLoading] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerEntries, setWorkerEntries] = useState([]);
  const [workerEntriesLoading, setWorkerEntriesLoading] = useState(false);
  const [newPayrollWorkerId, setNewPayrollWorkerId] = useState("");

  // Payroll worker source is per-ledger:
  //  - "pool" (existing pool-supply biz) → drivers + secretaries (people
  //    who already exist in the login system).
  //  - "mgmt_usa_nj" (separate books) → a name-only roster stored in the
  //    payroll_workers collection. Managed inline from the Payroll tab.
  const usesRoster = ledger === "mgmt_usa_nj";

  useEffect(() => {
    if (navigationRequest?.target === "payroll" && ["workers", "records", "billing", "templates"].includes(navigationRequest.section)) setPayrollSection(navigationRequest.section);
  }, [navigationRequest]);

  const load = useCallback(() => {
    api.get(withLedger(`/payroll?date_from=${dateFrom}&date_to=${dateTo}`, ledger))
      .then((r) => setList(r.data))
      .catch(() => {});
  }, [dateFrom, dateTo, ledger]);

  const loadWorkers = useCallback(() => {
    if (usesRoster) {
      api.get(withLedger("/payroll/workers", ledger))
        .then((r) => setWorkers((r.data || []).map((w) => ({ id: w.id, name: w.name, role: "worker", notes: w.notes || "", user_id: w.user_id || null, email: w.email || "", is_worker_manager: w.is_worker_manager === true }))))
        .catch(() => {});
    } else {
      Promise.all([api.get("/drivers"), api.get("/secretaries")])
        .then(([d, s]) => {
          const combined = [
            ...d.data.map((u) => ({ id: u.id, name: u.name, role: "driver" })),
            ...s.data.map((u) => ({ id: u.id, name: u.name, role: "secretary" })),
          ];
          setWorkers(combined);
        })
        .catch(() => {});
    }
  }, [usesRoster, ledger]);

  const loadColonies = useCallback(() => {
    if (!usesRoster) { setColonies([]); return; }
    api.get("/mgmt/colonies").then((response) => setColonies(response.data || [])).catch(() => setColonies([]));
  }, [usesRoster]);

  const loadTemplates = useCallback(() => {
    if (!usesRoster) { setPayrollTemplates([]); setTemplatesLoading(false); return; }
    setTemplatesLoading(true);
    api.get(withLedger("/payroll/templates", ledger))
      .then((response) => setPayrollTemplates(response.data || []))
      .catch(() => setPayrollTemplates([]))
      .finally(() => setTemplatesLoading(false));
  }, [usesRoster, ledger]);

  const loadWorkerDashboard = useCallback(() => {
    if (!usesRoster) { setWorkerDashboard(null); return; }
    setWorkerDashboardLoading(true);
    api.get(withLedger("/payroll/worker-dashboard", ledger))
      .then((response) => setWorkerDashboard(response.data || null))
      .catch((error) => toast.error(formatErr(error.response?.data?.detail) || error.message))
      .finally(() => setWorkerDashboardLoading(false));
  }, [usesRoster, ledger]);

  const loadWorkerEntries = useCallback(async (worker) => {
    if (!worker) return;
    setSelectedWorker(worker);
    setWorkerEntriesLoading(true);
    const params = new URLSearchParams();
    params.set("ledger", ledger);
    if (worker.source_worker_ids?.length) params.set("worker_user_ids", worker.source_worker_ids.join(","));
    else if (worker.worker_user_id) params.set("worker_user_id", worker.worker_user_id);
    if (worker.source_worker_names?.length) params.set("worker_names", worker.source_worker_names.join("|"));
    else if (!worker.worker_user_id) params.set("worker_name", worker.worker_name);
    try {
      const response = await api.get(`/payroll?${params.toString()}`);
      setWorkerEntries(response.data || []);
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally { setWorkerEntriesLoading(false); }
  }, [ledger]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadWorkers(); }, [loadWorkers]);
  useEffect(() => { loadColonies(); }, [loadColonies]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadWorkerDashboard(); }, [loadWorkerDashboard]);

  const colonyMap = useMemo(() => Object.fromEntries(colonies.map((colony) => [colony.id, colony.name])), [colonies]);

  const total = useMemo(() => list.reduce((s, p) => s + Number(p.gross_pay || 0), 0), [list]);
  const totalPaid = useMemo(() => list.reduce((s, p) => s + Number(p.amount_paid ?? (p.paid ? (p.gross_pay || 0) : 0)), 0), [list]);
  const totalOwed = Math.max(0, total - totalPaid);

  const remove = async (p) => {
    if (!window.confirm(`Delete payroll for ${p.worker_name} (${fmtMoney(p.gross_pay)})?`)) return;
    try {
      await api.delete(`/payroll/${p.id}`);
      toast.success("Deleted");
      load(); loadWorkerDashboard();
      if (selectedWorker) loadWorkerEntries(selectedWorker);
    }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className={usesRoster ? "management-section" : "space-y-4"} data-testid="finance-payroll">
      {usesRoster && <div className="flex flex-wrap items-center justify-between gap-3"><h1 className="management-title font-display" data-testid="management-payroll-heading">Payroll</h1><TutorialButton section="payroll" /></div>}
      {usesRoster && (
        <nav className="management-subtabs flex flex-wrap gap-x-5 border-b border-border" aria-label="Payroll sections" data-testid="payroll-section-tabs">
          {[["workers", "Worker balances"], ["records", "All payroll entries"], ["billing", "Payroll billing"], ["templates", "Worker templates"]].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setPayrollSection(value)}
              aria-current={payrollSection === value ? "page" : undefined}
              className={`h-10 shrink-0 border-b-2 text-sm font-semibold transition-colors ${payrollSection === value ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`payroll-section-${value}`}
            >
              {label}
            </button>
          ))}
        </nav>
      )}

      {usesRoster && payrollSection === "workers" ? (
        <PayrollWorkerDashboard
          dashboard={workerDashboard}
          loading={workerDashboardLoading}
          onOpenWorker={loadWorkerEntries}
          onAddPayroll={() => { setNewPayrollWorkerId(""); setEditing(null); setOpen(true); }}
          onManageWorkers={() => setWorkersOpen(true)}
        />
      ) : usesRoster && payrollSection === "billing" ? (
        <PayrollBillingSection dateFrom={dateFrom} dateTo={dateTo} ledger={ledger} refreshKey={payrollRefreshKey} />
      ) : usesRoster && payrollSection === "templates" ? (
        <PayrollTemplatesSection ledger={ledger} colonies={colonies} onChanged={() => { setPayrollRefreshKey((value) => value + 1); load(); loadTemplates(); }} />
      ) : (
      <>
      <div className={usesRoster ? "management-section-header" : "flex items-center justify-between flex-wrap gap-3 mobile-stack"}>
        <div>
        {usesRoster && <h1 className="management-title font-display" data-testid="payroll-records-heading">All payroll entries</h1>}
        <p className={usesRoster ? "hidden" : "text-sm text-muted-foreground"} data-testid="payroll-records-summary">
          {list.length} entries · gross <span className="font-semibold text-foreground mono-num">{fmtMoney(total)}</span>
          {" · "}paid <span className="font-semibold text-success mono-num">{fmtMoney(totalPaid)}</span>
          {totalOwed > 0 && <> · owed <span className="font-semibold text-destructive mono-num">{fmtMoney(totalOwed)}</span></>}
        </p>
        </div>
        <Button onClick={() => { setNewPayrollWorkerId(""); setEditing(null); setOpen(true); }} disabled={usesRoster && templatesLoading} className="rounded-sm mobile-full" data-testid="payroll-add">
          {usesRoster && templatesLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} {usesRoster && templatesLoading ? "Loading worker defaults…" : "Record payroll"}
        </Button>
      </div>

      {usesRoster && <FinanceSummaryCards testId="payroll-records-cards" items={[
        { key: "gross", label: "Gross recorded", value: fmtMoney(total), icon: Receipt, tone: "sky" },
        { key: "paid", label: "Total paid", value: fmtMoney(totalPaid), icon: Wallet, tone: "green" },
        { key: "owed", label: "Total owed", value: fmtMoney(totalOwed), icon: Coins, tone: "rose" },
        { key: "count", label: "Payroll entries", value: list.length, icon: Users },
      ]} />}

      {usesRoster && (
        <div className="flex items-center justify-between gap-3 border border-border rounded-sm px-3 py-2 bg-secondary/20 mobile-stack" data-testid="mgmt-workers-summary">
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{workers.length}</span> worker{workers.length === 1 ? "" : "s"} on the roster
          </p>
          <Button size="sm" variant="outline" className="rounded-sm mobile-full" onClick={() => setWorkersOpen(true)} data-testid="mgmt-workers-manage">
            <UserCog className="w-3.5 h-3.5 mr-1.5" /> Manage workers
          </Button>
        </div>
      )}

      <div className="management-list border border-border rounded-sm overflow-hidden admin-table-scroll" data-testid="payroll-records-list">
        <div className="admin-responsive-header grid grid-cols-12 px-4 py-2 text-xs font-mono uppercase tracking-wider text-muted-foreground border-b border-border bg-secondary/40">
          <div className="col-span-3">Worker</div>
          <div className="col-span-2">Period</div>
          <div className="col-span-1 text-right">Gross</div>
          <div className="col-span-1 text-right">Paid</div>
          <div className="col-span-1 text-right">Balance</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">·</div>
        </div>
        {list.length === 0 && <div className="px-4 py-10 text-center text-sm text-muted-foreground">No payroll entries in this period.</div>}
        {list.map((p) => {
          const gross = Number(p.gross_pay || 0);
          // amount_paid is authoritative once partial payments exist; fall back
          // to gross × paid flag for legacy rows that pre-date partial payments.
          const paid = Number(p.amount_paid ?? (p.paid ? gross : 0));
          const owed = Math.max(0, gross - paid);
          const fullyPaid = paid + 0.005 >= gross;
          const partiallyPaid = paid > 0 && !fullyPaid;
          const pct = gross > 0 ? Math.min(100, Math.round((paid / gross) * 100)) : 0;
          const paymentRows = p.payments || [];
          const latestPayment = paymentRows[paymentRows.length - 1];
          return (
            <div
              key={p.id}
              className="admin-responsive-row grid grid-cols-12 px-4 py-3 items-center border-b border-border last:border-b-0 hover:bg-secondary/30 cursor-pointer"
              onClick={(event) => {
                if (!event.target.closest("button, input, [role='button'], [role='checkbox'], [role='combobox']")) setDetails(p);
              }}
              data-testid={`payroll-row-${p.id}`}
            >
              <div className="col-span-3 text-sm font-medium" data-mobile-label="Worker" data-mobile-span="full">
                <div>{p.worker_name}</div>
                {p.colony_id && (
                  <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`payroll-colony-label-${p.id}`}>
                    Bill to {p.colony_name || colonyMap[p.colony_id] || "Colony"}
                    {p.billing_charge_id && ` · ${fmtMoney(p.colony_charge_total)} charge`}
                  </div>
                )}
              </div>
              <div className="col-span-2 text-sm text-muted-foreground" data-mobile-label="Period" data-mobile-span="full">{p.period_start} → {p.period_end}</div>
              <div className="col-span-1 text-right mono-num font-semibold" data-mobile-label="Gross">{fmtMoney(gross)}</div>
              <div className="col-span-1 text-right mono-num text-sm text-success" data-mobile-label="Paid">{fmtMoney(paid)}</div>
              <div className={`col-span-1 text-right mono-num text-sm ${owed > 0 ? "text-destructive font-semibold" : "text-muted-foreground"}`} data-mobile-label="Balance">{fmtMoney(owed)}</div>
              <div className="col-span-2 text-sm" data-mobile-label="Status">
                {fullyPaid ? (
                  <div>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs uppercase tracking-wider rounded-sm font-semibold" data-testid={`payroll-status-paid-${p.id}`}>Paid</span>
                    {(latestPayment || p.paid_at) && (
                      <div className="text-[10px] text-muted-foreground mt-1" data-testid={`payroll-paid-summary-${p.id}`}>
                        {latestPayment?.paid_at || String(p.paid_at).slice(0, 10)}{(latestPayment?.payment_method || p.payment_method) ? ` · ${latestPayment?.payment_method || p.payment_method}` : ""}
                      </div>
                    )}
                  </div>
                ) : partiallyPaid ? (
                  <div className="space-y-1" data-testid={`payroll-status-partial-${p.id}`}>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs uppercase tracking-wider rounded-sm font-semibold">{pct}% paid</span>
                    <div className="h-1 bg-secondary rounded-sm overflow-hidden">
                      <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                ) : (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs uppercase tracking-wider rounded-sm font-semibold" data-testid={`payroll-status-pending-${p.id}`}>Pending</span>
                )}
              </div>
              <div className="col-span-2 text-right flex justify-end gap-1 mobile-card-actions" data-mobile-label="Actions" data-mobile-span="full">
                {!fullyPaid && (
                  <Button type="button" size="sm" variant="outline" onClick={(event) => { event.stopPropagation(); setPayDialog(p); }} className="rounded-sm" title="Record a payment (full or partial)" data-testid={`payroll-pay-${p.id}`}>
                    <Wallet className="w-3.5 h-3.5 mr-1" /> Pay
                  </Button>
                )}
                <Button type="button" size="sm" variant="ghost" onClick={(event) => { event.stopPropagation(); setEditing(p); setOpen(true); }} className="rounded-sm" data-testid={`payroll-edit-${p.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={(event) => { event.stopPropagation(); remove(p); }} className="rounded-sm text-destructive" data-testid={`payroll-delete-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      </>
      )}

      <PayrollDialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setNewPayrollWorkerId(""); } }}
        editing={editing}
        workers={workers}
        templates={payrollTemplates}
        templatesLoading={templatesLoading}
        colonies={colonies}
        ledger={ledger}
        initialWorkerId={newPayrollWorkerId}
        onSaved={(saved) => {
          // Auto-extend the date window so the just-saved row is never
          // hidden by the current filter. Mirrors the CSV-import behavior
          // in Expenses. Without this, users repeatedly hit "record →
          // saved → row invisible" and re-enter, causing double payments.
          const ps = saved?.period_start;
          const pe = saved?.period_end;
          if (pe && pe > dateTo) setDateTo(pe);
          if (ps && ps < dateFrom) setDateFrom(ps);
          setOpen(false);
          setEditing(null);
          setNewPayrollWorkerId("");
          load(); loadWorkerDashboard();
        }}
      />

      <PayrollPaymentDialog
        entry={payDialog}
        onOpenChange={(v) => { if (!v) setPayDialog(null); }}
        onSaved={() => { setPayDialog(null); load(); loadWorkerDashboard(); }}
      />

      <PayrollDetailsDialog
        entry={details}
        colonyName={details?.colony_id ? (details.colony_name || colonyMap[details.colony_id]) : ""}
        onOpenChange={(value) => { if (!value) setDetails(null); }}
      />

      <PayrollWorkerDetailsDialog
        worker={selectedWorker}
        entries={workerEntries}
        loading={workerEntriesLoading}
        onOpenChange={(value) => { if (!value) setSelectedWorker(null); }}
        onAdd={() => { const workerId = selectedWorker?.worker_user_id || ""; setSelectedWorker(null); setNewPayrollWorkerId(workerId); setEditing(null); setOpen(true); }}
        onManage={() => { setSelectedWorker(null); setWorkersOpen(true); }}
        onView={(entry) => { setSelectedWorker(null); setDetails(entry); }}
        onPay={(entry) => { setSelectedWorker(null); setPayDialog(entry); }}
        onEdit={(entry) => { setSelectedWorker(null); setEditing(entry); setOpen(true); }}
        onDelete={remove}
      />

      {usesRoster && (
        <DirectoryManagerDialog
          open={workersOpen}
          onOpenChange={setWorkersOpen}
          initialTab="workers"
          ledger={ledger}
          onWorkersChanged={() => { loadWorkers(); loadTemplates(); loadWorkerDashboard(); }}
          onColoniesChanged={loadColonies}
        />
      )}
    </div>
  );
}

function payrollDefaultsForWorker(templates, workerId) {
  const template = (templates || []).find((row) => row.worker_user_id === workerId);
  const saved = template && Number(template.gross_pay || 0) > 0 && !String(template.id || "").startsWith("template-");
  if (!saved) return { hasTemplate: false, hours: "", rate: "", gross_pay: 0, notes: "", colony_id: "", markup_type: "none", markup_value: 0, billing_amount_mode: "markup", billing_amount: null };
  return {
    hasTemplate: true,
    hours: template.hours ?? "",
    rate: template.rate ?? "",
    gross_pay: Number(template.gross_pay || 0),
    notes: template.notes || "",
    colony_id: template.colony_id || "",
    markup_type: template.markup_type || "none",
    markup_value: Number(template.markup_value || 0),
    billing_amount_mode: template.billing_amount_mode || "markup",
    billing_amount: template.billing_amount ?? null,
  };
}

function PayrollDialog({ open, onOpenChange, editing, workers, templates, templatesLoading, colonies, onSaved, ledger = "pool", initialWorkerId = "" }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [templateLoadedFor, setTemplateLoadedFor] = useState("");
  const initializedRef = React.useRef(false);

  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      setTemplateLoadedFor("");
      return;
    }
    if (initializedRef.current || (!editing && ledger === "mgmt_usa_nj" && templatesLoading)) return;
    if (open) {
      const period = previousPayrollPeriod();
      const editPayments = editing?.payments || [];
      const latestPayment = editPayments[editPayments.length - 1];
      const workerId = editing?.worker_user_id || initialWorkerId || workers[0]?.id || templates?.[0]?.worker_user_id || "";
      const defaults = payrollDefaultsForWorker(templates, workerId);
      setForm(editing
        ? {
            worker_user_id: editing.worker_user_id,
            period_start: editing.period_start,
            period_end: editing.period_end,
            hours: editing.hours ?? "",
            rate: editing.rate ?? "",
            gross_pay: editing.gross_pay,
            notes: editing.notes || "",
            paid: !!editing.paid,
            paid_at: latestPayment?.paid_at || String(editing.paid_at || todayISO()).slice(0, 10),
            payment_method: latestPayment?.payment_method || editing.payment_method || "",
            payment_reference: latestPayment?.payment_reference || editing.payment_reference || "",
            colony_id: editing.colony_id || "",
            markup_type: editing.markup_type || "none",
            markup_value: editing.markup_value ?? 0,
            billing_amount_mode: editing.billing_amount_mode || "markup",
            billing_amount: editing.billing_amount ?? null,
          }
        : {
            worker_user_id: workerId,
            ...period,
            hours: defaults.hours,
            rate: defaults.rate,
            gross_pay: defaults.gross_pay,
            notes: defaults.notes,
            paid: false,
            paid_at: todayISO(),
            payment_method: "",
            payment_reference: "",
            colony_id: defaults.colony_id,
            markup_type: defaults.markup_type,
            markup_value: defaults.markup_value,
            billing_amount_mode: defaults.billing_amount_mode,
            billing_amount: defaults.billing_amount,
          });
      setTemplateLoadedFor(!editing && defaults.hasTemplate ? workerId : "");
      initializedRef.current = true;
    }
  }, [open, editing, workers, templates, templatesLoading, ledger, initialWorkerId]);

  const setHoursOrRate = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      const hours = parseFloat(next.hours);
      const rate = parseFloat(next.rate);
      if (!Number.isNaN(hours) && !Number.isNaN(rate)) next.gross_pay = Number((hours * rate).toFixed(2));
      return next;
    });
  };

  const selectWorker = (workerId) => {
    if (editing) {
      setForm((current) => ({ ...current, worker_user_id: workerId }));
      return;
    }
    const defaults = payrollDefaultsForWorker(templates, workerId);
    setForm((current) => ({
      ...current,
      worker_user_id: workerId,
      hours: defaults.hours,
      rate: defaults.rate,
      gross_pay: defaults.gross_pay,
      notes: defaults.notes,
      colony_id: defaults.colony_id,
      markup_type: defaults.markup_type,
      markup_value: defaults.markup_value,
      billing_amount_mode: defaults.billing_amount_mode,
      billing_amount: defaults.billing_amount,
    }));
    setTemplateLoadedFor(defaults.hasTemplate ? workerId : "");
  };

  const methodLower = (form.payment_method || "").toLowerCase();
  const payRefSpec = (() => {
    if (methodLower === "check") return { show: true, label: "Check #", placeholder: "1042", maxLen: 10, digitsOnly: true, testId: "payroll-payment-check-number" };
    if (methodLower === "zelle") return { show: true, label: "Zelle transaction #", placeholder: "ABC123XYZ", maxLen: 32, testId: "payroll-payment-zelle-reference" };
    if (methodLower === "ach") return { show: true, label: "ACH confirmation #", placeholder: "TRN12345", maxLen: 32, testId: "payroll-payment-ach-reference" };
    if (methodLower === "credit card") return { show: true, label: "Card last 4", placeholder: "1234", maxLen: 4, digitsOnly: true, testId: "payroll-payment-card-last4" };
    return { show: false };
  })();

  const gross = Number(form.gross_pay || 0);
  const markupValue = Math.max(0, Number(form.markup_value || 0));
  const markupAmount = form.markup_type === "percent"
    ? gross * markupValue / 100
    : form.markup_type === "fixed" ? markupValue : 0;
  const directBilling = form.billing_amount_mode === "direct";
  const colonyCharge = directBilling ? Math.max(0, Number(form.billing_amount || 0)) : gross + markupAmount;

  const save = async () => {
    if (!form.worker_user_id) return toast.error("Pick a worker");
    if (gross <= 0) return toast.error("Gross pay must be > 0");
    if (form.period_start > form.period_end) return toast.error("Period start must be before period end");
    if (form.paid && !form.payment_method) return toast.error("Choose how the payroll was paid");
    if (form.paid && !form.paid_at) return toast.error("Choose the payment date");
    if (ledger === "mgmt_usa_nj" && form.colony_id && directBilling && colonyCharge <= 0) return toast.error("Exact colony billing must be greater than zero");
    setSaving(true);
    try {
      const w = workers.find((x) => x.id === form.worker_user_id);
      const body = {
        ...form,
        worker_name: w?.name || "",
        hours: form.hours === "" ? null : parseFloat(form.hours),
        rate: form.rate === "" ? null : parseFloat(form.rate),
        paid_at: form.paid ? form.paid_at : null,
        payment_method: form.paid ? form.payment_method : "",
        payment_reference: form.paid && payRefSpec.show ? (form.payment_reference || "") : "",
        ledger,
        colony_id: ledger === "mgmt_usa_nj" ? (form.colony_id || null) : null,
        markup_type: ledger === "mgmt_usa_nj" && form.colony_id && !directBilling ? (form.markup_type || "none") : "none",
        markup_value: ledger === "mgmt_usa_nj" && form.colony_id && !directBilling ? markupValue : 0,
        billing_amount_mode: ledger === "mgmt_usa_nj" && form.colony_id ? (form.billing_amount_mode || "markup") : "markup",
        billing_amount: ledger === "mgmt_usa_nj" && form.colony_id && directBilling ? colonyCharge : null,
      };
      const response = editing ? await api.put(`/payroll/${editing.id}`, body) : await api.post("/payroll", body);
      toast.success(response.data?.billing_charge_id ? "Payroll and linked billing charge updated" : "Payroll saved — create its charge under Payroll billing");
      // Pass period dates back so the parent can auto-extend the date
      // filter when the saved row would otherwise be hidden.
      onSaved(response.data || { period_start: body.period_start, period_end: body.period_end });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payroll-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit payroll entry" : "Record payroll"}</DialogTitle>
          <DialogDescription>Enter hours × rate and the gross fills in automatically. Or skip both and just type a flat amount.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Worker</Label>
            <Select value={form.worker_user_id} onValueChange={selectWorker}>
              <SelectTrigger data-testid="payroll-worker"><SelectValue placeholder="Pick a worker" /></SelectTrigger>
              <SelectContent>
                {workers.length === 0 && (
                  <SelectItem value="__none__" disabled>
                    {ledger === "mgmt_usa_nj" ? "No workers yet — add one from Manage workers" : "No drivers or secretaries on file"}
                  </SelectItem>
                )}
                {workers.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                    {w.role && w.role !== "worker" && <span className="text-xs text-muted-foreground"> · {w.role}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!editing && templateLoadedFor === form.worker_user_id && (
              <p className="text-xs text-emerald-700 mt-1.5" data-testid="payroll-template-defaults-loaded">Saved worker template loaded. You can change any value for this payroll record.</p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Period start</Label><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="payroll-start" /></div>
            <div><Label>Period end</Label><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="payroll-end" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Hours (optional)</Label><Input type="number" step="0.25" min="0" value={form.hours} onChange={(e) => setHoursOrRate("hours", e.target.value)} data-testid="payroll-hours" /></div>
            <div><Label>Rate $/hr (optional)</Label><Input type="number" step="0.01" min="0" value={form.rate} onChange={(e) => setHoursOrRate("rate", e.target.value)} data-testid="payroll-rate" /></div>
            <div><Label>Gross pay $</Label><Input type="number" step="0.01" min="0" value={form.gross_pay} onChange={(e) => setForm({ ...form, gross_pay: parseFloat(e.target.value || 0) })} data-testid="payroll-gross" /></div>
          </div>
          <div><Label>Notes (optional)</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="payroll-notes" /></div>

          {ledger === "mgmt_usa_nj" && (
            <div>
              <Label>Bill to (bungalow colony)</Label>
              <Select value={form.colony_id || "__none__"} onValueChange={(v) => setForm({ ...form, colony_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="payroll-colony"><SelectValue placeholder="— unassigned overhead —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— unassigned overhead —</SelectItem>
                  {colonies.map((c) => <SelectItem key={c.id} value={c.id}><ColonyDotName id={c.id} name={c.name} /></SelectItem>)}
                </SelectContent>
              </Select>
              {colonies.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">No colonies yet — add one on the Overview tab first.</p>
              )}
            </div>
          )}

          {ledger === "mgmt_usa_nj" && form.colony_id && (
            <div className="border border-border rounded-sm p-3 space-y-3" data-testid="payroll-colony-billing-panel">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">Colony payroll charge</div>
                  <p className="text-xs text-muted-foreground">The colony sees one combined amount; markup stays private.</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm font-semibold bg-sky-100 text-sky-800" data-testid="payroll-auto-billing-status">Payroll billing</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Colony billing</Label>
                  <Select value={form.billing_amount_mode || "markup"} onValueChange={(value) => setForm({ ...form, billing_amount_mode: value, billing_amount: value === "direct" ? (form.billing_amount || gross) : null })}>
                    <SelectTrigger data-testid="payroll-billing-amount-mode"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="markup">Payroll + markup</SelectItem><SelectItem value="direct">Exact billing amount</SelectItem></SelectContent>
                  </Select>
                </div>
                {directBilling ? (
                  <div><Label>Exact colony amount $</Label><Input type="number" min="0.01" step="0.01" value={form.billing_amount ?? ""} onChange={(event) => setForm({ ...form, billing_amount: event.target.value })} data-testid="payroll-billing-direct-amount" /></div>
                ) : (
                  <div>
                    <Label>Markup type</Label>
                    <Select value={form.markup_type || "none"} onValueChange={(value) => setForm({ ...form, markup_type: value, markup_value: value === "none" ? 0 : form.markup_value })}>
                      <SelectTrigger data-testid="payroll-markup-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">No markup</SelectItem><SelectItem value="percent">Percentage</SelectItem><SelectItem value="fixed">Fixed amount</SelectItem></SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {!directBilling && form.markup_type !== "none" && (
                <div><Label>{form.markup_type === "percent" ? "Markup %" : "Markup amount $"}</Label><Input type="number" min="0" step="0.01" value={form.markup_value ?? 0} onChange={(event) => setForm({ ...form, markup_value: event.target.value })} data-testid="payroll-markup-value" /></div>
              )}
              <div className="grid grid-cols-3 gap-2 text-center bg-secondary/30 rounded-sm p-2" data-testid="payroll-colony-charge-preview">
                <div><div className="text-[10px] text-muted-foreground uppercase">Payroll</div><div className="font-semibold mono-num">{fmtMoney(gross)}</div></div>
                <div><div className="text-[10px] text-muted-foreground uppercase">{directBilling ? "Difference" : "Markup"}</div><div className="font-semibold mono-num">{fmtMoney(colonyCharge - gross)}</div></div>
                <div><div className="text-[10px] text-muted-foreground uppercase">Colony charge</div><div className="font-bold mono-num text-primary">{fmtMoney(colonyCharge)}</div></div>
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!form.paid} onChange={(e) => setForm({ ...form, paid: e.target.checked })} data-testid="payroll-paid" />
            Mark as paid (funds already moved)
          </label>
          {form.paid && (
            <div className="border border-border rounded-sm p-3 space-y-3" data-testid="payroll-initial-payment-panel">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Date paid</Label>
                  <Input type="date" value={form.paid_at || todayISO()} onChange={(event) => setForm({ ...form, paid_at: event.target.value })} data-testid="payroll-payment-date" />
                </div>
                <div>
                  <Label>Payment method</Label>
                  <Select value={form.payment_method || ""} onValueChange={(value) => setForm({ ...form, payment_method: value, payment_reference: "" })}>
                    <SelectTrigger data-testid="payroll-payment-method"><SelectValue placeholder="Choose method" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Check">Check</SelectItem>
                      <SelectItem value="ACH">ACH</SelectItem>
                      <SelectItem value="Zelle">Zelle</SelectItem>
                      <SelectItem value="Credit card">Credit card</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {payRefSpec.show && (
                <div>
                  <Label>{payRefSpec.label}</Label>
                  <Input
                    inputMode={payRefSpec.digitsOnly ? "numeric" : "text"}
                    maxLength={payRefSpec.maxLen}
                    placeholder={payRefSpec.placeholder}
                    value={form.payment_reference || ""}
                    onChange={(event) => {
                      let value = event.target.value;
                      if (payRefSpec.digitsOnly) value = value.replace(/\D/g, "");
                      setForm({ ...form, payment_reference: value.slice(0, payRefSpec.maxLen) });
                    }}
                    data-testid={payRefSpec.testId}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="payroll-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayrollDetailsDialog({ entry, colonyName, onOpenChange }) {
  if (!entry) return null;
  const payments = entry.payments || [];
  const gross = Number(entry.gross_pay || 0);
  const paid = Number(entry.amount_paid ?? (entry.paid ? gross : 0));
  const markup = Number(entry.markup_amount || 0);
  const charge = Number(entry.colony_charge_total ?? (entry.colony_id ? gross + markup : 0));
  return (
    <Dialog open={!!entry} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="payroll-details-dialog">
        <DialogHeader>
          <DialogTitle>Payroll details · {entry.worker_name}</DialogTitle>
          <DialogDescription>Payment, work period, and colony billing information for this record.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="payroll-details-summary">
            <DetailValue label="Period" value={`${entry.period_start} → ${entry.period_end}`} tone="sky" />
            <DetailValue label="Gross payroll" value={fmtMoney(gross)} tone="amber" />
            <DetailValue label="Paid" value={fmtMoney(paid)} tone="emerald" />
            <DetailValue label="Balance" value={fmtMoney(Math.max(0, gross - paid))} tone={gross - paid > 0.005 ? "rose" : "emerald"} />
          </div>
          {(entry.hours != null || entry.rate != null || entry.notes) && (
            <div className="text-sm border border-border rounded-sm p-3 space-y-1">
              {entry.hours != null && <p><span className="text-muted-foreground">Hours:</span> {entry.hours}</p>}
              {entry.rate != null && <p><span className="text-muted-foreground">Rate:</span> {fmtMoney(entry.rate)}/hr</p>}
              {entry.notes && <p><span className="text-muted-foreground">Notes:</span> {entry.notes}</p>}
            </div>
          )}
          <div className="border border-border rounded-sm overflow-hidden" data-testid="payroll-details-payments">
            <div className="px-3 py-2 bg-secondary/30 text-xs uppercase tracking-wider text-muted-foreground">How payment was made</div>
            {payments.length ? payments.map((payment) => (
              <div key={payment.id} className="px-3 py-2 border-t border-border text-sm flex justify-between gap-3" data-testid={`payroll-details-payment-${payment.id}`}>
                <div>
                  <div className="font-medium">{payment.paid_at || "Date not recorded"} · {payment.payment_method || "Method not recorded"}</div>
                  {(payment.payment_reference || payment.note) && <div className="text-xs text-muted-foreground">{[payment.payment_reference && `Ref ${payment.payment_reference}`, payment.note].filter(Boolean).join(" · ")}</div>}
                </div>
                <div className="mono-num font-semibold">{fmtMoney(payment.amount)}</div>
              </div>
            )) : (
              <div className="px-3 py-4 text-sm text-muted-foreground">No payment has been recorded.</div>
            )}
          </div>
          <div className="border border-border rounded-sm p-3" data-testid="payroll-details-colony-billing">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Colony billing</div>
            {entry.colony_id ? (
              <div className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between gap-3"><span>Bill to</span><span className="font-semibold">{colonyName || "Colony"}</span></div>
                <div className="flex justify-between gap-3"><span>Payroll</span><span className="mono-num">{fmtMoney(gross)}</span></div>
                {entry.billing_amount_mode === "direct" ? (
                  <div className="flex justify-between gap-3"><span>Direct colony amount</span><span className="mono-num">{fmtMoney(charge)}</span></div>
                ) : (
                  <div className="flex justify-between gap-3"><span>Private markup {entry.markup_type === "percent" ? `(${entry.markup_value || 0}%)` : entry.markup_type === "fixed" ? "(fixed)" : ""}</span><span className="mono-num">{fmtMoney(markup)}</span></div>
                )}
                <div className="flex justify-between gap-3 pt-1 border-t border-border font-bold"><span>Combined colony charge</span><span className="mono-num">{fmtMoney(charge)}</span></div>
                <p className="text-xs text-muted-foreground pt-1">
                  {entry.billing_charge_id ? "Linked charge is visible under Payroll Billing and in the colony portal." : "No billing charge yet. Create it under Payroll Billing or enable the worker's billing schedule."}
                </p>
              </div>
            ) : <p className="text-sm text-muted-foreground mt-2">Unassigned overhead — no colony selected.</p>}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} data-testid="payroll-details-close">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailValue({ label, value, tone = "sky" }) {
  const tones = {
    sky: "border-sky-200 bg-sky-50 text-sky-950",
    amber: "border-amber-200 bg-amber-50 text-amber-950",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-950",
    rose: "border-rose-200 bg-rose-50 text-rose-950",
  };
  return (
    <div className={`border rounded-sm p-2 ${tones[tone] || tones.sky}`} data-testid={`payroll-detail-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-sm font-bold mt-0.5">{value}</div>
    </div>
  );
}

function PayrollPaymentDialog({ entry, onOpenChange, onSaved }) {
  // Drives a partial-payment workflow against a payroll entry. "entry" is the
  // currently-selected payroll row (null when the dialog is closed). We can't
  // memoize the derived totals up in the parent because the dialog re-opens
  // each time with a fresh row, so all the math lives here.
  const open = !!entry;
  const gross = Number(entry?.gross_pay || 0);
  const alreadyPaid = Number(entry?.amount_paid ?? (entry?.paid ? gross : 0));
  const owed = Math.max(0, gross - alreadyPaid);

  const [amount, setAmount] = useState("");
  const [paidAt, setPaidAt] = useState(todayISO());
  const [note, setNote] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    if (open) {
      // Default the input to the outstanding balance — admin most often wants
      // to mark it fully paid in one click. They can override for partials.
      setAmount(owed > 0 ? owed.toFixed(2) : "");
      setPaidAt(todayISO());
      setNote("");
      setPaymentMethod("");
      setPaymentReference("");
    }
  }, [entry?.id, open, owed]);

  if (!entry) return null;

  // Reference-input rules per method (mirrors the Expense dialog):
  //   Check → Check #        (digits, up to 10)
  //   Zelle → Transaction #  (up to 32 chars, free-form)
  //   ACH / Bank → Confirmation # (up to 32 chars, free-form)
  //   Cash / Other → no reference
  const methodLower = (paymentMethod || "").toLowerCase();
  const payRefSpec = (() => {
    if (methodLower.includes("check")) return { show: true, label: "Check #", placeholder: "1042", maxLen: 10, digitsOnly: true, testId: "pay-dialog-method-check-number" };
    if (methodLower.includes("zelle")) return { show: true, label: "Zelle transaction #", placeholder: "e.g. ABC123XYZ", maxLen: 32, digitsOnly: false, testId: "pay-dialog-method-zelle-ref" };
    if (methodLower.includes("ach")) return { show: true, label: "ACH confirmation #", placeholder: "e.g. TRN12345", maxLen: 32, digitsOnly: false, testId: "pay-dialog-method-ach-ref" };
    if (methodLower.includes("credit")) return { show: true, label: "Card last 4", placeholder: "1234", maxLen: 4, digitsOnly: true, testId: "pay-dialog-method-card-last4" };
    return { show: false };
  })();

  const onPaymentMethodChange = (v) => {
    // Reset the reference when the method category changes so an old
    // check number doesn't stick around on a Zelle payment.
    setPaymentMethod(v);
    setPaymentReference("");
  };

  const onPaymentRefChange = (raw) => {
    let v = raw;
    if (payRefSpec.digitsOnly) v = v.replace(/\D/g, "");
    if (payRefSpec.maxLen) v = v.slice(0, payRefSpec.maxLen);
    setPaymentReference(v);
  };

  const amtNum = parseFloat(amount);
  const invalid = !(amtNum > 0) || amtNum > owed + 0.005;
  const previewBalance = Math.max(0, owed - (isNaN(amtNum) ? 0 : amtNum));

  const record = async () => {
    if (invalid) {
      if (!(amtNum > 0)) return toast.error("Enter an amount greater than zero.");
      return toast.error(`Amount can't exceed the remaining balance of ${fmtMoney(owed)}.`);
    }
    if (!paymentMethod) return toast.error("Choose how the payroll was paid.");
    setSaving(true);
    try {
      await api.post(`/payroll/${entry.id}/payment`, {
        amount: amtNum,
        paid_at: paidAt,
        note: note.trim(),
        payment_method: paymentMethod || "",
        payment_reference: payRefSpec.show ? (paymentReference || "") : "",
      });
      toast.success(amtNum + 0.005 >= owed ? "Marked fully paid" : "Payment recorded");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const removePayment = async (pid) => {
    if (!window.confirm("Remove this payment? The balance will be restored.")) return;
    setRemovingId(pid);
    try {
      await api.delete(`/payroll/${entry.id}/payment/${pid}`);
      toast.success("Payment removed");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setRemovingId(null); }
  };

  const payments = entry.payments || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payroll-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record payment · {entry.worker_name}</DialogTitle>
          <DialogDescription>
            Pay all or part of this payroll. Partial payments are tracked and the balance updates automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Running totals — Gross / Paid / Remaining at a glance. */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-center border border-border rounded-sm overflow-hidden">
            <div className="p-3 border-r border-border">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Gross</div>
              <div className="font-semibold mono-num" data-testid="pay-dialog-gross">{fmtMoney(gross)}</div>
            </div>
            <div className="p-3 border-r border-border bg-success/5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Paid so far</div>
              <div className="font-semibold mono-num text-success" data-testid="pay-dialog-paid">{fmtMoney(alreadyPaid)}</div>
            </div>
            <div className="p-3 bg-destructive/5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Remaining</div>
              <div className="font-semibold mono-num text-destructive" data-testid="pay-dialog-remaining">{fmtMoney(owed)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Amount $</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={owed}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
                data-testid="pay-dialog-amount"
              />
              <div className="flex flex-wrap gap-1 mt-1">
                <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setAmount(owed.toFixed(2))} data-testid="pay-dialog-amount-full">Full ({fmtMoney(owed)})</Button>
                {owed >= 0.02 && <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setAmount((owed / 2).toFixed(2))} data-testid="pay-dialog-amount-half">Half</Button>}
              </div>
            </div>
            <div>
              <Label>Date paid</Label>
              <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid="pay-dialog-date" />
            </div>
          </div>

          <div>
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. paid at kitchen table, direct deposit, etc." data-testid="pay-dialog-note" />
          </div>

          {/* Payment method + optional reference (Zelle #, Check #, ACH #). */}
          <div className={payRefSpec.show ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : ""}>
            <div>
              <Label>Payment method</Label>
              <Select value={paymentMethod || "__none__"} onValueChange={(v) => onPaymentMethodChange(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="pay-dialog-method"><SelectValue placeholder="Pick a method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" disabled>Choose method</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Check">Check</SelectItem>
                  <SelectItem value="ACH">ACH</SelectItem>
                  <SelectItem value="Zelle">Zelle</SelectItem>
                  <SelectItem value="Credit card">Credit card</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {payRefSpec.show && (
              <div>
                <Label>{payRefSpec.label}</Label>
                <Input
                  inputMode={payRefSpec.digitsOnly ? "numeric" : "text"}
                  pattern={payRefSpec.digitsOnly ? "[0-9]*" : undefined}
                  maxLength={payRefSpec.maxLen}
                  placeholder={payRefSpec.placeholder}
                  value={paymentReference}
                  onChange={(e) => onPaymentRefChange(e.target.value)}
                  data-testid={payRefSpec.testId}
                />
              </div>
            )}
          </div>

          {!isNaN(amtNum) && amtNum > 0 && !invalid && (
            <p className="text-xs text-muted-foreground" data-testid="pay-dialog-preview">
              After this payment, balance will be <span className="font-semibold mono-num text-foreground">{fmtMoney(previewBalance)}</span>
              {previewBalance < 0.005 && <span className="text-success font-semibold"> — fully paid.</span>}
            </p>
          )}

          {/* Payment history — admin can undo a misclick. */}
          {payments.length > 0 && (
            <div className="border border-border rounded-sm" data-testid="pay-dialog-history">
              <div className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-secondary/30">
                Payment history ({payments.length})
              </div>
              <div className="divide-y divide-border max-h-40 overflow-y-auto">
                {payments.map((p) => {
                  const methodLbl = p.payment_method || "";
                  const refLbl = p.payment_reference || "";
                  const mLower = methodLbl.toLowerCase();
                  const refDisplay = refLbl
                    ? (mLower.includes("check") ? `#${refLbl}` : refLbl)
                    : "";
                  const metaParts = [p.paid_at, methodLbl && (refDisplay ? `${methodLbl} · ${refDisplay}` : methodLbl), p.note].filter(Boolean);
                  return (
                    <div key={p.id} className="px-3 py-2 flex items-center justify-between text-sm" data-testid={`pay-dialog-history-row-${p.id}`}>
                      <div>
                        <div className="font-medium mono-num">{fmtMoney(p.amount)}</div>
                        <div className="text-xs text-muted-foreground">{metaParts.join(" · ")}</div>
                      </div>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" disabled={removingId === p.id} onClick={() => removePayment(p.id)} data-testid={`pay-dialog-remove-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={record} disabled={saving || invalid} data-testid="pay-dialog-save">
            {saving ? "Recording…" : `Record ${amtNum > 0 ? fmtMoney(amtNum) : "payment"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DirectoryManagerDialog({ open, onOpenChange, initialTab = "workers", ledger, onWorkersChanged, onColoniesChanged }) {
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92dvh] overflow-hidden flex flex-col" data-testid="directory-manager-dialog" closeTestId="directory-manager-x-close">
        <DialogHeader>
          <DialogTitle data-testid="directory-manager-title">Manage workers &amp; colonies</DialogTitle>
          <DialogDescription data-testid="directory-manager-description">Add, edit, and manage both payroll workers and bungalow colonies without leaving this window.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-1 rounded-sm bg-secondary p-1" role="tablist" aria-label="Management type" data-testid="directory-manager-tabs">
          <button type="button" role="tab" aria-selected={tab === "workers"} onClick={() => setTab("workers")} className={`min-h-10 rounded-sm px-3 text-sm font-semibold transition-[background-color,color,box-shadow] ${tab === "workers" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`} data-testid="directory-manager-tab-workers"><Users className="w-4 h-4 inline mr-1.5" />Workers</button>
          <button type="button" role="tab" aria-selected={tab === "colonies"} onClick={() => setTab("colonies")} className={`min-h-10 rounded-sm px-3 text-sm font-semibold transition-[background-color,color,box-shadow] ${tab === "colonies" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`} data-testid="directory-manager-tab-colonies"><Building2 className="w-4 h-4 inline mr-1.5" />Colonies</button>
        </div>
        <div className="min-h-0 overflow-y-auto pr-1" data-testid="directory-manager-content">
          {tab === "workers" ? <ManageWorkersPanel active={open} ledger={ledger} onChanged={onWorkersChanged} /> : <ManageColoniesPanel active={open} onChanged={onColoniesChanged} />}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} data-testid="directory-manager-close">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageWorkersPanel({ active, ledger, onChanged }) {
  // Management-scoped roster with optional login setup using the existing invite flow.
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id, name, notes} being renamed inline
  const [userDialogFor, setUserDialogFor] = useState(null); // worker whose login is being managed

  const refresh = useCallback(() => {
    setLoading(true);
    return api.get(withLedger("/payroll/workers", ledger))
      .then((response) => setRows((response.data || []).map((worker) => ({ ...worker, notes: worker.notes || "", user_id: worker.user_id || null, email: worker.email || "", is_worker_manager: worker.is_worker_manager === true }))))
      .catch(() => toast.error("Could not load workers."))
      .finally(() => setLoading(false));
  }, [ledger]);

  useEffect(() => {
    if (active) {
      refresh();
      setEditing(null);
    }
  }, [active, refresh]);

  const workerSaved = worker => {
    setRows(previous => [...previous.filter(row => row.id !== worker.id), worker].sort((a, b) => a.name.localeCompare(b.name)));
    onChanged?.();
  };

  const saveEdit = async () => {
    const nm = (editing?.name || "").trim();
    if (!nm) return toast.error("Name can't be empty");
    setSaving(true);
    try {
      const r = await api.put(`/payroll/workers/${editing.id}`, { name: nm, display_name: editing.display_name || "", notes: (editing.notes || "").trim(), ledger });
      setRows((prev) => prev.map((w) => (w.id === editing.id ? { ...w, ...r.data } : w)).sort((a, b) => a.name.localeCompare(b.name)));
      setEditing(null);
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const remove = async (w) => {
    if (!window.confirm(`Remove ${w.name} from the payroll roster? Historical payroll rows are kept — this just hides them from the picker.`)) return;
    try {
      await api.delete(`/payroll/workers/${w.id}`, { params: { ledger } });
      setRows((prev) => prev.filter((x) => x.id !== w.id));
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <>
      <div className="space-y-4 py-3" data-testid="mgmt-workers-dialog">
        <div><h3 className="font-semibold" data-testid="directory-workers-heading">Workers</h3><p className="text-xs text-muted-foreground">This roster is only for Management USA NJ payroll. Worker portal logins can be managed from the key button.</p></div>
          {/* Add new worker */}
          <AddWorkerForm ledger={ledger} onSaved={workerSaved} />

          {/* Roster */}
          <div className="border border-border rounded-sm overflow-hidden">
            <div className="hidden sm:grid grid-cols-[1fr_1fr_auto] px-3 py-2 text-xs font-mono uppercase tracking-wider text-muted-foreground border-b border-border bg-secondary/40">
              <div>Name</div><div>Notes</div><div className="text-right">·</div>
            </div>
            {loading ? (
              <div className="px-3 py-8 flex items-center justify-center text-sm text-muted-foreground" data-testid="mgmt-workers-loading"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading workers…</div>
            ) : rows.length === 0 && (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="mgmt-workers-empty">
                No workers yet. Add one above.
              </div>
            )}
            <div className="max-h-64 overflow-y-auto">
              {rows.map((w) => {
                const isEditing = editing?.id === w.id;
                return (
                  <div key={w.id} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-center px-3 py-3 border-b border-border last:border-b-0" data-testid={`mgmt-worker-row-${w.id}`}>
                    {isEditing ? (
                      <>
                        <div className="min-w-0 space-y-2"><Input aria-label="Worker name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter" && !saving) saveEdit(); }} autoFocus data-testid={`mgmt-worker-edit-name-${w.id}`} /><DisplayNameField id={`mgmt-worker-edit-display-name-${w.id}`} value={editing.display_name} onChange={value => setEditing({ ...editing, display_name: value })} /></div>
                        <Input value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); }} data-testid={`mgmt-worker-edit-notes-${w.id}`} />
                        <div className="flex gap-1 justify-end mobile-card-actions">
                          <Button size="sm" onClick={saveEdit} disabled={saving} className="rounded-sm" data-testid={`mgmt-worker-edit-save-${w.id}`}>Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="rounded-sm" data-testid={`mgmt-worker-edit-cancel-${w.id}`}>Cancel</Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm font-medium truncate" data-testid={`mgmt-worker-row-name-${w.id}`}>{w.name}</div>
                        <div className="text-sm text-muted-foreground truncate" data-testid={`mgmt-worker-row-notes-${w.id}`}>{w.notes || "—"}</div>
                        <div className="flex gap-1 justify-end mobile-card-actions">
                          <Button size="sm" variant="ghost" className={`rounded-sm ${w.user_id ? "text-emerald-700" : ""}`} onClick={() => setUserDialogFor(w)} title={w.user_id ? `Login active: ${w.email}` : "Create a login for this worker"} aria-label={`Manage login for ${w.name}`} data-testid={`mgmt-worker-user-${w.id}`}>
                            <KeyRound className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => setEditing({ id: w.id, name: w.original_name || w.name, display_name: w.display_name || "", notes: w.notes || "" })} aria-label={`Edit ${w.name}`} data-testid={`mgmt-worker-edit-${w.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="rounded-sm text-destructive" onClick={() => remove(w)} aria-label={`Delete ${w.name}`} data-testid={`mgmt-worker-delete-${w.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
      </div>
      <WorkerUserDialog
        worker={userDialogFor}
        onOpenChange={(v) => !v && setUserDialogFor(null)}
        onSaved={() => { setUserDialogFor(null); refresh(); onChanged?.(); }}
        onManagerToggled={(next) => {
          setRows((prev) => prev.map((x) => (x.id === userDialogFor?.id ? { ...x, is_worker_manager: next } : x)));
          setUserDialogFor((prev) => (prev ? { ...prev, is_worker_manager: next } : prev));
          onChanged?.();
        }}
      />
    </>
  );
}


// ---------------------------------------------------------------------
// Bungalow Colonies — Management USA NJ "customers".
// Each colony accumulates a bill (expenses + payroll tagged with its
// colony_id) and tracks payments received back. Owner sees a running
// balance per colony and can open a full statement.
// ---------------------------------------------------------------------
function BungalowColoniesSection({ onNavigate }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manageOpen, setManageOpen] = useState(false);
  const [statementFor, setStatementFor] = useState(null); // {id, name}
  const [touchRows, setTouchRows] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/mgmt/colonies/summary")
      .then((r) => setSummary(r.data))
      .catch(() => toast.error("Could not load colony balances."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const query = window.matchMedia("(pointer: coarse), (max-width: 1024px)");
    const update = () => setTouchRows(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  const list = summary?.colonies || [];

  return (
    <section className="management-breakdown border border-border rounded-sm bg-background overflow-hidden shadow-sm" data-testid="colonies-section">
      <div className="px-3 sm:px-5 lg:px-6 py-3 flex items-start justify-between flex-wrap gap-3 sm:gap-5 mobile-stack border-b border-border bg-secondary/15">
        <div>
          <h2 className="font-semibold text-sm sm:text-base flex items-center gap-2 sm:gap-3" data-testid="colonies-heading"><span className="h-8 w-8 rounded-sm bg-secondary flex items-center justify-center"><Building2 className="w-4 h-4" /></span> Bungalow colonies</h2>
          <p className="text-xs text-muted-foreground mt-1.5" data-testid="colonies-description">Colony balances and general overhead stay clearly separated.</p>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm mobile-full" onClick={() => setManageOpen(true)} data-testid="colonies-manage">
          <UserCog className="w-3.5 h-3.5 mr-1.5" /> Manage colonies
        </Button>
      </div>

      <div className="overflow-hidden admin-table-scroll">
        <div className="admin-responsive-header grid grid-cols-12 px-4 sm:px-5 lg:px-6 py-2 text-xs font-mono uppercase tracking-wider text-muted-foreground border-b border-border bg-secondary/40">
          <div className="col-span-5">Colony</div>
          <div className="col-span-2 text-right">Billed</div>
          <div className="col-span-2 text-right">Received</div>
          <div className="col-span-3 text-right" data-testid="colonies-balance-heading">Billed balance</div>
        </div>
        {loading && <div className="px-4 py-10 flex items-center justify-center text-sm text-muted-foreground" data-testid="colonies-loading"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading colony balances…</div>}
        {!loading && list.length === 0 && !summary?.unassigned?.billed_total && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="colonies-empty">
            No colonies yet, and no expenses on the mgmt ledger. Click <span className="font-semibold">Manage colonies</span> to add one, or use Inv &amp; Exp / Payroll to record a bill.
          </div>
        )}
        {list.map((colony) => <ColonyBalanceRow key={colony.id} colony={colony} touchMode={touchRows} onOpen={setStatementFor} />)}
        {/* Unassigned overhead row — mgmt expenses/payroll with no colony_id
            still surface here so nothing is invisible. Received column is
            "—" (no colony to pay you back for overhead). */}
        {summary?.unassigned && (summary.unassigned.billed_total > 0 || list.length > 0) && (
          <div className="admin-responsive-row grid grid-cols-12 px-4 sm:px-5 lg:px-6 py-2 items-center border-b border-border last:border-b-0 hover:bg-secondary/30 bg-secondary/10" data-testid="colony-row-unassigned">
            <div className="col-span-5" data-mobile-label="Colony" data-mobile-span="full">
              <div className="font-medium text-sm flex items-center gap-2">Unassigned overhead <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary px-1.5 py-0.5 rounded-sm">general</span></div>
              <div className="text-xs text-muted-foreground">Not billed to a specific colony</div>
            </div>
            <div className="col-span-2 text-right mono-num text-sm" data-mobile-label="Billed" data-testid="colony-row-unassigned-billed">{fmtMoney(summary.unassigned.billed_total)}</div>
            <div className="col-span-2 text-right mono-num text-sm text-muted-foreground" data-mobile-label="Received">—</div>
            <div className="col-span-3 text-right mono-num text-sm text-muted-foreground" data-mobile-label="Balance">—</div>
          </div>
        )}
        {(list.length > 0 || (summary?.unassigned?.billed_total || 0) > 0) && (
          <div className="admin-responsive-row grid grid-cols-12 px-4 sm:px-5 lg:px-6 py-2 items-center border-t-2 border-border bg-secondary/40 text-sm font-semibold" data-testid="colonies-totals">
            <div className="col-span-5" data-mobile-label="Summary" data-mobile-span="full">Totals</div>
            <div className="col-span-2 text-right mono-num" data-mobile-label="Billed">{fmtMoney(summary.billed_total)}</div>
            <div className="col-span-2 text-right mono-num text-success" data-mobile-label="Received">{fmtMoney(summary.received_total)}</div>
            <div className={`col-span-3 text-right mono-num ${summary.balance_total > 0 ? "text-destructive" : summary.balance_total < 0 ? "text-emerald-600" : "text-muted-foreground"}`} data-mobile-label="Balance">{fmtMoney(summary.balance_total)}</div>
          </div>
        )}
      </div>

      <DirectoryManagerDialog open={manageOpen} onOpenChange={setManageOpen} initialTab="colonies" ledger="mgmt_usa_nj" onColoniesChanged={load} />
      <ColonyStatementDialog
        colony={statementFor}
        onOpenChange={(v) => { if (!v) setStatementFor(null); }}
        onChanged={load}
        onNavigate={(target, section) => { setStatementFor(null); onNavigate?.(target, section); }}
      />
    </section>
  );
}

function ColonyBalanceRow({ colony, touchMode, onOpen }) {
  const [armed, setArmed] = useState(false);
  const lastTapRef = React.useRef(0);
  const timerRef = React.useRef(null);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const activate = (event) => {
    if (!touchMode || event.detail === 0) {
      onOpen({ id: colony.id, name: colony.name });
      return;
    }
    const now = Date.now();
    if (now - lastTapRef.current < 650) {
      window.clearTimeout(timerRef.current);
      lastTapRef.current = 0;
      setArmed(false);
      onOpen({ id: colony.id, name: colony.name });
      return;
    }
    lastTapRef.current = now;
    setArmed(true);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setArmed(false), 700);
  };

  return (
    <div className={`group relative admin-responsive-row grid grid-cols-12 px-4 sm:px-5 lg:px-6 py-2 items-center border-b border-border last:border-b-0 transition-[background-color,box-shadow] ${armed ? "bg-primary/10 shadow-[inset_3px_0_0_hsl(var(--primary))]" : "hover:bg-secondary/45"}`} data-testid={`colony-row-${colony.id}`}>
      <button type="button" onClick={activate} className="absolute inset-0 z-0 touch-manipulation rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset" aria-label={touchMode ? `Double-tap to open ${colony.name} statement` : `Open ${colony.name} statement`} title={touchMode ? "Double-tap to open statement" : "Click to open statement"} data-testid={`colony-view-${colony.id}`} />
      <div className="relative z-10 pointer-events-none col-span-5" data-mobile-label="Colony" data-mobile-span="full">
        <div className="font-medium text-sm" data-testid={`colony-name-${colony.id}`}>{colony.name}</div>
        {(colony.contact_name || colony.contact_phone) && <div className="text-xs text-muted-foreground">{[colony.contact_name, colony.contact_phone].filter(Boolean).join(" · ")}</div>}
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground"><span data-testid={`colony-unbilled-${colony.id}`}>Not yet billed {fmtMoney(colony.unbilled_total)}</span><span data-testid={`colony-total-owed-${colony.id}`}>Total owed {fmtMoney(colony.total_owed)}</span></div>
      </div>
      <div className="relative z-10 pointer-events-none col-span-2 text-right mono-num text-sm" data-mobile-label="Billed" data-testid={`colony-billed-${colony.id}`}>{fmtMoney(colony.billed_total)}</div>
      <div className="relative z-10 pointer-events-none col-span-2 text-right mono-num text-sm text-success" data-mobile-label="Received" data-testid={`colony-received-${colony.id}`}>{fmtMoney(colony.received_total)}</div>
      <div className={`relative z-10 pointer-events-none col-span-3 text-right mono-num text-sm font-semibold flex items-center justify-end gap-2 ${colony.balance > 0 ? "text-destructive" : colony.balance < 0 ? "text-emerald-600" : "text-muted-foreground"}`} data-mobile-label="Balance" data-testid={`colony-balance-${colony.id}`}><span>{fmtMoney(colony.balance)}</span><ChevronRight className="w-4 h-4 text-muted-foreground" /></div>
    </div>
  );
}

function ManageColoniesPanel({ active, onChanged }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", display_name: "", contact_name: "", contact_phone: "", contact_email: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [usersFor, setUsersFor] = useState(null);
  const [watchersFor, setWatchersFor] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    return api.get("/mgmt/colonies")
      .then((response) => setRows(response.data || []))
      .catch(() => toast.error("Could not load colonies."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { if (active) { refresh(); setEditing(null); setForm({ name: "", display_name: "", contact_name: "", contact_phone: "", contact_email: "", notes: "" }); } }, [active, refresh]);

  const add = async () => {
    const nm = form.name.trim();
    if (!nm) return toast.error("Colony name is required");
    setSaving(true);
    try {
      await api.post("/mgmt/colonies", { ...form, name: nm });
      setForm({ name: "", display_name: "", contact_name: "", contact_phone: "", contact_email: "", notes: "" });
      refresh(); onChanged?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const saveEdit = async () => {
    const nm = (editing?.name || "").trim();
    if (!nm) return toast.error("Name can't be empty");
    setSaving(true);
    try {
      await api.put(`/mgmt/colonies/${editing.id}`, editing);
      statementCache.delete(editing.id);
      setEditing(null);
      refresh(); onChanged?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name} from the colony list? Existing bills stay in history — the colony just disappears from pickers.`)) return;
    try {
      await api.delete(`/mgmt/colonies/${c.id}`);
      refresh(); onChanged?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const toggleAiBreakdown = async (c) => {
    const next = !c.show_ai_breakdown;
    setRows((prev) => prev.map((r) => (r.id === c.id ? { ...r, show_ai_breakdown: next } : r)));
    try {
      await api.patch(`/mgmt/colonies/${c.id}/ai-breakdown`, { show_ai_breakdown: next });
      toast.success(next ? `${c.name} can now see the AI receipt breakdown on their charges` : `AI receipt breakdown hidden from ${c.name}`);
      onChanged?.();
    } catch (e) {
      setRows((prev) => prev.map((r) => (r.id === c.id ? { ...r, show_ai_breakdown: !next } : r)));
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <>
      <div className="space-y-4 py-3" data-testid="colonies-dialog">
        <div><h3 className="font-semibold" data-testid="directory-colonies-heading">Colonies</h3><p className="text-xs text-muted-foreground">Add the bungalow colonies you service under Management USA NJ.</p></div>
          <div className="border border-border rounded-sm p-3 bg-secondary/20 space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Add a colony</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 items-end gap-3"><div className="min-w-0 space-y-1"><Label htmlFor="colony-form-name" className="text-xs text-muted-foreground" data-testid="colony-form-name-label">Name</Label><Input id="colony-form-name" placeholder="Name (e.g. Green Acres)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="colony-form-name" /></div><DisplayNameField id="colony-form-display-name" value={form.display_name} onChange={value => setForm({ ...form, display_name: value })} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input placeholder="Contact name (optional)" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="colony-form-contact-name" />
              <Input placeholder="Phone (optional)" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="colony-form-contact-phone" />
            </div>
            <Input type="email" placeholder="Billing email — invoices are sent here" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="colony-form-contact-email" />
            <div className="flex justify-end">
              <Button onClick={add} disabled={saving || !form.name.trim()} className="rounded-sm" data-testid="colony-form-add">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>
          <div className="border border-border rounded-sm max-h-64 overflow-y-auto">
            {loading ? <div className="px-3 py-6 flex items-center justify-center text-sm text-muted-foreground" data-testid="colonies-manage-loading"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading colonies…</div> : rows.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="colonies-manage-empty">No colonies yet.</div>}
            {rows.map((c) => {
              const isEditing = editing?.id === c.id;
              return (
                <div key={c.id} className="px-3 py-2 flex items-center justify-between border-b border-border last:border-b-0 gap-2" data-testid={`colony-manage-row-${c.id}`}>
                  {isEditing ? (
                    <>
                      <div className="flex-1 min-w-0 space-y-2">
                        <Input aria-label="Colony name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid={`colony-edit-name-${c.id}`} />
                        <DisplayNameField id={`colony-edit-display-name-${c.id}`} value={editing.display_name} onChange={value => setEditing({ ...editing, display_name: value })} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                          <Input placeholder="Contact" value={editing.contact_name || ""} onChange={(e) => setEditing({ ...editing, contact_name: e.target.value })} data-testid={`colony-edit-contact-${c.id}`} />
                          <Input placeholder="Phone" value={editing.contact_phone || ""} onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })} data-testid={`colony-edit-phone-${c.id}`} />
                        </div>
                        <Input type="email" placeholder="Billing email" value={editing.contact_email || ""} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} data-testid={`colony-edit-contact-email-${c.id}`} />
                      </div>
                      <div className="flex gap-1 flex-col">
                        <Button size="sm" onClick={saveEdit} disabled={saving} className="rounded-sm" data-testid={`colony-edit-save-${c.id}`}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="rounded-sm" data-testid={`colony-edit-cancel-${c.id}`}>Cancel</Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate" data-testid={`colony-manage-name-${c.id}`}>{c.name}</div>
                        <div className="text-xs text-muted-foreground truncate" data-testid={`colony-manage-contact-${c.id}`}>{[c.contact_name, c.contact_phone, c.contact_email].filter(Boolean).join(" · ") || "—"}</div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className={`rounded-sm ${c.show_ai_breakdown ? "text-violet-600" : "text-muted-foreground/50"}`} onClick={() => toggleAiBreakdown(c)} title={c.show_ai_breakdown ? "Colony portal shows the AI receipt breakdown (fixes, products, receipt) on their charges — click to hide" : "Let this colony see the AI receipt breakdown (fixes, products, receipt) on their charges"} aria-label={`Toggle AI receipt breakdown for ${c.name}`} data-testid={`colony-manage-ai-${c.id}`}>
                          <Sparkles className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => setUsersFor(c)} title="Manage portal logins for this colony" aria-label={`Manage portal logins for ${c.name}`} data-testid={`colony-manage-users-${c.id}`}>
                          <Users className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => setWatchersFor(c)} title="Pick who gets emailed on ticket events" aria-label={`Manage ticket email recipients for ${c.name}`} data-testid={`colony-manage-watchers-${c.id}`}>
                          <Bell className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => setEditing({ id: c.id, name: c.original_name || c.name, display_name: c.display_name || "", contact_name: c.contact_name || "", contact_phone: c.contact_phone || "", contact_email: c.contact_email || "", notes: c.notes || "" })} aria-label={`Edit ${c.name}`} data-testid={`colony-manage-edit-${c.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-sm text-destructive" onClick={() => remove(c)} aria-label={`Delete ${c.name}`} data-testid={`colony-manage-delete-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
      </div>
      <ColonyPortalUsersDialog colony={usersFor} onOpenChange={(v) => !v && setUsersFor(null)} />
      <ColonyWatchersDialog colony={watchersFor} onOpenChange={(v) => !v && setWatchersFor(null)} />
    </>
  );
}

function ColonyStatementDialog({ colony, onOpenChange, onChanged, onNavigate }) {
  // Full statement view: itemized expenses + payroll + payments received.
  // The "Record payment received" button opens a nested small dialog.
  const open = !!colony;
  const [stmt, setStmt] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [invTypeFilter, setInvTypeFilter] = useState("all"); // all | regular | payroll
  const [stmtBusy, setStmtBusy] = useState(null); // "pdf" | "email"

  const load = useCallback((force = false) => {
    if (!colony) return;
    const cached = statementCache.get(colony.id);
    if (!force && cached && Date.now() - cached.savedAt < STATEMENT_CACHE_TTL_MS) {
      setStmt(cached.data);
    }
    api.get(`/mgmt/colonies/${colony.id}/statement`).then((r) => {
      statementCache.set(colony.id, { data: r.data, savedAt: Date.now() });
      setStmt(r.data);
    }).catch(() => toast.error("Could not load this colony statement."));
  }, [colony]);
  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { if (!open) setStmt(null); }, [open]);

  const removePayment = async (pid) => {
    if (!window.confirm("Remove this recorded payment? The balance will go back up.")) return;
    try {
      await api.delete(`/mgmt/colonies/${colony.id}/payments/${pid}`);
      toast.success("Payment removed");
      load(true); onChanged();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="colony-statement-dialog">
        <DialogHeader>
          <DialogTitle>Statement · {colony.name}</DialogTitle>
          <DialogDescription data-testid="colony-statement-description">Sent invoices, amounts not yet billed, and payments received.</DialogDescription>
        </DialogHeader>

        {!stmt ? <p className="text-sm text-muted-foreground py-4" data-testid="colony-statement-loading">Loading…</p> : (
          <div className="space-y-4">
            {/* Running totals */}
            <ColonyStatementTotals statement={stmt} overhead={!!colony.unassigned} />

            <div className="flex justify-end gap-2 flex-wrap">
              {!colony.unassigned && (
                <>
                  <Button
                    size="sm" variant="outline" disabled={stmtBusy === "pdf"} className="rounded-sm"
                    onClick={async () => {
                      setStmtBusy("pdf");
                      try {
                        const r = await api.get(`/mgmt/colonies/${colony.id}/statement/pdf`, { responseType: "blob" });
                        const url = URL.createObjectURL(r.data);
                        const a = document.createElement("a");
                        a.href = url; a.download = `statement-${(colony.name || "colony").replace(/\s+/g, "-")}.pdf`; a.click();
                        URL.revokeObjectURL(url);
                      } catch { toast.error("Could not build the statement PDF."); }
                      finally { setStmtBusy(null); }
                    }}
                    data-testid="colony-statement-pdf"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" /> {stmtBusy === "pdf" ? "Building…" : "PDF"}
                  </Button>
                  <Button
                    size="sm" variant="outline" disabled={stmtBusy === "email"} className="rounded-sm"
                    onClick={async () => {
                      setStmtBusy("email");
                      try {
                        const r = await api.post(`/mgmt/colonies/${colony.id}/statement/email`);
                        toast.success(`Statement emailed to ${r.data.to}.`);
                      } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not send the statement."); }
                      finally { setStmtBusy(null); }
                    }}
                    data-testid="colony-statement-email"
                  >
                    <Mail className="w-3.5 h-3.5 mr-1.5" /> {stmtBusy === "email" ? "Sending…" : "Email statement"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setInvOpen(true)} className="rounded-sm" data-testid="colony-create-invoice">
                    <FileText className="w-3.5 h-3.5 mr-1.5" /> Create invoice
                  </Button>
                </>
              )}
              <Button size="sm" onClick={() => setPayOpen(true)} className="rounded-sm" data-testid="colony-record-payment">
                <Wallet className="w-3.5 h-3.5 mr-1.5" /> Record payment received
              </Button>
            </div>

            {/* Invoices already generated for this colony */}
              <Section
                title="Invoices"
                empty={invTypeFilter === "all" ? "No invoices billed to this colony yet." : `No ${invTypeFilter} invoices for this colony.`}
                onActivate={() => onNavigate?.("expenses", "invoices")}
                testId="statement-shortcut-invoices"
                headerRight={
                  <span className="inline-flex gap-1" data-testid="colony-stmt-inv-type-filter">
                    {[["all", "All"], ["regular", "Regular"], ["payroll", "Payroll"]].map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setInvTypeFilter(val)}
                        className={`px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                          invTypeFilter === val ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-foreground"
                        }`}
                        data-testid={`colony-stmt-inv-filter-${val}`}
                      >
                        {label}
                      </button>
                    ))}
                  </span>
                }
              >
                {stmt.invoices.filter((inv) => invTypeFilter === "all" || (inv.invoice_type || "regular") === invTypeFilter).map((inv) => (
                  <div key={inv.id} className="px-3 py-2 border-b border-border last:border-b-0 text-sm flex justify-between items-center gap-2" data-testid={`colony-stmt-inv-${inv.id}`}>
                    <div className="truncate min-w-0 flex-1">
                      <span className="font-semibold mr-2">{inv.number}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold uppercase tracking-wider mr-2 ${(inv.invoice_type || "regular") === "payroll" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`} data-testid={`colony-stmt-inv-type-${inv.id}`}>
                        {(inv.invoice_type || "regular") === "payroll" ? "Payroll" : "Regular"}
                      </span>
                      <span className="text-muted-foreground mono-num text-xs mr-2">{inv.invoice_date}</span>
                      <InvoiceStatusChip status={inv.status} amountPaid={inv.amount_paid} balanceDue={inv.balance_due} />
                    </div>
                    <div className="mono-num shrink-0 text-right"><div className="font-semibold">Due {fmtMoney(inv.balance_due ?? inv.total)}</div>{Number(inv.amount_paid || 0) > 0 && <div className="text-[10px] text-emerald-700">Paid {fmtMoney(inv.amount_paid)}</div>}</div>
                  </div>
                ))}
              </Section>

            {/* Itemized: Expenses */}
            <Section title="Colony expenses" empty="No expenses tagged yet." onActivate={() => onNavigate?.("expenses", "expenses")} testId="statement-shortcut-expenses">
              {stmt.expenses.map((e) => (
                <div key={e.id} className="px-3 py-2 border-b border-border last:border-b-0 text-sm" data-testid={`colony-stmt-exp-${e.id}`}>
                  <div className="flex justify-between gap-2">
                    <div className="truncate min-w-0 flex-1">
                      <span className="text-muted-foreground mono-num text-xs mr-2">{e.date}</span>{e.category}{e.vendor ? ` · ${e.vendor}` : ""}
                      {!colony.unassigned && <ChargeBillingBadge status={e.billing_status} id={`colony-stmt-exp-invoiced-${e.id}`} />}
                    </div>
                    <div className="mono-num font-semibold shrink-0">{fmtMoney(e.amount)}</div>
                  </div>
                  {(e.notes || e.payment_method) && (
                    <div className="text-xs text-muted-foreground">{[e.notes, e.payment_method].filter(Boolean).join(" · ")}</div>
                  )}
                </div>
              ))}
            </Section>

            <Section title="Payroll billing" empty="No payroll billing charges yet." onActivate={() => onNavigate?.("payroll", "billing")} testId="statement-shortcut-payroll">
              {(stmt.payroll_billing || []).map((charge) => (
                <div key={charge.id} className="px-3 py-2 border-b border-border last:border-b-0 text-sm" data-testid={`colony-stmt-payroll-billing-${charge.id}`}>
                  <div className="flex justify-between">
                    <div className="min-w-0 flex-1 flex items-center gap-2">
                      <div className="truncate min-w-0"><span className="text-muted-foreground mono-num text-xs mr-2">{charge.period_start} → {charge.period_end}</span>{charge.worker_name || "Payroll services"}</div>
                      <ChargeBillingBadge status={charge.billing_status} id={`colony-stmt-payroll-billing-status-${charge.id}`} />
                    </div>
                    <div className="mono-num font-semibold shrink-0">{fmtMoney(charge.amount)}</div>
                  </div>
                </div>
              ))}
            </Section>

            {colony.unassigned && (
              <Section title="Unassigned payroll overhead" empty="No unassigned payroll.">
                {(stmt.payroll || []).map((p) => (
                  <div key={p.id} className="px-3 py-2 border-b border-border last:border-b-0 text-sm" data-testid={`colony-stmt-pay-${p.id}`}>
                    <div className="flex justify-between"><div>{p.period_start} → {p.period_end} · {p.worker_name}</div><div className="mono-num font-semibold">{fmtMoney(p.gross_pay)}</div></div>
                  </div>
                ))}
              </Section>
            )}

            {/* Payments received */}
            <Section title="Payments received" empty="No payments recorded yet." onActivate={() => onNavigate?.("expenses", "payments")} testId="statement-shortcut-payments">
              {stmt.payments_received.map((r) => {
                const linkedInv = r.invoice_id ? (stmt.invoices || []).find((inv) => inv.id === r.invoice_id) : null;
                return (
                <div key={r.id} className="px-3 py-2 border-b border-border last:border-b-0 text-sm flex justify-between items-center" data-testid={`colony-stmt-recv-${r.id}`}>
                  <div className="min-w-0">
                    <div className="mono-num font-semibold text-success">
                      {fmtMoney(r.amount)}
                      {linkedInv && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm bg-sky-100 text-sky-800 font-semibold uppercase tracking-wider" data-testid={`colony-stmt-recv-invoice-${r.id}`}>
                          → {linkedInv.number}{(linkedInv.invoice_type || "regular") === "payroll" ? " (payroll)" : ""}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {[r.paid_at, r.payment_method && (r.payment_reference ? `${r.payment_method} · ${r.payment_method.toLowerCase().includes("check") ? "#" : ""}${r.payment_reference}` : r.payment_method), r.note].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive" onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); removePayment(r.id); }} data-testid={`colony-stmt-recv-remove-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                );
              })}
            </Section>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>

        <ColonyReceivePaymentDialog
          open={payOpen}
          onOpenChange={setPayOpen}
          colony={colony}
          invoices={stmt?.invoices || []}
          onSaved={() => { setPayOpen(false); load(true); onChanged(); }}
        />
        <CreateInvoiceDialog
          open={invOpen}
          onOpenChange={setInvOpen}
          presetColony={colony}
          allowAttachments={String(colony.ledger || stmt?.colony?.ledger || "").startsWith("mgmt_")}
          onCreated={() => { load(true); onChanged(); }}
        />
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, empty, children, onActivate, testId, headerRight }) {
  const [armed, setArmed] = useState(false);
  const lastPressRef = React.useRef(0);
  const timerRef = React.useRef(null);
  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const activate = (event) => {
    if (!onActivate) return;
    if (event.detail === 0) { onActivate(); return; }
    const now = Date.now();
    if (now - lastPressRef.current < 650) {
      window.clearTimeout(timerRef.current);
      lastPressRef.current = 0;
      setArmed(false);
      onActivate();
      return;
    }
    lastPressRef.current = now;
    setArmed(true);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setArmed(false), 700);
  };

  const items = React.Children.toArray(children);
  return (
    <div onClick={onActivate ? activate : undefined} onKeyDown={onActivate ? (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onActivate(); } } : undefined} role={onActivate ? "button" : undefined} tabIndex={onActivate ? 0 : undefined} aria-label={onActivate ? `Open ${title}` : undefined} className={`border rounded-sm transition-[border-color,background-color] ${onActivate ? "cursor-pointer touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" : ""} ${armed ? "border-primary bg-primary/5" : "border-border"}`} data-testid={testId}>
      <div className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-secondary/30 flex items-center justify-between gap-2">
        <span>{title}</span>
        {headerRight && <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>{headerRight}</span>}
      </div>
      {items.length === 0 ? <div className="px-3 py-4 text-sm text-muted-foreground text-center">{empty}</div> : <div>{items}</div>}
    </div>
  );
}

function ColonyReceivePaymentDialog({ open, onOpenChange, colony, onSaved, invoices = [] }) {
  const [amount, setAmount] = useState("");
  const [paidAt, setPaidAt] = useState(todayISO());
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [saving, setSaving] = useState(false);
  const prefillRef = React.useRef("");
  // Fully paid invoices cannot accept more money.
  const pickable = useMemo(() => {
    const rows = invoices.filter((invoice) => Number(invoice.balance_due ?? invoice.total) > 0.005);
    rows.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return rows;
  }, [invoices]);

  useEffect(() => {
    if (open) {
      setAmount(""); setPaidAt(todayISO()); setMethod(""); setReference(""); setNote(""); setInvoiceId("");
      prefillRef.current = "";
    }
  }, [open]);

  const pickInvoice = (v) => {
    const id = v === "__none__" ? "" : v;
    setInvoiceId(id);
    const inv = pickable.find((x) => x.id === id);
    // Prefill the remaining balance, but never clobber an amount the user typed.
    if (inv && (!amount || amount === prefillRef.current)) {
      const val = String(inv.balance_due ?? inv.total ?? "");
      setAmount(val);
      prefillRef.current = val;
    }
  };

  const mLower = (method || "").toLowerCase();
  const refSpec = (() => {
    if (mLower.includes("check")) return { show: true, label: "Check #", placeholder: "1042", maxLen: 10, digitsOnly: true, testId: "colony-pay-check-number" };
    if (mLower.includes("zelle")) return { show: true, label: "Zelle transaction #", placeholder: "e.g. ABC123", maxLen: 32, testId: "colony-pay-zelle-ref" };
    if (mLower.includes("ach") || mLower.includes("bank")) return { show: true, label: "ACH / confirmation #", placeholder: "TRN12345", maxLen: 32, testId: "colony-pay-ach-ref" };
    return { show: false };
  })();

  const amtNum = parseFloat(amount);
  const invalid = !(amtNum > 0);

  const submit = async () => {
    if (invalid) return toast.error("Enter an amount greater than zero.");
    const selectedInvoice = invoices.find((invoice) => invoice.id === invoiceId);
    const selectedBalance = Number(selectedInvoice?.balance_due ?? selectedInvoice?.total ?? 0);
    if (selectedInvoice && amtNum > selectedBalance + 0.005) return toast.error(`Payment cannot exceed the invoice's remaining ${fmtMoney(selectedBalance)}.`);
    setSaving(true);
    try {
      await api.post(`/mgmt/colonies/${colony.id}/payments`, {
        amount: amtNum,
        paid_at: paidAt,
        payment_method: method || "",
        payment_reference: refSpec.show ? reference : "",
        note: note.trim(),
        invoice_id: invoiceId || null,
      });
      toast.success("Payment recorded");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="colony-pay-dialog">
        <DialogHeader>
          <DialogTitle>Record payment received · {colony?.name}</DialogTitle>
          <DialogDescription>Money coming FROM the colony back to you.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {pickable.length > 0 && (
            <div>
              <Label>Apply towards (optional)</Label>
              <Select value={invoiceId || "__none__"} onValueChange={pickInvoice}>
                <SelectTrigger data-testid="colony-pay-invoice"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— general payment (no specific invoice) —</SelectItem>
                  {pickable.map((inv) => (
                    <SelectItem key={inv.id} value={inv.id} data-testid={`colony-pay-invoice-opt-${inv.id}`}>
                      {inv.number} · {(inv.invoice_type || "regular") === "payroll" ? "Payroll" : "Regular"} · Due {fmtMoney(inv.balance_due ?? inv.total)}{Number(inv.balance_due ?? inv.total) <= 0.005 ? " · already paid" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {invoiceId && (
                <p className="text-[11px] text-muted-foreground mt-1" data-testid="colony-pay-invoice-hint">
                  The invoice flips to Paid automatically once its payments cover the full total.
                </p>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Amount $</Label>
              <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus data-testid="colony-pay-amount" />
            </div>
            <div>
              <Label>Date received</Label>
              <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid="colony-pay-date" />
            </div>
          </div>
          <div className={refSpec.show ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : ""}>
            <div>
              <Label>Payment method</Label>
              <Select value={method || "__none__"} onValueChange={(v) => { setMethod(v === "__none__" ? "" : v); setReference(""); }}>
                <SelectTrigger data-testid="colony-pay-method"><SelectValue placeholder="Pick a method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— not specified —</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Check">Check</SelectItem>
                  <SelectItem value="Zelle">Zelle</SelectItem>
                  <SelectItem value="ACH">ACH</SelectItem>
                  <SelectItem value="Bank transfer">Bank transfer</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {refSpec.show && (
              <div>
                <Label>{refSpec.label}</Label>
                <Input
                  inputMode={refSpec.digitsOnly ? "numeric" : "text"}
                  pattern={refSpec.digitsOnly ? "[0-9]*" : undefined}
                  maxLength={refSpec.maxLen}
                  placeholder={refSpec.placeholder}
                  value={reference}
                  onChange={(e) => setReference(refSpec.digitsOnly ? e.target.value.replace(/\D/g, "").slice(0, refSpec.maxLen) : e.target.value.slice(0, refSpec.maxLen))}
                  data-testid={refSpec.testId}
                />
              </div>
            )}
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. for August work" data-testid="colony-pay-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || invalid} data-testid="colony-pay-save">
            {saving ? "Saving…" : `Record ${amtNum > 0 ? fmtMoney(amtNum) : "payment"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Management USA NJ — Expenses page with left-side sub-tabs so the admin
// can flip between raw Expenses, the Invoices generated from them, and
// the connected credit-card + colony-mapping panel (Plaid).
function ExpensesInvoicesSplit(props) {
  const isMgmt = props.ledger === "mgmt_usa_nj";
  const showCardSubtab = isMgmt && !props.hideCards;
  const validSubs = useMemo(
    () => showCardSubtab ? ["expenses", "invoices", "payments", "cards"] : ["expenses", "invoices", "payments"],
    [showCardSubtab],
  );
  const [invoiceTarget, setInvoiceTarget] = useState(null);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [sub, setSubRaw] = useState(() => {
    if (props.focusExpenseId) return "expenses";
    try {
      const saved = localStorage.getItem(`finance_expense_subtab_${props.ledger}`);
      return validSubs.includes(saved) ? saved : "expenses";
    } catch { return "expenses"; }
  });
  const setSub = (k) => {
    setSubRaw(k);
    if (k !== "invoices") setInvoiceTarget(null);
    if (k !== "payments") setPaymentTarget(null);
    try { localStorage.setItem(`finance_expense_subtab_${props.ledger}`, k); } catch { /* private mode */ }
  };
  const openInvoiceFromPayment = (payment) => {
    setInvoiceTarget({ id: payment.invoice_id, number: payment.invoice_number, requestId: Date.now() });
    setSub("invoices");
  };
  const openPaymentsFromInvoice = (invoice) => {
    setPaymentTarget({ invoiceId: invoice.id, invoiceNumber: invoice.number, requestId: Date.now() });
    setSub("payments");
  };
  useEffect(() => {
    const requested = props.navigationRequest?.target === "expenses" ? props.navigationRequest.section : null;
    if (requested && validSubs.includes(requested)) {
      setSubRaw(requested);
      try { localStorage.setItem(`finance_expense_subtab_${props.ledger}`, requested); } catch { /* private mode */ }
    }
  }, [props.navigationRequest, props.ledger, validSubs]);
  const tabs = [
    { k: "expenses", label: "Expenses", icon: Receipt },
    { k: "invoices", label: "Invoices", icon: FileText },
    { k: "payments", label: "Received payment", icon: Wallet },
    ...(showCardSubtab ? [{ k: "cards", label: "Cards", icon: CreditCard }] : []),
  ];
  return (
    <div className="management-finance-split flex flex-col lg:flex-row gap-4">
      <div className="management-side-tabs grid grid-cols-2 lg:flex lg:flex-col gap-1 lg:w-40 flex-shrink-0" data-testid="expense-side-tabs">
        <span className="management-side-label" data-testid="expense-side-heading">Invoices &amp; expenses</span>
        {tabs.map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            onClick={() => setSub(k)}
            aria-current={sub === k ? "page" : undefined}
            className={`flex items-center gap-2 px-3 py-2 min-h-11 text-sm font-semibold rounded-sm border transition-colors text-left whitespace-nowrap flex-1 lg:flex-none ${sub === k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary/40"}`}
            data-testid={`expense-subtab-${k}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      <div className="flex-1 min-w-0">
        {sub === "expenses" && <Expenses {...props} />}
        {sub === "invoices" && <InvoicesPanel allowAttachments={isMgmt} focusRequest={invoiceTarget} dateFrom={props.dateFrom} dateTo={props.dateTo} onOpenPayments={openPaymentsFromInvoice} />}
        {sub === "payments" && <ReceivedPaymentsPanel dateFrom={props.dateFrom} dateTo={props.dateTo} onOpenInvoice={openInvoiceFromPayment} focusRequest={paymentTarget} />}
        {sub === "cards" && <PlaidCards />}
      </div>
    </div>
  );
}

function InvoiceStatusChip({ status, amountPaid = 0, balanceDue = null }) {
  const fullyPaid = Number(amountPaid || 0) > 0 && balanceDue !== null && Number(balanceDue) <= 0.005;
  const partial = Number(amountPaid || 0) > 0 && Number(balanceDue || 0) > 0.005;
  const displayStatus = fullyPaid ? "paid" : partial ? "partial" : status;
  const cls = displayStatus === "paid"
    ? "bg-emerald-100 text-emerald-800"
    : displayStatus === "sent"
      ? "bg-sky-100 text-sky-800"
      : displayStatus === "partial"
        ? "bg-amber-100 text-amber-800"
      : "bg-secondary text-muted-foreground";
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold uppercase tracking-wider ${cls}`}>{displayStatus}</span>;
}

// Invoice list for the Management USA NJ ledger: view line items, flip
// status, email to the colony contact, or delete (releases the expenses).
function InvoicesPanel({ focusRequest, dateFrom, dateTo, onOpenPayments, allowAttachments = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [colonyFilter, setColonyFilter] = useState("all");
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState("all");
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [fixEmailFor, setFixEmailFor] = useState(null); // invoice whose colony lacks a billing email
  const [payFor, setPayFor] = useState(null); // invoice being paid off
  const [managePaymentsFor, setManagePaymentsFor] = useState(null);
  const [autoOpen, setAutoOpen] = useState(false); // monthly auto-invoice settings
  const [receiptView, setReceiptView] = useState(null); // photos array for the lightbox
  const [editReasonFor, setEditReasonFor] = useState(null);
  const [editRequest, setEditRequest] = useState(null);
  const [resendAfterEdit, setResendAfterEdit] = useState(null);

  const [lineAi, setLineAi] = useState({}); // expense_id -> "loading" | { ai_audit, imgs }
  // receiptOnly: unscanned lines skip the AI box and open the receipt straight away.
  const toggleLineAi = useCallback(async (eid, receiptOnly = false) => {
    if (!receiptOnly && lineAi[eid] && lineAi[eid] !== "loading") return setLineAi((p) => ({ ...p, [eid]: undefined }));
    if (!receiptOnly) setLineAi((p) => ({ ...p, [eid]: "loading" }));
    try {
      const { data } = await api.get(`/expenses/${eid}`);
      const imgs = [...(data.receipts || []), ...(data.receipt_data_url ? [data.receipt_data_url] : [])].filter(Boolean);
      if (receiptOnly) return imgs.length ? setReceiptView(imgs) : toast.message("No receipt was attached to this line.");
      setLineAi((p) => ({ ...p, [eid]: { ai_audit: data.ai_audit || null, imgs } }));
    } catch (e) {
      setLineAi((p) => ({ ...p, [eid]: undefined }));
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  }, [lineAi]);

  const load = useCallback(() => {
    api.get("/mgmt/invoices")
      .then((r) => setRows(r.data?.invoices || []))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!focusRequest?.id || loading) return;
    const target = rows.find((row) => row.id === focusRequest.id);
    if (!target) {
      toast.error(`Invoice ${focusRequest.number || ""} is no longer available.`.trim());
      return;
    }
    setInvoiceTypeFilter("all");
    setColonyFilter("all");
    setInvoiceSearch("");
    setExpanded(target.id);
    window.setTimeout(() => {
      document.querySelector(`[data-testid="invoice-row-${target.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  }, [focusRequest, loading, rows]);

  const dateRows = useMemo(
    () => rows.filter((row) => valueInDateRange(row.invoice_date || row.created_at, dateFrom, dateTo)),
    [rows, dateFrom, dateTo],
  );
  const colonies = useMemo(() => {
    const m = new Map();
    dateRows.forEach((r) => { if (r.colony_id) m.set(r.colony_id, r.colony_name || "Colony"); });
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [dateRows]);
  const typeRows = (invoiceTypeFilter === "all" || invoiceTypeFilter === "separated") ? dateRows : dateRows.filter((row) => (row.invoice_type || "regular") === invoiceTypeFilter);
  const colonyRows = colonyFilter === "all" ? typeRows : typeRows.filter((r) => r.colony_id === colonyFilter);
  const visible = useMemo(() => {
    const term = invoiceSearch.trim().toLowerCase();
    const filtered = !term ? [...colonyRows] : colonyRows.filter((invoice) => {
      const total = Number(invoice.total || 0);
      const lineText = (invoice.line_items || []).flatMap((line) => [line.category, line.vendor, line.description]).filter(Boolean).join(" ");
      const haystack = [
        invoice.number,
        invoice.name,
        invoice.title,
        invoice.description,
        invoice.notes,
        invoice.colony_name,
        invoice.status,
        invoice.invoice_type || "regular",
        invoice.invoice_date,
        String(total),
        total.toFixed(2),
        fmtMoney(total),
        lineText,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(term);
    });
    const valueForSort = (invoice) => {
      if (sortKey === "date") return invoice.invoice_date || invoice.created_at || "";
      if (sortKey === "colony") return invoice.colony_name || "";
      if (sortKey === "amount") return Number(invoice.total || 0);
      if (sortKey === "balance") return Number(invoice.balance_due ?? invoice.total ?? 0);
      if (sortKey === "status") {
        const balance = Number(invoice.balance_due ?? invoice.total ?? 0);
        if (Number(invoice.total || 0) > 0 && balance <= 0.005) return "paid";
        if (Number(invoice.amount_paid || 0) > 0) return "partially paid";
        return invoice.status || "draft";
      }
      return invoice.number || "";
    };
    const direction = sortDir === "asc" ? 1 : -1;
    return filtered.sort((a, b) => {
      const aValue = valueForSort(a);
      const bValue = valueForSort(b);
      if (typeof aValue === "number" && typeof bValue === "number") return (aValue - bValue) * direction;
      return String(aValue || "").localeCompare(String(bValue || ""), undefined, { numeric: true, sensitivity: "base" }) * direction;
    });
  }, [colonyRows, invoiceSearch, sortKey, sortDir]);

  const toggleInvoiceSort = (key) => {
    if (sortKey === key) {
      setSortDir((direction) => direction === "asc" ? "desc" : "asc");
      return;
    }
    setSortKey(key);
    setSortDir(["number", "date", "amount", "balance"].includes(key) ? "desc" : "asc");
  };

  const setStatus = async (inv, status, extra = {}) => {
    setBusyId(inv.id);
    try {
      await api.patch(`/mgmt/invoices/${inv.id}/status`, { status, ...extra });
      toast.success(status === "paid" ? `${inv.number} paid off — payment recorded on the colony statement.` : `${inv.number} is back to ${status}.`);
      load();
      return true;
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); return false; }
    finally { setBusyId(null); }
  };

  const recordInvoicePayment = async (inv, payment) => {
    setBusyId(inv.id);
    try {
      await api.post(`/mgmt/colonies/${inv.colony_id}/payments`, {
        amount: Number(payment.amount),
        paid_at: payment.paid_at,
        payment_method: payment.payment_method,
        payment_reference: payment.payment_reference,
        note: payment.note,
        invoice_id: inv.id,
      });
      const remaining = Math.max(0, Number(inv.balance_due ?? inv.total) - Number(payment.amount));
      toast.success(remaining <= 0.005 ? `${inv.number} is paid in full.` : `${fmtMoney(payment.amount)} recorded. ${fmtMoney(remaining)} still due.`);
      setPayFor(null);
      load();
    } catch (error) { toast.error(formatErr(error.response?.data?.detail) || error.message); }
    finally { setBusyId(null); }
  };

  const emailInvoice = async (inv) => {
    setBusyId(inv.id);
    try {
      const r = await api.post(`/mgmt/invoices/${inv.id}/email`);
      toast.success(`Invoice ${inv.number} emailed to ${r.data.to}.`);
      load();
      return { sent: true };
    } catch (e) {
      const msg = formatErr(e.response?.data?.detail) || e.message;
      if ((msg || "").toLowerCase().includes("no contact email")) {
        setResendAfterEdit(null);
        setFixEmailFor(inv);  // guide the admin straight to adding one
        return { sent: false, needsEmail: true };
      } else {
        toast.error(msg);
      }
      return { sent: false, error: msg };
    }
    finally { setBusyId(null); }
  };

  const downloadPdf = async (inv) => {
    setBusyId(inv.id);
    try {
      const r = await api.get(`/mgmt/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.number || "invoice"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download the PDF."); }
    finally { setBusyId(null); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.number} (${fmtMoney(inv.total)})? Its source charges will return to the open billing queue.`)) return;
    setBusyId(inv.id);
    try {
      await api.delete(`/mgmt/invoices/${inv.id}`);
      toast.success(`Invoice ${inv.number} deleted.`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusyId(null); }
  };

  const requestEdit = (invoice) => {
    if (invoice.sent_at || ["sent", "paid"].includes(invoice.status)) {
      setEditReasonFor(invoice);
      return;
    }
    setEditRequest({ invoice, reason: "" });
  };

  return (
    <div className="management-section" data-testid="finance-invoices">
      <div className="management-section-header">
        <div>
        <div className="flex flex-wrap items-center gap-3"><h1 className="management-title font-display" data-testid="management-invoices-heading">Invoices</h1><TutorialButton section="invoices" compact /></div>
        <p className="hidden" data-testid="invoice-search-summary">
          {visible.length} invoice{visible.length === 1 ? "" : "s"} · invoiced <span className="font-semibold text-foreground mono-num">{fmtMoney(visible.reduce((s, r) => s + Number(r.total || 0), 0))}</span> · still due <span className="font-semibold text-amber-700 mono-num">{fmtMoney(visible.reduce((s, r) => s + Number(r.balance_due ?? r.total ?? 0), 0))}</span>
        </p>
        </div>
        <div className="management-invoice-controls flex gap-2 flex-wrap items-center mobile-full">
          {colonies.length > 1 && (
            <Select value={colonyFilter} onValueChange={setColonyFilter}>
              <SelectTrigger className="h-11 sm:h-9 w-full sm:w-44 rounded-sm" data-testid="invoice-colony-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="invoice-colony-option-all">All colonies</SelectItem>
                {colonies.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`invoice-colony-option-${c.id}`}><ColonyDotName id={c.id} name={c.name} /></SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={() => setAutoOpen(true)} className="rounded-sm flex-1 sm:flex-none" data-testid="invoice-auto-settings-btn">
            <Calendar className="w-4 h-4 mr-2" /> Auto-invoice
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="rounded-sm flex-1 sm:flex-none" data-testid="invoice-create-btn">
            <Plus className="w-4 h-4 mr-2" /> Create invoice
          </Button>
        </div>
      </div>
      <FinanceSummaryCards testId="invoice-summary-cards" compactFirst loading={loading} items={[
        { key: "count", label: "Invoices", value: visible.length, icon: FileText },
        { key: "invoiced", label: "Total invoiced", value: fmtMoney(visible.reduce((sum, invoice) => sum + Number(invoice.total || 0), 0)), icon: Receipt, tone: "sky" },
        { key: "due", label: "Balance due", value: fmtMoney(visible.reduce((sum, invoice) => sum + Number(invoice.balance_due ?? invoice.total ?? 0), 0)), icon: Wallet, tone: "rose" },
      ]} />
      <nav className="management-subtabs flex gap-5 border-b border-border" aria-label="Invoice types" data-testid="invoice-type-tabs">
        {[["all", "All"], ["regular", "Regular"], ["payroll", "Payroll"], ["separated", "Separated"]].map(([value, label]) => (
          <button key={value} type="button" onClick={() => setInvoiceTypeFilter(value)} aria-current={invoiceTypeFilter === value ? "page" : undefined}
            className={`h-10 border-b-2 text-sm font-semibold transition-colors ${invoiceTypeFilter === value ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`invoice-type-${value}`}>{label}</button>
        ))}
      </nav>
      <ExpandingFinanceSearch
        alwaysOpen
        value={invoiceSearch}
        onChange={setInvoiceSearch}
        placeholder="Search invoices by number, colony/name, description, or amount"
        testId="invoice-search"
        ariaLabel="Search invoices"
      />

      <div className="management-list border border-border rounded-sm overflow-hidden" data-testid="invoice-list-surface">
        <div className="management-table-heading management-invoice-heading grid grid-cols-2 sm:grid-cols-3 2xl:grid-cols-[minmax(8rem,1fr)_6.5rem_minmax(9rem,1fr)_6rem_6.5rem_7rem_19rem] gap-x-2 2xl:gap-x-3 items-center px-4 py-2 border-b border-border bg-secondary/40 text-[10px] sm:text-xs font-mono uppercase tracking-wider text-muted-foreground" data-testid="invoice-sort-header">
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="number" onToggle={toggleInvoiceSort} className="whitespace-nowrap" testId="invoice-sort-number">Invoice number</SortableHeader>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="date" onToggle={toggleInvoiceSort} className="whitespace-nowrap" testId="invoice-sort-date">Date</SortableHeader>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="colony" onToggle={toggleInvoiceSort} className="whitespace-nowrap" testId="invoice-sort-colony">Colony</SortableHeader>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="status" onToggle={toggleInvoiceSort} className="whitespace-nowrap" testId="invoice-sort-status">Status</SortableHeader>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="amount" onToggle={toggleInvoiceSort} className="management-invoice-money-heading whitespace-nowrap" testId="invoice-sort-amount">Amount</SortableHeader>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="balance" onToggle={toggleInvoiceSort} className="management-invoice-money-heading management-invoice-balance-heading whitespace-nowrap" testId="invoice-sort-balance">Balance due</SortableHeader>
          <span className="hidden 2xl:block text-right" aria-hidden="true">Actions</span>
        </div>
        {loading && <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="invoice-list-loading">Loading…</div>}
        {!loading && visible.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground break-words" data-testid="invoice-empty-state">
            {invoiceSearch.trim()
              ? `No invoices match “${invoiceSearch.trim()}”.`
              : `No ${(invoiceTypeFilter === "all" || invoiceTypeFilter === "separated") ? "" : `${invoiceTypeFilter} `}invoices yet.`}
          </div>
        )}
        {(() => {
        const renderRow = (inv) => {
          const isOpen = expanded === inv.id;
          const balanceDue = Number(inv.balance_due ?? inv.total);
          const amountPaid = Number(inv.amount_paid || 0);
          return (
            <div key={inv.id} className="management-invoice-row border-b border-border last:border-b-0" data-testid={`invoice-row-${inv.id}`}>
              <div
                className="management-invoice-clickable px-3 sm:px-4 py-3 flex items-center gap-3 flex-wrap 2xl:grid 2xl:grid-cols-[minmax(8rem,1fr)_6.5rem_minmax(9rem,1fr)_6rem_6.5rem_7rem_19rem] 2xl:gap-x-3 cursor-pointer hover:bg-secondary/30"
                onClick={() => setExpanded(isOpen ? null : inv.id)}
                data-testid={`invoice-clickable-row-${inv.id}`}
              >
                <button
                  type="button"
                  onClick={(event) => { event.stopPropagation(); setExpanded(isOpen ? null : inv.id); }}
                  className="management-invoice-expand flex items-center gap-3 flex-1 min-w-0 text-left 2xl:w-full"
                  aria-expanded={isOpen}
                  data-testid={`invoice-expand-${inv.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                      {inv.number}
                      <span className="2xl:hidden"><InvoiceStatusChip status={inv.status} amountPaid={inv.amount_paid} balanceDue={inv.balance_due} /></span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold uppercase tracking-wider ${(inv.invoice_type || "regular") === "payroll" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`} data-testid={`invoice-type-badge-${inv.id}`}>
                        {(inv.invoice_type || "regular") === "payroll" ? "Payroll" : "Regular"}
                      </span>
                      {Number(inv.markup_pct) > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 font-semibold">+{inv.markup_pct}% markup</span>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate 2xl:hidden">
                      <ColonyDotName id={inv.colony_id} name={inv.colony_name} /> · {inv.invoice_date}{inv.through_date ? ` · ${(inv.invoice_type || "regular") === "payroll" ? "payroll" : "expenses"} through ${inv.through_date}` : ""} · {(inv.line_items || []).length} line{(inv.line_items || []).length === 1 ? "" : "s"}
                    </div>
                  </div>
                </button>
                <div className="hidden 2xl:block text-xs mono-num" data-testid={`invoice-date-${inv.id}`}>{inv.invoice_date || "—"}</div>
                <div className="hidden 2xl:block min-w-0 text-xs" data-testid={`invoice-colony-${inv.id}`}>
                  <div className="truncate"><ColonyDotName id={inv.colony_id} name={inv.colony_name} /></div>
                  <div className="truncate text-[10px] text-muted-foreground">{(inv.line_items || []).length} line{(inv.line_items || []).length === 1 ? "" : "s"}</div>
                </div>
                <div className="hidden 2xl:flex items-center" data-testid={`invoice-status-${inv.id}`}><InvoiceStatusChip status={inv.status} amountPaid={inv.amount_paid} balanceDue={inv.balance_due} /></div>
                <div className="hidden 2xl:block mono-num text-right text-sm font-semibold" data-testid={`invoice-amount-${inv.id}`}>{fmtMoney(inv.total)}</div>
                <div className="management-invoice-due mono-num min-w-0 overflow-hidden text-right text-sm ml-auto sm:ml-0 2xl:ml-0 2xl:w-full" data-testid={`invoice-due-${inv.id}`}>
                  <div className="font-bold"><span className="2xl:hidden">Due </span>{fmtMoney(balanceDue)}</div>
                  {amountPaid > 0 && (
                    <button
                      type="button"
                      onClick={(event) => { event.stopPropagation(); onOpenPayments(inv); }}
                      className="text-[10px] leading-tight whitespace-normal text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                      title={`Open received payment${amountPaid === 1 ? "" : "s"} for ${inv.number}`}
                      data-testid={`invoice-open-received-payment-${inv.id}`}
                    >
                      Paid {fmtMoney(amountPaid)} of {fmtMoney(inv.total)}
                    </button>
                  )}
                </div>
                <div
                  className="management-invoice-actions flex sm:grid sm:grid-cols-[8rem_2.5rem_8.5rem] items-center gap-1 sm:gap-2 w-full sm:w-auto 2xl:w-[19rem] justify-end flex-wrap"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <div className="flex justify-end sm:justify-center min-h-8">
                    {balanceDue > 0.005 ? (
                      <Button size="sm" variant="outline" onClick={() => setPayFor(inv)} disabled={busyId === inv.id} className="rounded-sm h-8 text-xs" data-testid={`invoice-record-payment-${inv.id}`}>
                        <Wallet className="w-3.5 h-3.5 mr-1" /> Record payment
                      </Button>
                    ) : <span className="self-center text-xs font-semibold text-emerald-700 px-2" data-testid={`invoice-paid-in-full-${inv.id}`}>Paid in full</span>}
                  </div>
                  <div className="flex justify-end sm:justify-center min-h-8">
                    {amountPaid > 0 && (
                      <Button size="icon" variant="ghost" onClick={() => setManagePaymentsFor(inv)} title="Undo payment" aria-label={`Undo payment for ${inv.number}`} className="rounded-sm h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`invoice-undo-payment-${inv.id}`}><Undo2 className="w-3.5 h-3.5" /></Button>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => requestEdit(inv)} disabled={busyId === inv.id} title={`Edit ${inv.number}`} aria-label={`Edit ${inv.number}`} className="rounded-sm h-8 w-8" data-testid={`invoice-edit-${inv.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => downloadPdf(inv)} disabled={busyId === inv.id} title="Download PDF" aria-label={`Download ${inv.number} PDF`} className="rounded-sm h-8 w-8" data-testid={`invoice-pdf-${inv.id}`}>
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => emailInvoice(inv)} disabled={busyId === inv.id} title="Email to colony contact (PDF attached — moves the invoice to Sent)" aria-label={`Email ${inv.number}`} className="rounded-sm h-8 w-8" data-testid={`invoice-email-${inv.id}`}>
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(inv)} disabled={busyId === inv.id} title={`Delete ${inv.number}`} aria-label={`Delete ${inv.number}`} className="rounded-sm h-8 w-8 text-destructive" data-testid={`invoice-delete-${inv.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
              {isOpen && (
                <div className="px-4 pb-3 bg-secondary/20" data-testid={`invoice-lines-${inv.id}`}>
                  <div className="border border-border rounded-sm bg-background">
                    {(inv.line_items || []).map((li, i) => (
                      <React.Fragment key={i}>
                      <div className="px-3 py-2 flex items-start sm:items-center justify-between gap-3 text-sm border-b border-border last:border-b-0">
                        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                          <span className="text-muted-foreground mono-num text-xs">{li.date}</span>
                          {li.category && (
                            <span className="text-[10px] uppercase tracking-wider bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded-sm font-semibold shrink-0">
                              {li.category}
                            </span>
                          )}
                          <span className="truncate">
                            {li.description}{li.vendor ? ` · ${li.vendor}` : ""}
                          </span>
                          {li.expense_id && !li.ai_scanned && li.has_receipt && (
                            <button
                              type="button"
                              onClick={() => toggleLineAi(li.expense_id, true)}
                              className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-primary hover:text-primary/80 underline underline-offset-2 shrink-0"
                              title="Open the attached receipt"
                              data-testid={`invoice-line-receipt-${inv.id}-${i}`}
                            >
                              <FileText className="w-3 h-3" /> Receipt
                            </button>
                          )}
                          {li.expense_id && li.ai_scanned && (
                            <button
                              type="button"
                              onClick={() => toggleLineAi(li.expense_id)}
                              className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-violet-700 hover:text-violet-900 underline underline-offset-2 shrink-0"
                              title="See what the AI read off this receipt, and open the receipt"
                              data-testid={`invoice-line-ai-${inv.id}-${i}`}
                            >
                              <Sparkles className="w-3 h-3" /> {lineAi[li.expense_id] === "loading" ? "…" : "AI details"}
                            </button>
                          )}
                        </div>
                        <div className="mono-num shrink-0">{fmtMoney(li.amount)}</div>
                      </div>
                      {lineAi[li.expense_id] && lineAi[li.expense_id] !== "loading" && (
                        <div className="px-3 pb-2">
                          <AiBreakdownPanel
                            audit={lineAi[li.expense_id].ai_audit}
                            hasReceipt={lineAi[li.expense_id].imgs.length > 0}
                            onViewReceipt={() => setReceiptView(lineAi[li.expense_id].imgs)}
                            testId={`invoice-line-ai-panel-${inv.id}-${i}`}
                          />
                        </div>
                      )}
                      </React.Fragment>
                    ))}
                    <div className="px-3 py-1.5 flex justify-between text-sm border-t border-border">
                      <span className="text-muted-foreground">Subtotal</span><span className="mono-num">{fmtMoney(inv.subtotal)}</span>
                    </div>
                    {Number(inv.markup_amount) > 0 && (
                      <div className="px-3 py-1.5 flex justify-between text-sm">
                        <span className="text-muted-foreground">Markup ({inv.markup_pct}%) — hidden from the colony</span><span className="mono-num">{fmtMoney(inv.markup_amount)}</span>
                      </div>
                    )}
                    <div className="px-3 py-1.5 flex justify-between text-sm border-t-2 border-border">
                      <span>Invoice total</span><span className="mono-num">{fmtMoney(inv.total)}</span>
                    </div>
                    {Number(inv.amount_paid || 0) > 0 && (
                      <button
                        type="button"
                        onClick={() => onOpenPayments(inv)}
                        className="w-full px-3 py-1.5 flex justify-between text-sm text-emerald-700 hover:bg-emerald-50 transition-colors"
                        title={`Open received payments for ${inv.number}`}
                        data-testid={`invoice-open-received-payments-summary-${inv.id}`}
                      >
                        <span className="underline underline-offset-2">Payments received</span><span className="mono-num">−{fmtMoney(inv.amount_paid)}</span>
                      </button>
                    )}
                    <div className="px-3 py-1.5 flex justify-between text-sm font-bold border-t border-border"><span>Still unpaid</span><span className="mono-num" data-testid={`invoice-balance-due-${inv.id}`}>{fmtMoney(inv.balance_due ?? inv.total)}</span></div>
                  </div>
                  {inv.notes && <p className="text-xs text-muted-foreground mt-2">{inv.notes}</p>}
                  <InvoiceEditHistory invoice={inv} />
                  {allowAttachments && <InvoiceAttachments key={inv.id} invoiceId={inv.id} refreshKey={inv.attachment_version || 0} />}
                </div>
              )}
            </div>
          );
        };
        if (invoiceTypeFilter !== "separated") return visible.map(renderRow);
        // "Separated" — both types on screen at once, each with its own
        // count + running total.
        return ["regular", "payroll"].map((type) => {
          const group = visible.filter((row) => (row.invoice_type || "regular") === type);
          return (
            <div key={type} data-testid={`invoice-group-${type}`}>
              <div className="px-3 sm:px-4 py-2 bg-secondary/60 border-b border-border flex items-center justify-between text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                <span>{type === "payroll" ? "Payroll invoices" : "Regular invoices"}</span>
                <span className="mono-num" data-testid={`invoice-group-total-${type}`}>
                  {group.length} · due {fmtMoney(group.reduce((s, r) => s + Number(r.balance_due ?? r.total ?? 0), 0))}
                </span>
              </div>
              {group.length === 0
                ? <div className="px-4 py-4 text-center text-sm text-muted-foreground border-b border-border" data-testid={`invoice-group-empty-${type}`}>No {type} invoices.</div>
                : group.map(renderRow)}
            </div>
          );
        });
        })()}
      </div>

      <CreateInvoiceDialog allowAttachments={allowAttachments} open={createOpen} onOpenChange={setCreateOpen} onCreated={invoice => { load(); if (allowAttachments && invoice?.id) setExpanded(invoice.id); }} />
      <InvoicePaymentDialog
        invoice={payFor}
        onOpenChange={(v) => { if (!v) setPayFor(null); }}
        onConfirm={recordInvoicePayment}
      />
      <InvoicePaymentsDialog invoice={managePaymentsFor} onOpenChange={(open) => { if (!open) setManagePaymentsFor(null); }} onChanged={load} />
      <AutoInvoiceSettingsDialog open={autoOpen} onOpenChange={setAutoOpen} />
      <InvoiceEditReasonDialog
        invoice={editReasonFor}
        onOpenChange={(open) => { if (!open) setEditReasonFor(null); }}
        onConfirm={(reason) => { setEditRequest({ invoice: editReasonFor, reason }); setEditReasonFor(null); }}
      />
      <InvoiceEditDialog
        request={editRequest}
        allowAttachments={allowAttachments}
        onOpenChange={(open) => { if (!open) setEditRequest(null); }}
        onSaved={(updated, options = {}) => {
          const offerResend = !options.attachmentsPending && shouldOfferInvoiceResend(editRequest?.invoice, updated);
          setEditRequest(null);
          setExpanded(updated.id);
          if (offerResend) setResendAfterEdit(updated);
          load();
        }}
      />
      <InvoiceResendDialog invoice={resendAfterEdit} onOpenChange={(open) => { if (!open) setResendAfterEdit(null); }} onSend={emailInvoice} />
      <FixBillingEmailDialog
        invoice={fixEmailFor}
        onOpenChange={(v) => { if (!v) setFixEmailFor(null); }}
        onSaved={(inv) => { setFixEmailFor(null); emailInvoice(inv); }}
      />
      {receiptView && (
        <PhotoLightbox photos={receiptView} alt="receipt" onClose={() => setReceiptView(null)} />
      )}
    </div>
  );
}

// The colony had no billing email when the admin hit "Email invoice" —
// instead of a dead-end error, collect the email right here, save it on
// the colony, then retry sending.
function FixBillingEmailDialog({ invoice, onOpenChange, onSaved }) {
  const open = !!invoice;
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setEmail(""); }, [open]);

  const save = async () => {
    const em = email.trim();
    if (!em || !em.includes("@")) return toast.error("Enter a valid email address.");
    setSaving(true);
    try {
      const { data: colonies } = await api.get("/mgmt/colonies");
      const colony = (colonies || []).find((c) => c.id === invoice.colony_id);
      if (!colony) throw new Error("Colony not found — refresh and try again.");
      await api.put(`/mgmt/colonies/${colony.id}`, { ...colony, contact_email: em });
      toast.success(`Billing email saved for ${colony.name}.`);
      onSaved(invoice);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="fix-billing-email-dialog" closeTestId="fix-billing-email-close">
        <DialogHeader>
          <DialogTitle>Add billing email · {invoice?.colony_name}</DialogTitle>
          <DialogDescription>
            This colony has no billing email yet, so invoice {invoice?.number} can&apos;t be sent. Add one below — we&apos;ll save it on the colony and send the invoice right away.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label>Billing email</Label>
          <Input
            type="email"
            autoFocus
            placeholder="billing@colony.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            data-testid="fix-billing-email-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="fix-billing-email-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !email.trim()} className="rounded-sm" data-testid="fix-billing-email-save">
            {saving ? "Saving…" : "Save & send invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// "Create invoice" — roll open expenses, open payroll billing, or custom items
// into a draft without leaving the Invoices workflow. Used from the
// Invoices sub-tab AND the per-colony statement dialog.
function CreateInvoiceDialog({ open, onOpenChange, presetColony, onCreated, allowAttachments = false }) {
  const [colonies, setColonies] = useState([]);
  const [cid, setCid] = useState("");
  const [mode, setMode] = useState("expenses");
  const [through, setThrough] = useState(todayISO());
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [items, setItems] = useState([{ description: "", date: "", qty: "1", amount: "" }]);
  const [markup, setMarkup] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [payrollRows, setPayrollRows] = useState([]);
  const [payrollCharges, setPayrollCharges] = useState([]);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [createError, setCreateError] = useState("");
  const attachmentDraft = useInvoiceAttachmentDraft({ open, enabled: allowAttachments });

  useEffect(() => {
    if (!open) return;
    setThrough(todayISO()); setMarkup(""); setNotes("");
    setCreatedInvoice(null); setCreateError("");
    setMode("expenses"); setInvoiceDate(todayISO());
    setItems([{ description: "", date: "", qty: "1", amount: "" }]);
    setCid(presetColony?.id || "");
    if (!presetColony) api.get("/mgmt/colonies").then((r) => setColonies(r.data || [])).catch(() => {});
    setPayrollLoading(true);
    Promise.all([
      api.get(withLedger("/payroll?date_from=2020-01-01&date_to=2099-12-31", "mgmt_usa_nj")),
      api.get(withLedger("/payroll/billing?date_from=2020-01-01&date_to=2099-12-31", "mgmt_usa_nj")),
    ])
      .then(([payrollResponse, chargeResponse]) => {
        setPayrollRows(payrollResponse.data || []);
        setPayrollCharges(chargeResponse.data || []);
      })
      .catch(() => { setPayrollRows([]); setPayrollCharges([]); })
      .finally(() => setPayrollLoading(false));
  }, [open, presetColony]);

  const setItem = (i, patch) => setItems((prev) => prev.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  // Qty multiplies the per-unit amount; an empty qty means 1.
  const lineTotal = (it) => (it.qty === "" ? 1 : parseFloat(it.qty) || 0) * (parseFloat(it.amount) || 0);
  const customTotal = items.reduce((s, it) => s + lineTotal(it), 0);
  const openPayroll = useMemo(() => {
    const raw = payrollRows
      .filter((row) => row.colony_id === cid && !row.billing_charge_id && !row.billing_expense_id)
      .map((row) => ({ id: row.id, source: "payroll", worker_name: row.worker_name, period_start: row.period_start, period_end: row.period_end, amount: Number(row.colony_charge_total ?? row.gross_pay ?? 0) }));
    const charges = payrollCharges
      .filter((row) => row.colony_id === cid && !row.invoiced)
      .map((row) => ({ id: row.id, source: "charge", worker_name: row.worker_name, period_start: row.period_start, period_end: row.period_end, amount: Number(row.amount || 0) }));
    return [...raw, ...charges].sort((a, b) => String(a.period_end || "").localeCompare(String(b.period_end || "")));
  }, [cid, payrollRows, payrollCharges]);
  const openPayrollTotal = useMemo(() => openPayroll.reduce((sum, row) => sum + row.amount, 0), [openPayroll]);

  const submit = async () => {
    if (!cid) return toast.error("Pick a colony first.");
    if (saving) return;
    setSaving(true); setCreateError("");
    let saved = createdInvoice;
    try {
      let r;
      if (saved) {
        r = { data: saved };
      } else if (mode === "custom") {
        const line_items = items
          .filter((it) => it.description.trim() || parseFloat(it.amount))
          .map((it) => {
            const qty = it.qty === "" ? 1 : parseFloat(it.qty) || 0;
            const unit = parseFloat(it.amount) || 0;
            const desc = it.description.trim();
            return {
              // Quantity is spelled out in the description so the colony
              // sees exactly how the line total was built.
              description: qty !== 1 ? `${desc || "Item"} (${qty} × ${fmtMoney(unit)})` : desc,
              date: it.date || "",
              amount: Math.round(qty * unit * 100) / 100,
            };
          });
        if (!line_items.length) { setSaving(false); return toast.error("Add at least one line item."); }
        r = await api.post("/mgmt/invoices", {
          colony_id: cid,
          invoice_date: invoiceDate,
          line_items,
          markup_pct: parseFloat(markup) || 0,
          notes: notes.trim(),
        });
      } else if (mode === "payroll") {
        if (!openPayroll.length) { setSaving(false); return toast.error("No uninvoiced payroll is waiting for this colony."); }
        const response = await api.post("/mgmt/payroll-billing/invoices", {
          payroll_ids: openPayroll.filter((row) => row.source === "payroll").map((row) => row.id),
          charge_ids: openPayroll.filter((row) => row.source === "charge").map((row) => row.id),
          action: "draft",
        });
        const invoice = response.data?.invoices?.[0]?.invoice;
        if (!invoice) throw new Error("Payroll invoice was not returned.");
        r = { data: invoice };
      } else {
        r = await api.post(`/mgmt/colonies/${cid}/invoices/from-expenses`, {
          through_date: through,
          markup_pct: parseFloat(markup) || 0,
          notes: notes.trim(),
        });
      }
      saved = r.data;
      setCreatedInvoice(saved);
      await attachmentDraft.commit(saved.id);
      toast.success(`Invoice ${saved.number} created — ${fmtMoney(saved.total)}.`);
      onCreated?.(saved);
      onOpenChange(false);
    } catch (e) {
      if (saved?.id) setCreateError("The invoice was created, but its attachments were not saved. Retry to finish, or close and add them later with Edit invoice. No email was sent.");
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };
  const close = () => {
    if (saving) return;
    if (createdInvoice) onCreated?.(createdInvoice);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={value => { if (!value) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Create invoice{presetColony ? ` · ${presetColony.name}` : ""}</DialogTitle>
          <DialogDescription>
            {mode === "custom"
              ? "Type your own line items — nothing is pulled from the expense ledger and no expenses get marked invoiced."
              : mode === "payroll"
                ? "Creates one draft invoice from this colony’s payroll billing that has not been invoiced yet."
                : "Rolls every open expense — up to the date you pick — into one regular invoice. Those expenses are marked invoiced so the next regular invoice starts where this one ended."}
          </DialogDescription>
        </DialogHeader>
        <fieldset disabled={saving || !!createdInvoice} className="min-w-0 space-y-3 disabled:opacity-70">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 p-1 bg-secondary rounded-sm">
            {[["expenses", "From open charges"], ["payroll", "From payroll"], ["custom", "Custom items"]].map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setMode(v)}
                className={`h-8 text-sm font-semibold rounded-sm transition-colors ${mode === v ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`create-invoice-mode-${v}`}
              >
                {label}
              </button>
            ))}
          </div>
          {!presetColony && (
            <div>
              <Label>Colony</Label>
              <Select value={cid || "__none__"} onValueChange={(v) => setCid(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="create-invoice-colony"><SelectValue placeholder="Pick a colony" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— pick a colony —</SelectItem>
                  {colonies.map((c) => <SelectItem key={c.id} value={c.id}><ColonyDotName id={c.id} name={c.name} /></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {mode === "custom" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Invoice date</Label>
                  <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} data-testid="create-invoice-date" />
                </div>
                <div>
                  <Label>Markup % (optional)</Label>
                  <Input type="number" step="0.1" min="0" placeholder="0" value={markup} onChange={(e) => setMarkup(e.target.value)} data-testid="create-invoice-markup" />
                </div>
              </div>
              <div>
                <Label>Line items</Label>
                <div className="space-y-2 mt-1">
                  {items.map((it, i) => (
                    <div key={i} className="border border-border rounded-sm p-2 space-y-2" data-testid={`create-invoice-item-row-${i}`}>
                      <div className="flex gap-2 items-center">
                        <Input
                          placeholder="Description"
                          value={it.description}
                          onChange={(e) => setItem(i, { description: e.target.value })}
                          className="flex-1"
                          data-testid={`create-invoice-item-desc-${i}`}
                        />
                        <button
                          type="button"
                          onClick={() => setItems((prev) => prev.length > 1 ? prev.filter((_, j) => j !== i) : prev)}
                          className="text-muted-foreground hover:text-red-600 shrink-0"
                          aria-label="Remove line"
                          data-testid={`create-invoice-item-remove-${i}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex flex-nowrap gap-1.5 sm:gap-2 items-center">
                        <Input
                          type="date"
                          value={it.date}
                          onChange={(e) => setItem(i, { date: e.target.value })}
                          className="w-[118px] sm:w-[150px] shrink-0 px-2 sm:px-3"
                          title="Line date (optional)"
                          data-testid={`create-invoice-item-date-${i}`}
                        />
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          placeholder="Qty"
                          value={it.qty}
                          onChange={(e) => setItem(i, { qty: e.target.value })}
                          className="w-12 sm:w-[70px] shrink-0 px-2 sm:px-3"
                          title="Quantity — multiplies the amount"
                          data-testid={`create-invoice-item-qty-${i}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="$ each"
                          value={it.amount}
                          onChange={(e) => setItem(i, { amount: e.target.value })}
                          className="w-20 sm:w-[100px] shrink-0 px-2 sm:px-3"
                          title="Amount per unit"
                          data-testid={`create-invoice-item-amount-${i}`}
                        />
                        <span className="ml-auto mono-num text-sm font-semibold whitespace-nowrap" data-testid={`create-invoice-item-total-${i}`}>
                          {fmtMoney(lineTotal(it))}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="rounded-sm px-2 text-muted-foreground"
                    onClick={() => setItems((prev) => [...prev, { description: "", date: "", qty: "1", amount: "" }])}
                    data-testid="create-invoice-add-item"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Add line
                  </Button>
                  <span className="text-sm font-semibold" data-testid="create-invoice-custom-total">
                    Total: {fmtMoney(customTotal * (1 + (parseFloat(markup) || 0) / 100))}
                  </span>
                </div>
              </div>
            </>
          ) : mode === "payroll" ? (
            <div className="border border-border rounded-sm overflow-hidden" data-testid="create-invoice-payroll-source">
              <div className="flex items-center justify-between gap-3 px-3 py-2 bg-secondary/30 border-b border-border">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Uninvoiced payroll</span>
                <span className="mono-num font-bold" data-testid="create-invoice-payroll-total">{fmtMoney(openPayrollTotal)}</span>
              </div>
              {payrollLoading ? (
                <div className="py-8 flex items-center justify-center text-sm text-muted-foreground" data-testid="create-invoice-payroll-loading"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading payroll…</div>
              ) : !cid ? (
                <p className="py-8 text-center text-sm text-muted-foreground" data-testid="create-invoice-payroll-pick-colony">Pick a colony to see its uninvoiced payroll.</p>
              ) : openPayroll.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground" data-testid="create-invoice-payroll-empty">No uninvoiced payroll is waiting for this colony.</p>
              ) : (
                <div className="divide-y divide-border" data-testid="create-invoice-payroll-rows">
                  {openPayroll.map((row) => (
                    <div key={`${row.source}-${row.id}`} className="grid grid-cols-[1fr_auto] gap-3 px-3 py-2.5 text-sm" data-testid={`create-invoice-payroll-row-${row.id}`}>
                      <div className="min-w-0"><div className="font-semibold truncate">{row.worker_name || "Worker"}</div><div className="text-xs text-muted-foreground">{row.period_start} → {row.period_end}</div></div>
                      <div className="mono-num font-bold self-center">{fmtMoney(row.amount)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Include expenses through</Label>
                <Input type="date" value={through} onChange={(e) => setThrough(e.target.value)} data-testid="create-invoice-through" />
              </div>
              <div>
                <Label>Markup % (optional)</Label>
                <Input type="number" step="0.1" min="0" placeholder="0" value={markup} onChange={(e) => setMarkup(e.target.value)} data-testid="create-invoice-markup" />
              </div>
            </div>
          )}
          {mode !== "payroll" && <p className="text-xs text-muted-foreground -mt-1">The markup is spread into the line amounts — the colony only sees final numbers, never the markup itself.</p>}
          {mode !== "payroll" && <div>
            <Label>Note on the invoice (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. July management expenses" data-testid="create-invoice-notes" />
          </div>}
        </fieldset>
        {allowAttachments && <InvoiceAttachmentEditor draft={attachmentDraft} disabled={saving} invoiceId={createdInvoice?.id} id="create-invoice-attachments" />}
        {createError && <p role="alert" className="text-sm text-destructive" data-testid="create-invoice-attachment-save-error">{createError}</p>}
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={close} className="rounded-sm" data-testid="create-invoice-cancel">{createdInvoice ? "Finish later" : "Cancel"}</Button>
          <Button onClick={submit} disabled={saving || attachmentDraft.loading || !cid || (!createdInvoice && mode === "payroll" && (payrollLoading || !openPayroll.length))} className="rounded-sm" data-testid="create-invoice-submit">
            {saving ? "Saving…" : createdInvoice ? "Retry attachments" : mode === "payroll" ? "Create payroll invoice" : "Create invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
// ---------------------------------------------------------------------
// Row helper for ExpenseDetailsDialog. Defined at module scope (NOT inside
// the parent component) so React doesn't unmount+remount the subtree on
// every render — that used to blow away input focus + kill perf.
const ExpenseDetailsRow = ({ label, children, testId }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border last:border-b-0 text-sm">
    <span className="text-xs uppercase tracking-wider text-muted-foreground pt-0.5 flex-shrink-0">{label}</span>
    <span className="text-right min-w-0" data-testid={testId}>{children}</span>
  </div>
);

// AI receipt audit: line items the AI read off the receipt, plus a diff of
// anything it corrected (vendor / date the worker typed wrong). Admin-facing.
function ExpenseAiAuditSection({ expense: e, onRefresh }) {
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing] = useState(false);
  const audit = e.ai_audit;
  const hasReceipt = (e.receipt_count || 0) > 0 || (e.receipts?.length || 0) > 0 || !!e.receipt_data_url;

  const rescan = async () => {
    setScanning(true);
    try {
      await api.post(`/expenses/${e.id}/ai-scan`);
      toast.success("Receipt re-scanned");
      onRefresh?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setScanning(false);
    }
  };

  const applyAmount = async () => {
    setFixing(true);
    try {
      await api.post(`/expenses/${e.id}/ai-apply-amount`);
      toast.success(`Amount fixed to ${fmtMoney(audit.receipt_total)}`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setFixing(false);
    }
  };

  if (!audit && !hasReceipt) return null;
  const FIELD_LABELS = { vendor: "Vendor / store", date: "Date", amount: "Amount" };
  return (
    <div className="border border-violet-200 bg-violet-50/40 rounded-sm p-3" data-testid="expense-ai-audit">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <p className="text-xs uppercase tracking-wider text-violet-800 font-semibold flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> AI receipt check
        </p>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] rounded-sm text-violet-700" onClick={rescan} disabled={scanning} data-testid="expense-ai-rescan">
          {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : audit ? "Re-scan" : "Scan now"}
        </Button>
      </div>
      {!audit && <p className="text-xs text-muted-foreground">Not scanned yet — new expenses are scanned automatically a few seconds after they're saved.</p>}
      {audit?.status === "failed" && <p className="text-xs text-red-600">Scan failed — try Re-scan.</p>}
      {audit?.status === "done" && audit.is_receipt === false && <p className="text-xs text-amber-700">The attached photo doesn't look like a receipt.</p>}
      {audit?.status === "done" && audit.is_receipt !== false && (
        <div className="space-y-2">
          {(audit.corrections || []).length > 0 ? (
            <div className="space-y-1" data-testid="expense-ai-corrections">
              {audit.corrections.map((c, i) => (
                <div key={i} className="text-xs bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5">
                  <span className="font-semibold text-amber-800">{FIELD_LABELS[c.field] || c.field} fixed:</span>{" "}
                  <span className="line-through text-muted-foreground">{c.worker_value || "—"}</span>
                  {" → "}
                  <span className="font-semibold">{c.ai_value}</span>
                  <span className="text-muted-foreground"> (worker entered the crossed-out value)</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-emerald-700" data-testid="expense-ai-allgood">Vendor and date match the receipt — nothing was changed.</p>
          )}
          {audit.receipt_total != null && (
            <div className="text-xs flex items-center flex-wrap gap-1.5">
              <span>{(audit.receipts_read || 1) > 1 ? `Receipts total (${audit.receipts_read} receipts):` : "Receipt total:"} <span className="mono-num font-semibold">{fmtMoney(audit.receipt_total)}</span></span>
              {audit.amount_mismatch && (
                <>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700 font-semibold uppercase tracking-wider" data-testid="expense-ai-amount-mismatch">
                    Doesn't match entered {fmtMoney(e.amount)}
                  </span>
                  <Button size="sm" className="h-6 px-2 text-[11px] rounded-sm bg-red-600 hover:bg-red-700 text-white" onClick={applyAmount} disabled={fixing} data-testid="expense-ai-fix-amount">
                    {fixing ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Fix to {fmtMoney(audit.receipt_total)}</>}
                  </Button>
                </>
              )}
            </div>
          )}
          {(audit.line_items || []).length > 0 && (
            <div className="border border-border rounded-sm overflow-hidden bg-white" data-testid="expense-ai-lineitems">
              <div className="grid grid-cols-12 px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary/50">
                <span className="col-span-7">Product</span>
                <span className="col-span-2 text-right">Qty</span>
                <span className="col-span-3 text-right">Price</span>
              </div>
              {audit.line_items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 px-2 py-1 text-xs border-t border-border">
                  <span className="col-span-7 break-words">
                    {it.name}
                    {it.vendor && <span className="block text-[10px] text-muted-foreground">{it.vendor}</span>}
                  </span>
                  <span className="col-span-2 text-right mono-num">{it.qty}</span>
                  <span className="col-span-3 text-right mono-num">{it.line_total != null ? fmtMoney(it.line_total) : "—"}</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-[10px] text-muted-foreground">Scanned {new Date(audit.scanned_at).toLocaleString()} — read from the receipt photo by AI; double-check anything unusual.</p>
        </div>
      )}
    </div>
  );
}

// Click any expense row → everything about it in one place (vendor lives
// here on the mgmt ledger, where the table column shows the colony instead).
function ExpenseDetailsDialog({ expense: e, onOpenChange, isMgmt, colonyMap, onViewReceipts, onEdit, onRefresh }) {
  if (!e) return null;
  const receipts = [...(e.receipts || []), ...(e.receipt_data_url ? [e.receipt_data_url] : [])].filter(Boolean);
  const payLabel = e.payment_method
    ? e.payment_reference
      ? `${e.payment_method} · ${e.payment_method.toLowerCase().includes("check") ? "#" : "••••"}${e.payment_reference}`
      : e.payment_method
    : "—";
  const Row = ExpenseDetailsRow;
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md max-h-[85vh] overflow-y-auto" data-testid="expense-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center justify-between gap-3 pr-8">
            <span>{e.category || "Expense"}</span>
            <span className="mono-num">{fmtMoney(e.amount)}</span>
          </DialogTitle>
          <DialogDescription>{e.date}</DialogDescription>
        </DialogHeader>
        <div>
          {isMgmt && (
            <Row label="Colony" testId="expense-details-colony">
              {e.colony_id ? <ColonyDotName id={e.colony_id} name={colonyMap[e.colony_id] || "Colony"} /> : <span className="text-muted-foreground">Not assigned</span>}
            </Row>
          )}
          <Row label="Vendor" testId="expense-details-vendor">{e.vendor || "—"}</Row>
          <Row label="Paid with" testId="expense-details-payment">{payLabel}</Row>
          <Row label="Submitted by" testId="expense-details-submitter">
            {e.submitter_name || e.created_by || "—"}
            {!isMgmt && e.submitter_role === "driver" && <span className="ml-1.5 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-sm font-semibold">Driver</span>}
          </Row>
          {isMgmt && (
            <Row label="Invoiced" testId="expense-details-invoiced">
              {e.invoiced ? <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-sky-100 text-sky-800 font-semibold uppercase tracking-wider">Yes</span> : "Not yet"}
            </Row>
          )}
          {e.notes && <Row label="Notes" testId="expense-details-notes">{e.notes}</Row>}
          {e.no_receipt_reason && <Row label="No receipt" testId="expense-details-noreceipt"><span className="text-amber-700">{e.no_receipt_reason}</span></Row>}
        </div>
        {receipts.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Receipts</p>
            <div className="flex flex-wrap gap-2">
              {receipts.map((src, i) => (
                <button key={i} type="button" onClick={() => onViewReceipts(receipts)} className="border border-border rounded-sm overflow-hidden hover:opacity-80" data-testid={`expense-details-receipt-${i}`}>
                  <LivePhotoSurface value={src} alt={`receipt ${i + 1}`} className="w-20 h-20" testId={`create-invoice-receipt-${i}`} />
                </button>
              ))}
            </div>
          </div>
        )}
        {receipts.length === 0 && e._loadingReceipts && (e.receipt_count || 0) > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Receipts</p>
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: Math.min(e.receipt_count || 1, 4) }).map((_, i) => (
                <div key={i} className="w-20 h-20 bg-secondary/40 rounded-sm animate-pulse" />
              ))}
            </div>
          </div>
        )}
        <ExpenseAiAuditSection expense={e} onRefresh={() => onRefresh?.(e)} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Close</Button>
          <Button onClick={() => onEdit(e)} className="rounded-sm" data-testid="expense-details-edit">
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
// "Record payment" on an invoice — supports partial or full payment and
// always shows the remaining unpaid balance before saving.
function InvoicePaymentDialog({ invoice, onOpenChange, onConfirm }) {
  const open = !!invoice;
  const [amount, setAmount] = useState("");
  const [paidAt, setPaidAt] = useState(todayISO());
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setAmount(String(invoice?.balance_due ?? invoice?.total ?? "")); setPaidAt(todayISO()); setMethod(""); setReference(""); setNote(""); setSaving(false); }
  }, [open, invoice]);

  const balanceDue = Number(invoice?.balance_due ?? invoice?.total ?? 0);
  const entered = Number(amount || 0);
  const remainingAfter = Math.max(0, balanceDue - entered);

  const mLower = (method || "").toLowerCase();
  const refSpec = (() => {
    if (mLower.includes("check")) return { show: true, label: "Check #", placeholder: "1042", digitsOnly: true };
    if (mLower.includes("zelle")) return { show: true, label: "Zelle transaction #", placeholder: "e.g. ABC123" };
    if (mLower.includes("ach") || mLower.includes("bank")) return { show: true, label: "ACH / confirmation #", placeholder: "TRN12345" };
    return { show: false };
  })();

  const submit = async () => {
    if (!Number.isFinite(entered) || entered <= 0) return toast.error("Enter a payment amount greater than zero.");
    if (entered > balanceDue + 0.005) return toast.error(`Payment cannot exceed the remaining ${fmtMoney(balanceDue)}.`);
    setSaving(true);
    await onConfirm(invoice, {
      amount: entered,
      paid_at: paidAt,
      payment_method: method || "",
      payment_reference: refSpec.show ? reference : "",
      note: note.trim(),
    });
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeTestId="invoice-payment-close" data-testid="invoice-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record payment · {invoice?.number}</DialogTitle>
          <DialogDescription>
            {invoice && <>Record a partial or full payment from <b>{invoice.colony_name}</b>. The invoice closes only when the remaining balance reaches zero.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Amount</Label>
              <Input type="number" min="0.01" max={balanceDue} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mono-num font-semibold" data-testid="invoice-pay-amount" />
            </div>
            <div>
              <Label>Date received</Label>
              <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid="invoice-pay-date" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 border border-border rounded-sm p-3 text-xs" data-testid="invoice-payment-balance-summary">
            <div><div className="text-muted-foreground">Invoice</div><div className="font-semibold mono-num">{fmtMoney(invoice?.total)}</div></div>
            <div><div className="text-muted-foreground">Paid before</div><div className="font-semibold mono-num text-emerald-700">{fmtMoney(invoice?.amount_paid)}</div></div>
            <div><div className="text-muted-foreground">Still unpaid after</div><div className="font-semibold mono-num text-amber-700" data-testid="invoice-payment-remaining-after">{fmtMoney(remainingAfter)}</div></div>
          </div>
          <div className={refSpec.show ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : ""}>
            <div>
              <Label>Payment method</Label>
              <Select value={method || "__none__"} onValueChange={(v) => { setMethod(v === "__none__" ? "" : v); setReference(""); }}>
                <SelectTrigger data-testid="invoice-pay-method"><SelectValue placeholder="Pick a method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— not specified —</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Check">Check</SelectItem>
                  <SelectItem value="Zelle">Zelle</SelectItem>
                  <SelectItem value="ACH">ACH</SelectItem>
                  <SelectItem value="Bank transfer">Bank transfer</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {refSpec.show && (
              <div>
                <Label>{refSpec.label}</Label>
                <Input
                  inputMode={refSpec.digitsOnly ? "numeric" : "text"}
                  placeholder={refSpec.placeholder}
                  value={reference}
                  onChange={(e) => setReference(refSpec.digitsOnly ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value.slice(0, 32))}
                  data-testid="invoice-pay-reference"
                />
              </div>
            )}
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. paid at the office" data-testid="invoice-pay-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="invoice-payment-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || entered <= 0 || entered > balanceDue + 0.005} className="rounded-sm" data-testid="invoice-pay-save">
            {saving ? "Saving…" : `Record ${fmtMoney(entered)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
// Monthly auto-invoice: on the 1st of each month a draft invoice is created
// per colony covering all open expenses through the end of the
// previous month. Drafts only — the admin still reviews and emails them.
function AutoInvoiceSettingsDialog({ open, onOpenChange }) {
  const [enabled, setEnabled] = useState(false);
  const [autoEmail, setAutoEmail] = useState(false);
  const [frequency, setFrequency] = useState("monthly");
  const [markup, setMarkup] = useState("");
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [colonies, setColonies] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/mgmt/auto-invoice-settings").then((r) => {
      setInfo(r.data);
      setEnabled(!!r.data.enabled);
      setAutoEmail(!!r.data.auto_email);
      setFrequency(r.data.frequency || "monthly");
      setMarkup(r.data.markup_pct ? String(r.data.markup_pct) : "");
    }).catch(() => {});
    api.get("/mgmt/colonies").then((r) => {
      setColonies((r.data || []).map((c) => ({
        ...c,
        _markup: c.auto_invoice_markup_pct != null ? String(c.auto_invoice_markup_pct) : "",
      })));
    }).catch(() => {});
  }, [open]);

  // Default markup (top field) — used for colonies without their own override.
  const defaultMarkup = parseFloat(markup) || 0;

  const toggleColony = async (c) => {
    const next = c.auto_invoice_enabled === false; // was off → on, else → off
    setColonies((prev) => prev.map((r) => r.id === c.id ? { ...r, auto_invoice_enabled: next } : r));
    try {
      await api.patch(`/mgmt/colonies/${c.id}/auto-invoice`, { auto_invoice_enabled: next });
    } catch (e) {
      setColonies((prev) => prev.map((r) => r.id === c.id ? { ...r, auto_invoice_enabled: !next } : r));
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const saveColonyMarkup = async (c) => {
    const raw = (c._markup ?? "").trim();
    const payload = { markup_pct: raw === "" ? null : Math.max(0, parseFloat(raw) || 0) };
    // Skip the round-trip when nothing actually changed.
    const current = c.auto_invoice_markup_pct != null ? String(c.auto_invoice_markup_pct) : "";
    if (raw === current) return;
    try {
      const r = await api.patch(`/mgmt/colonies/${c.id}/auto-invoice`, payload);
      setColonies((prev) => prev.map((x) => x.id === c.id
        ? { ...x, auto_invoice_markup_pct: r.data.auto_invoice_markup_pct, _markup: r.data.auto_invoice_markup_pct != null ? String(r.data.auto_invoice_markup_pct) : "" }
        : x));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/mgmt/auto-invoice-settings", { enabled, auto_email: autoEmail, frequency, markup_pct: parseFloat(markup) || 0 });
      setInfo(r.data);
      const freqLabel = frequency === "weekly" ? "every week" : frequency === "biweekly" ? "every 2 weeks" : "on the 1st of each month";
      toast.success(enabled
        ? (autoEmail ? `Auto-invoice is ON — invoices are created AND emailed ${freqLabel}.` : `Auto-invoice is ON — drafts are created ${freqLabel}.`)
        : "Auto-invoice turned off.");
      onOpenChange(false);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="auto-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Auto-invoice</DialogTitle>
          <DialogDescription>
            A <b>regular draft</b> invoice is created for every colony that has open expenses, covering everything up to the run. Payroll invoices use each worker template’s separate billing schedule. Runs <b>never happen on Saturday</b> — if one lands on Shabbos it waits for the next day.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2.5">
            <div>
              <div className="text-sm font-semibold">Run automatically</div>
              <div className="text-xs text-muted-foreground">
                {info?.last_run_at
                  ? `Last run ${new Date(info.last_run_at).toLocaleDateString()} — ${info.last_run_created} invoice${info.last_run_created === 1 ? "" : "s"} created${info.last_run_emailed ? `, ${info.last_run_emailed} emailed` : ""}`
                  : "Hasn't run yet"}
              </div>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="auto-invoice-toggle" />
          </div>
          <div className={!enabled ? "opacity-50" : ""}>
            <Label>How often</Label>
            <Select value={frequency} onValueChange={setFrequency} disabled={!enabled}>
              <SelectTrigger data-testid="auto-invoice-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly — every 7 days, bills through the day before</SelectItem>
                <SelectItem value="biweekly">Every 2 weeks — bills through the day before</SelectItem>
                <SelectItem value="monthly">Monthly — on the 1st, bills through last month&apos;s end</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className={`flex items-center justify-between border border-border rounded-sm px-3 py-2.5 ${!enabled ? "opacity-50" : ""}`}>
            <div>
              <div className="text-sm font-semibold">Email them automatically</div>
              <div className="text-xs text-muted-foreground">Each invoice is emailed to the colony (PDF attached) right after it&apos;s created and moves to Sent. Colonies without a billing email keep their draft.</div>
            </div>
            <Switch checked={autoEmail} onCheckedChange={setAutoEmail} disabled={!enabled} data-testid="auto-invoice-email-toggle" />
          </div>
          <div className={!enabled ? "opacity-50" : ""}>
            <Label>Default markup %</Label>
            <Input type="number" step="0.1" min="0" placeholder="0" value={markup} onChange={(e) => setMarkup(e.target.value)} disabled={!enabled} data-testid="auto-invoice-markup" />
            <p className="text-xs text-muted-foreground mt-1">Applied to any colony that doesn&apos;t set its own markup below. Spread into the line amounts — colonies never see the markup itself.</p>
          </div>
          {/* Per-colony control: each colony can be individually switched on/off
              and given its own markup that overrides the default above. */}
          <div className={`space-y-2 ${!enabled ? "opacity-50 pointer-events-none" : ""}`}>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Per-colony overrides</Label>
            <div className="border border-border rounded-sm max-h-56 overflow-y-auto divide-y divide-border">
              {colonies.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">No colonies yet.</div>}
              {colonies.map((c) => {
                const colonyOn = c.auto_invoice_enabled !== false;
                return (
                  <div key={c.id} className="px-3 py-2 flex items-center gap-3" data-testid={`auto-invoice-colony-row-${c.id}`}>
                    <Switch
                      checked={colonyOn}
                      onCheckedChange={() => toggleColony(c)}
                      disabled={!enabled}
                      data-testid={`auto-invoice-colony-toggle-${c.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{c.name}</div>
                      <div className="text-[11px] text-muted-foreground">{colonyOn ? "Included" : "Excluded — bill manually"}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Input
                        type="number" step="0.1" min="0"
                        className="h-8 w-20 rounded-sm text-right"
                        placeholder={String(defaultMarkup)}
                        value={c._markup}
                        onChange={(e) => setColonies((prev) => prev.map((x) => x.id === c.id ? { ...x, _markup: e.target.value } : x))}
                        onBlur={() => saveColonyMarkup(c)}
                        disabled={!enabled || !colonyOn}
                        title="Leave blank to use the default markup"
                        data-testid={`auto-invoice-colony-markup-${c.id}`}
                      />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">Leave a colony&apos;s markup blank to use the default. Turn a colony off to bill it manually.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="auto-invoice-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
